﻿// ============================================================
// ROTEADOR — ProcurationEngine.jsx (8137 linhas)
// ============================================================
//   1-51     Imports
//  52-126    Constantes, helpers (POWERS_CONDITION_MAP, MOJIBAKE_PATTERN, etc.)
// 127-144    DEFAULT_OWNER_DATA, DEFAULT_PARTNER
// 145-212    normalizeClauseLevel, computeHierNumbers
// 213-277    sanitizeScreenHtml (limpeza de classes CSS)
// 278-360    repairMojibakeText, normalizeLegacyTextDeep
// 361-520    TagSelector, CheckboxDropdownMultiSelect, RulesPanel
// 521-760    CryptoShield, ConfigSection, DocumentMap, Modais
// 761-5500  ProcurationFactory (componente principal)
// 761-977   Estado, refs, hooks iniciais
// 978-1200  Funções de template (persistência, sanitização)
// 1201-1600 Boot (useEffect), autosave, loadPublishedDocument
// 1601-2000 handleImportClient, handlePartnerImport, CRM relink
// 2001-2400 handleResetDraft, download, restore, cloud sync
// 2401-2800 handleExportMD, handleImportMD, handleSaveAsMaster
// 2801-3200 Navegação, edição de blocos (add/delete/reorder)
// 3201-3600 parseText (substituição de placeholders [[...]])
// 3601-4200 handlePublishProcuration (publicação HTML/PDF)
// 4201-4600 Funções de visibilidade (isBlockVisible, condition pass)
// 4601-5000 Render do documento principal (JSX do template)
// 5001-5400 Render da sidebar esquerda (Motor de Procurações)
// 5401-5500 #qual-editor + Parceiros + seções de configuração
// 5501-8137 Render completo e retorno
// ============================================================
// MARCO: #qual-editor (contentEditable) ~linha 5540
// BUG CONHECIDO: Chromium reverte direção do cursor quando
//   dangerouslySetInnerHTML + onInput(setState) são usados.
//   Solução: uncontrolled, apenas onBlur atualiza o estado.
// ============================================================
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { browserEnv } from "../../utils/browserEnv";
import { escapeHtml } from "../../utils/escapeHtml";
import { createPortal } from "react-dom";
import {
  Map as MapIcon,
  GripVertical,
  Trash2,
  Edit3,
  X,
  Plus,
  Download,
  FileText,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Filter,
  User,
  Tag,
  Link as LinkIcon,
  DollarSign,
  EyeOff,
} from "lucide-react";
import { collection, doc, serverTimestamp } from "firebase/firestore";
import {
  fetchProcurationMasterV1,
  fetchProcurationAutosave,
  saveProcurationAutosave,
  subscribeProcurationMasterV1,
  fetchClientData,
  getNextProcurationDocumentRef,
  updateProcurationDocument,
  publishProcurationDocument,
  deleteProcurationDocument,
  addProcurationProntuarioNote,
  fetchProcurationDocumentMeta,
  deleteProcurationRelatedNotes,
} from "../../services/ProcurationEngineRepository";
import {
  getDownloadURL,
  getStorage,
  ref as storageRef,
  uploadBytes,
} from "firebase/storage";
import { FIREBASE_CONFIG } from "../../config/Config";
import {
  RAMO_TAG_MAP as RAMO_TAG_MAP_SHARED,
  POSICOES_POR_RAMO as POSICOES_POR_RAMO_SHARED,
} from "../../config/PosicoesProcessuais";
import { db, auth } from "../../shared/api/firebase/config";

import ContractEditor from "../../components/ContractEditor";
import { SaveCancelBar } from "../../components/ui/SaveCancelBar";
import { CrmClientPickerModal } from "../../components/CrmClientPickerModal";
import { PreFlightCheckModal } from "../../components/common/PreFlightCheckModal";
import { CadernoSelector } from "../../components/ui/CadernoSelector";
import {
  DocumentHeader,
  DocumentFooter,
} from "../../components/layout/DocumentLetterhead";

import {
  generateQualificationText,
  buildCanonicalClientQualification,
  hydrateAndBuildCanonicalClientQualification,
  buildSignatureLine,
} from "../../config/ClientSchema";
import { POWERS_LIBRARY, SYSTEM_TEMPLATES } from "./ProcurationData";
import {
  TAXONOMY_TREE as CONTRACT_TAXONOMY_TREE,
  HONORARIOS_LIST,
} from "../contracts/ContractData";
import { useOfficeData } from "../settings/useOfficeData";
import { useCloudSync } from "../../hooks/useCloudSync";
import {
  getProcurationMaster,
  setProcurationMaster,
  subscribeProcurationMaster,
  getProcurationAutosave,
  setProcurationAutosave,
} from "../../services/TemplateRepository";

import { buildQuickNavMeta } from "../../utils/quickNavMeta";
import {
  generateDocumentFileName,
  generatePublishedArtifactFileStem,
} from "../../utils/documentUtils";
import {
  readLatestPublishedDocumentInSeries,
  readPublishedDocument,
} from "../../utils/documentConsent";
import {
  clearQueuedPublishedDocumentEdit,
  publishedDocumentEditEventName,
  readQueuedPublishedDocumentEdit,
} from "../../utils/publishedDocumentEditor";

import "../../styles/legal-print.css";
import { fetchWithWarmup } from "../../utils/backendWarmup";

const POWERS_CONDITION_MAP = (() => {
  const map = new Map();
  const addBlock = (blk) => {
    if (!blk) return;
    if (blk.id && blk.condition) map.set(String(blk.id), String(blk.condition));
    if (Array.isArray(blk.children)) blk.children.forEach(addBlock);
  };
  for (const blk of POWERS_LIBRARY) addBlock(blk);
  return map;
})();

const appId = FIREBASE_CONFIG.projectId;
const PUBLISHING_API_BASE_URL = String(
  import.meta?.env?.VITE_BACKEND_URL || "https://rda-backend-thuq.onrender.com",
)
  .trim()
  .replace(/\/+$/, "");
const storage = getStorage();
const DOCUMENTS_PATH = ["artifacts", appId, "public", "data", "documents"];
const documentsCol = collection(db, ...DOCUMENTS_PATH);

const DRAFT_KEY = "rda_procuration_draft_v1_enc";
const CLOUD_AUTOSAVE_DOC_ID = "autosave_v1";
const CLOUD_AUTOSAVE_INTERVAL_MS = 10000;
const FORCE_LOCAL_PROCURATION_RECOVERY = false;
const MOJIBAKE_PATTERN = /(?:Ã.|Â.|â€|–|—|�)/;
const readText = (value) => String(value || "").trim();

const DEFAULT_OWNER_DATA = {
  name: "RODRIGO DEMETERCO",
  nationality: "Brasileiro(a)",
  civilStatus: "Casado(a)",
  oab: "113.938",
  oabState: "PR",
  cpf: "",
  rg: "",
  rgIssuer: "",
  email: "",
  fullAddress: "Rua Bom Jesus de Iguape, n. 2035, Hauer, Curitiba/PR.",
};

const DEFAULT_PARTNER = {
  active: false,
  name: "",
  oab: "",
  cpf: "",
  address: "",
  clientId: null,
  qualificationData: null,
  qualificacaoCompleta: "",
};

const normalizeClauseLevel = (value) => {
  let lvl = Number(value || 0);
  if (!Number.isFinite(lvl) || lvl < 0) lvl = 0;
  return Math.floor(lvl);
};

const computeHierNumbers = (children, macroNum) => {
  const out = [];
  const stack = [];
  const arr = Array.isArray(children) ? children : [];
  const macro = typeof macroNum === "number" ? macroNum : null;

  for (let i = 0; i < arr.length; i += 1) {
    const child = arr[i] || {};
    const lvl = normalizeClauseLevel(child.level);

    while (stack.length <= lvl) stack.push(0);
    stack.length = lvl + 1;
    stack[lvl] = (stack[lvl] || 0) + 1;
    for (let k = 0; k < lvl; k += 1) {
      if (!stack[k]) stack[k] = 1;
    }

    const parts = [];
    if (macro !== null) parts.push(String(macro));
    for (let k = 0; k <= lvl; k += 1) parts.push(String(stack[k] || 1));
    out.push(parts.join(".") + ".");
  }

  return out;
};

const sanitizeScreenHtml = (rawHtml) => {
  let h = String(rawHtml || "");
  h = h.replace(
    /\sclass\s*=\s*(["'])([\s\S]*?)\1/gi,
    (full, quote, classText) => {
      const tokens = String(classText || "")
        .split(/\s+/g)
        .filter(Boolean);
      const kept = tokens.filter((t) => {
        if (!t) return false;
        if (
          t === "flex" ||
          t === "grid" ||
          t === "inline-flex" ||
          t === "inline-grid"
        )
          return false;
        if (t.startsWith("grid-cols-")) return false;
        if (t.startsWith("col-") || t.startsWith("col-span-")) return false;
        if (t.startsWith("w-") || t.startsWith("basis-")) return false;
        if (t.startsWith("columns-") || t.startsWith("column-")) return false;
        if (/^(m|mx|my|mt|mb|ml|mr|p|px|py|pt|pb|pl|pr)-/.test(t)) return false;
        return true;
      });
      if (kept.length === 0) return "";
      return ` class=${quote}${kept.join(" ")}${quote}`;
    },
  );
  return h;
};

const repairMojibakeText = (value) => {
  const text = String(value || "");
  if (!text || !MOJIBAKE_PATTERN.test(text)) return text;

  const replacements = [
    ["Cláusula", "Cláusula"],
    ["procuração", "procuração"],
    ["procurações", "procurações"],
    ["Publicação", "Publicação"],
    ["Revisão", "Revisão"],
    ["versão", "versão"],
    ["Descrição", "Descrição"],
    ["Ato específico", "Ato específico"],
    ["específico", "específico"],
    ["específicos", "específicos"],
    ["representação", "representação"],
    ["técnica", "técnica"],
    ["observância", "observância"],
    ["jurisprudência", "jurisprudência"],
    ["Justiça", "Justiça"],
    ["revogação", "revogação"],
    ["instituição", "instituição"],
    ["cartório", "cartório"],
    ["juízo", "juízo"],
    ["não", "não"],
    ["lícita", "lícita"],
    ["até", "até"],
    ["á", "á"],
    ["â", "â"],
    ["ã", "ã"],
    ["ê", "ê"],
    ["é", "é"],
    ["í", "í"],
    ["ó", "ó"],
    ["ô", "ô"],
    ["õ", "õ"],
    ["ú", "ú"],
    ["ç", "ç"],
    ["É", "É"],
    ["Ó", "Ó"],
    ["Ú", "Ú"],
    ["Ç", "Ç"],
    ["ª", "ª"],
    ["º", "º"],
    ["§", "§"],
    ["°", "°"],
    ["–", "–"],
    ["—", "—"],
    ["“", "“"],
    ["”", "”"],
    ["‘", "‘"],
    ["’", "’"],
    ["…", "…"],
    ["Â ", " "],
  ];

  let repaired = text;
  replacements.forEach(([from, to]) => {
    repaired = repaired.split(from).join(to);
  });
  // Mojibake real nunca emparelha "Â" com uma letra (ex.: GOIÂNIA deve sobreviver).
  repaired = repaired.replace(/Â(?![A-Za-zÀ-ÖØ-öø-ÿ])/g, "");
  return repaired;
};

const normalizeLegacyTextDeep = (value) => {
  if (typeof value === "string") return repairMojibakeText(value);
  if (Array.isArray(value))
    return value.map((item) => normalizeLegacyTextDeep(item));
  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value).map(([key, entryValue]) => [
        key,
        normalizeLegacyTextDeep(entryValue),
      ]),
    );
  }
  return value;
};

const containsIrrecoverableCorruption = (value) => {
  try {
    return JSON.stringify(value || "").includes("�");
  } catch {
    return String(value || "").includes("�");
  }
};

const TAXONOMY = CONTRACT_TAXONOMY_TREE || {};
const HONORARIOS_OPTIONS = HONORARIOS_LIST || [];
const CONDITION_OPTIONS = [
  {
    label: "Gerais",
    options: [
      { value: "ALWAYS", label: "Qualquer Posição" },
      { value: "AUTOR_ONLY", label: "Apenas Autor" },
      { value: "REU_ONLY", label: "Apenas Réu" },
      { value: "LEGAL_OUTORGANTE_ONLY", label: "Apenas PJ Outorgante" },
      {
        value: "PATRIMONIAL_IMOBILIARIO_ONLY",
        label: "Apenas Especialidade Patrimonial",
      },
      { value: "CRIPTO_ONLY", label: "Apenas Ativos Virtuais / Cripto" },
      {
        value: "INVESTIGACAO_DEFENSIVA_ONLY",
        label: "Apenas Investigação Defensiva",
      },
      { value: "ARBITRAGEM_ONLY", label: "Apenas Arbitragem / ODR" },
      {
        value: "COMPLIANCE_INTERNO_ONLY",
        label: "Apenas Compliance / Investigação Interna",
      },
    ],
  },
  {
    label: "Validade do Mandato",
    options: [
      {
        value: "VALIDADE_INDETERMINADO",
        label: "Validade: Prazo Indeterminado",
      },
      {
        value: "VALIDADE_DETERMINADO",
        label: "Validade: Prazo Determinado",
      },
      {
        value: "VALIDADE_ATO_ESPECIFICO",
        label: "Validade: Ato Específico",
      },
    ],
  },
  {
    label: "Setoriais",
    options: [
      {
        value: "RAMO_CIVIL_CONSUMIDOR",
        label: "Setorial: Cível / Consumerista",
      },
      {
        value: "RAMO_TRABALHISTA_PREVIDENCIARIO",
        label: "Setorial: Trabalhista / Previdenciário",
      },
      {
        value: "RAMO_PENAL_PUBLICA_PASSIVO",
        label: "Setorial: Penal - Ação Pública / Polo Passivo",
      },
      {
        value: "RAMO_PENAL_PUBLICA_ATIVO",
        label: "Setorial: Penal - Ação Pública / Polo Ativo",
      },
      {
        value: "RAMO_PENAL_PRIVADA_PASSIVO",
        label: "Setorial: Penal - Ação Privada / Polo Passivo",
      },
      {
        value: "RAMO_PENAL_PRIVADA_ATIVO",
        label: "Setorial: Penal - Ação Privada / Polo Ativo",
      },
      {
        value: "RAMO_SOCIETARIO_EMPRESARIAL",
        label: "Setorial: Societário / Empresarial",
      },
      { value: "RAMO_ADMINISTRATIVO", label: "Setorial: Administrativo" },
      { value: "RAMO_TRIBUTARIO", label: "Setorial: Tributário" },
      { value: "RAMO_FAMILIA", label: "Setorial: Família / Sucessões" },
      { value: "RAMO_ANIMAL", label: "Setorial: Direito Animal" },
      {
        value: "RAMO_MANDADO_SEGURANCA_IMPETRANTE",
        label: "Setorial: Mandado de Segurança - Impetrante",
      },
      {
        value: "RAMO_MANDADO_SEGURANCA_IMPETRADO",
        label: "Setorial: Mandado de Segurança - Impetrado",
      },
      {
        value: "RAMO_CIVIL_FIEL_DEPOSITARIO",
        label: "Setorial: Cível - Fiel Depositário",
      },
      {
        value: "RAMO_PENAL_FIEL_DEPOSITARIO",
        label: "Setorial: Penal - Fiel Depositário (Restituição de Coisas)",
      },
    ],
  },
];
const CONDITION_LABEL_MAP = CONDITION_OPTIONS.reduce((acc, group) => {
  (group.options || []).forEach((option) => {
    acc[option.value] = option.label;
  });
  return acc;
}, {});

const TagSelector = ({ selectedTags = [], onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleTag = (tag) => {
    const hasUniversal = selectedTags.includes("Universal");
    const withoutUniversal = selectedTags.filter((t) => t !== "Universal");

    let newTags = [];
    if (tag === "Universal") {
      newTags = hasUniversal ? [] : ["Universal"];
    } else {
      if (hasUniversal) {
        newTags = [tag];
      } else if (withoutUniversal.includes(tag)) {
        newTags = withoutUniversal.filter((t) => t !== tag);
      } else {
        newTags = [...withoutUniversal, tag];
      }
    }
    onChange(newTags.length ? newTags : ["Universal"]);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-1.5 bg-white border border-gray-300 rounded text-[10px] font-bold text-gray-600 hover:border-brand-blue transition-colors max-w-[240px]"
        type="button"
      >
        <Tag className="w-3 h-3 text-brand-blue" />
        <span className="truncate">
          {selectedTags.length > 0 ? selectedTags.join(", ") : "Vincular Áreas"}
        </span>
        <ChevronDown className="w-3 h-3" />
      </button>
      {isOpen && (
        <div className="absolute top-full left-0 mt-1 w-64 bg-white border border-gray-200 rounded-lg shadow-xl z-[70] p-2 animate-fade-in-up max-h-60 overflow-y-auto custom-scrollbar">
          <label className="flex items-center gap-2 p-2 hover:bg-orange-50 rounded cursor-pointer border-b border-gray-100 mb-1">
            <input
              type="checkbox"
              checked={selectedTags.includes("Universal")}
              onChange={() => toggleTag("Universal")}
              className="rounded text-brand-blue focus:ring-0 w-3 h-3"
            />
            <span className="text-xs font-bold text-gray-800">
              UNIVERSAL (Todas as Áreas)
            </span>
          </label>
          <p className="text-[9px] font-bold text-gray-400 uppercase mb-1 px-2 mt-2">
            Específicas
          </p>
          {Object.entries(TAXONOMY).map(([key, val]) => (
            <label
              key={key}
              className="flex items-center gap-2 p-2 hover:bg-blue-50 rounded cursor-pointer"
            >
              <input
                type="checkbox"
                checked={selectedTags.includes(key)}
                onChange={() => toggleTag(key)}
                className="rounded text-blue-600 focus:ring-0 w-3 h-3"
              />
              <span className="text-xs text-gray-600 leading-tight">
                {val.label}
              </span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
};

const CheckboxDropdownMultiSelect = ({
  options = [],
  values = [],
  onUpdate,
  placeholder,
  icon: Icon = null,
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const clickOut = (event) => {
      if (ref.current && !ref.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener("mousedown", clickOut);
    return () => document.removeEventListener("mousedown", clickOut);
  }, []);

  const toggleValue = (value) => {
    const nextValues = values.includes(value)
      ? values.filter((item) => item !== value)
      : [...values, value];
    onUpdate(nextValues);
  };

  const summary =
    values.length > 0
      ? options
          .filter((option) => values.includes(option.value))
          .map((option) => option.label)
          .join(", ")
      : placeholder || "Selecionar";

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((prev) => !prev)}
        className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 rounded-lg text-[11px] font-bold text-gray-700 hover:border-brand-blue transition-colors w-full justify-between"
        type="button"
      >
        {Icon ? (
          <Icon className="w-3.5 h-3.5 text-brand-blue shrink-0" />
        ) : null}
        <span className="truncate text-left flex-1">{summary}</span>
        <span className="text-[10px] font-extrabold text-gray-400 shrink-0">
          {values.length}
        </span>
        <ChevronDown className="w-3.5 h-3.5 shrink-0" />
      </button>
      {open ? (
        <div className="absolute top-full left-0 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-xl z-[70] p-2 max-h-64 overflow-y-auto custom-scrollbar">
          {options.map((option) => (
            <label
              key={option.value}
              className="flex items-center gap-2 p-2 hover:bg-blue-50 rounded cursor-pointer"
            >
              <input
                type="checkbox"
                checked={values.includes(option.value)}
                onChange={() => toggleValue(option.value)}
                className="rounded text-brand-blue focus:ring-0 w-3 h-3"
              />
              <span className="text-xs text-gray-700 leading-tight">
                {option.label}
              </span>
            </label>
          ))}
          {options.length === 0 ? (
            <p className="text-[10px] text-gray-400 p-2">Sem opções.</p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

const RulesPanel = ({ blockData, onChange, taxonomy }) => {
  const MultiSelectGeneric = ({
    label,
    options,
    values,
    onUpdate,
    icon: Icon,
  }) => {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    useEffect(() => {
      const clickOut = (e) => {
        if (ref.current && !ref.current.contains(e.target)) setOpen(false);
      };
      document.addEventListener("mousedown", clickOut);
      return () => document.removeEventListener("mousedown", clickOut);
    }, []);
    return (
      <div className="relative" ref={ref}>
        <button
          onClick={() => setOpen(!open)}
          className="flex items-center gap-2 px-3 py-1.5 bg-white border border-gray-300 rounded text-[10px] font-bold text-gray-600 hover:border-brand-blue max-w-full"
          type="button"
        >
          {Icon && <Icon className="w-3 h-3 text-brand-blue" />}{" "}
          <span>
            {label} ({values.length})
          </span>{" "}
          <ChevronDown className="w-3 h-3" />
        </button>
        {open && (
          <div className="absolute top-full left-0 mt-1 w-64 bg-white border border-gray-200 rounded shadow-xl z-[70] p-2 max-h-60 overflow-auto custom-scrollbar">
            {options.map((opt) => (
              <label
                key={opt.value}
                className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={values.includes(opt.value)}
                  onChange={() => {
                    onUpdate(
                      values.includes(opt.value)
                        ? values.filter((v) => v !== opt.value)
                        : [...values, opt.value],
                    );
                  }}
                  className="rounded text-blue-600 w-3 h-3"
                />
                <span className="text-xs text-gray-700">{opt.label}</span>
              </label>
            ))}
            {options.length === 0 ? (
              <p className="text-[10px] text-gray-400 p-2">Sem opções.</p>
            ) : null}
          </div>
        )}
      </div>
    );
  };

  const tx = taxonomy || {};
  const allObjects = Object.values(tx).flatMap((t) =>
    (t?.objects || []).map((o) => ({ value: o.id, label: o.label })),
  );

  return (
    <div className="bg-gray-50 p-3 rounded border border-gray-200 flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-bold text-gray-500 uppercase flex items-center gap-1">
          <Filter className="w-3 h-3" /> Regras de Vinculação
        </span>
        {String(blockData.condition || "ALWAYS") !== "ALWAYS" ? (
          <button
            type="button"
            onClick={() => onChange({ ...blockData, condition: "ALWAYS" })}
            className="text-[9px] font-bold uppercase px-2 py-1 rounded border border-purple-200 bg-white text-purple-700 hover:bg-purple-50 transition-colors"
            title="Remover regra de condição e voltar para condição neutra"
          >
            Limpar condição
          </button>
        ) : null}
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        <TagSelector
          selectedTags={blockData.tags || ["Universal"]}
          onChange={(newTags) => onChange({ ...blockData, tags: newTags })}
        />

        <div className="flex items-center gap-2 bg-white border border-gray-300 rounded px-2 py-1.5 max-w-full">
          <User className="w-3 h-3 text-brand-blue" />
          <select
            className="text-[10px] font-bold text-gray-700 bg-transparent outline-none cursor-pointer w-28"
            value={blockData.condition || "ALWAYS"}
            onChange={(e) =>
              onChange({ ...blockData, condition: e.target.value })
            }
          >
            {CONDITION_OPTIONS.map((group) => (
              <optgroup key={group.label} label={group.label}>
                {(group.options || []).map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
        </div>

        <MultiSelectGeneric
          label="Objetos"
          icon={LinkIcon}
          options={allObjects}
          values={blockData.requiredObjects || []}
          onUpdate={(v) => onChange({ ...blockData, requiredObjects: v })}
        />

        <MultiSelectGeneric
          label="Honorários"
          icon={DollarSign}
          options={HONORARIOS_OPTIONS.map((h) => ({
            value: h.id,
            label: h.label,
          }))}
          values={blockData.requiredHonorarios || []}
          onUpdate={(v) => onChange({ ...blockData, requiredHonorarios: v })}
        />

        <label
          className="flex items-center gap-2 bg-white border border-gray-300 rounded px-2 py-1.5 cursor-pointer hover:bg-gray-50"
          title="Se marcado, exige TODOS os itens (AND). Se desmarcado, exige APENAS UM (OR)."
        >
          <input
            type="checkbox"
            className="rounded text-brand-blue w-3 h-3"
            checked={blockData.matchRule === "ALL"}
            onChange={(e) =>
              onChange({
                ...blockData,
                matchRule: e.target.checked ? "ALL" : "ANY",
              })
            }
          />
          <span className="text-[10px] font-bold text-gray-600">
            Regra "E" (ALL)
          </span>
        </label>

        <div
          className="flex items-center gap-2 bg-white border border-gray-300 rounded px-2 py-1.5"
          title="Valor Mínimo da Causa"
        >
          <DollarSign className="w-3 h-3 text-green-600" />
          <input
            type="number"
            placeholder="Min R$"
            className="text-[10px] font-bold text-gray-700 bg-transparent outline-none w-24"
            value={blockData.minValue || ""}
            onChange={(e) =>
              onChange({ ...blockData, minValue: e.target.value })
            }
          />
        </div>

        <label className="flex items-center gap-2 bg-white border border-gray-300 rounded px-2 py-1.5 cursor-pointer hover:bg-gray-50">
          <input
            type="checkbox"
            className="rounded text-brand-blue w-3 h-3"
            checked={blockData.minParties > 1}
            onChange={(e) =>
              onChange({ ...blockData, minParties: e.target.checked ? 2 : 1 })
            }
          />
          <span className="text-[10px] font-bold text-gray-600">
            Plural (2+ Partes)
          </span>
        </label>

        <div className="w-px h-6 bg-gray-300 mx-1 hidden md:block"></div>

        <div
          className="flex items-center gap-1 bg-orange-50 border border-orange-200 rounded px-2 py-1.5"
          title="Nível Hierárquico (Recuo Progressivo)"
        >
          <button
            type="button"
            onClick={() =>
              onChange({
                ...blockData,
                level: Math.max(0, (blockData.level || 0) - 1),
              })
            }
            className="p-0.5 rounded hover:bg-orange-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={(blockData.level || 0) <= 0}
            title="Diminuir nível (recuo para esquerda)"
          >
            <ChevronLeft className="w-3 h-3 text-orange-700" />
          </button>

          <div className="flex flex-col items-center min-w-[60px]">
            <span className="text-[9px] font-bold text-orange-800 uppercase">
              Nível
            </span>
            <span className="text-[10px] font-bold text-orange-600">
              {blockData.level || 0}
            </span>
          </div>

          <button
            type="button"
            onClick={() =>
              onChange({ ...blockData, level: (blockData.level || 0) + 1 })
            }
            className="p-0.5 rounded hover:bg-orange-200 transition-colors"
            title="Aumentar nível (recuo para direita)"
          >
            <ChevronRight className="w-3 h-3 text-orange-700" />
          </button>
        </div>
      </div>

      <div className="flex gap-1 flex-wrap">
        {blockData.tags?.map((t) => (
          <span
            key={t}
            className="text-[9px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded border border-blue-200 uppercase"
          >
            {t}
          </span>
        ))}
        {blockData.condition !== "ALWAYS" ? (
          <span className="text-[9px] bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded border border-purple-200 uppercase">
            {CONDITION_LABEL_MAP[blockData.condition] || blockData.condition}
          </span>
        ) : null}
      </div>
    </div>
  );
};

const CryptoShield = {
  async encrypt(data) {
    try {
      const text = JSON.stringify(data);
      const encoded = new TextEncoder().encode(text);
      const digest = await crypto.subtle.digest("SHA-256", encoded);
      const hashArray = Array.from(new Uint8Array(digest));
      const hashHex = hashArray
        .map((b) => b.toString(16).padStart(2, "0"))
        .join("");
      const payload = { hash: hashHex, data };
      return btoa(unescape(encodeURIComponent(JSON.stringify(payload))));
    } catch (e) {
      console.error("[CryptoShield] encrypt error", e);
      return null;
    }
  },
  async decrypt(enc) {
    try {
      const decoded = decodeURIComponent(escape(atob(enc)));
      const payload = JSON.parse(decoded);
      if (!payload || !payload.data || !payload.hash) return null;
      const text = JSON.stringify(payload.data);
      const encoded = new TextEncoder().encode(text);
      const digest = await crypto.subtle.digest("SHA-256", encoded);
      const hashArray = Array.from(new Uint8Array(digest));
      const hashHex = hashArray
        .map((b) => b.toString(16).padStart(2, "0"))
        .join("");
      if (hashHex !== payload.hash) return null;
      return payload.data;
    } catch (e) {
      console.error("[CryptoShield] decrypt error", e);
      return null;
    }
  },
};

const inputBaseStyle =
  "w-full px-2.5 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-[12px] font-medium text-gray-800 outline-none transition-all focus:border-[#C5A059] focus:ring-1 focus:ring-[#C5A059]";
const labelBaseStyle =
  "text-[10px] font-bold text-gray-500 uppercase tracking-widest";
const PROCURATION_ROLE_OPTIONS = [
  "OUTORGANTE",
  "REPRESENTANTE LEGAL",
  "ANUENTE",
  "ASSISTENTE",
];

const ConfigSection = ({
  id,
  title,
  icon,
  children,
  openSection,
  setOpenSection,
}) => {
  const isOpen = openSection === id;
  return (
    <div className="border-b border-gray-200">
      <button
        onClick={() => setOpenSection(isOpen ? null : id)}
        className={`w-full flex items-center justify-between px-2.5 py-1.5 text-left transition-colors border-l-2 ${isOpen ? "bg-slate-50 border-l-[#C5A059]" : "bg-white border-l-transparent hover:bg-slate-50"}`}
        type="button"
      >
        <span
          className={`text-[9px] font-medium uppercase tracking-[0.03em] flex items-center gap-1.5 ${isOpen ? "text-[#20243B]" : "text-slate-500"}`}
        >
          <i
            className={`fas ${icon} ${isOpen ? "text-[#C5A059]" : "text-slate-300"}`}
          ></i>
          {title}
        </span>
        <i
          className={`fas fa-chevron-down text-[9px] text-slate-300 transition-transform ${isOpen ? "rotate-180" : ""}`}
        ></i>
      </button>

      {isOpen && (
        <div className="px-2.5 py-1.5 bg-slate-50/80 space-y-2 animate-fade-in">
          {children}
        </div>
      )}
    </div>
  );
};

const DocumentMap = ({
  structure,
  activeId,
  onScrollTo,
  onReorder,
  onDelete,
}) => {
  const [draggedIndex, setDraggedIndex] = useState(null);

  const isPreambuloBlock = (block) => {
    return (
      block?.id === "preambulo" ||
      String(block?.title || "")
        .toUpperCase()
        .includes("PREÂMBULO")
    );
  };

  const mapBlocks = Array.isArray(structure)
    ? structure.filter((b) => !isPreambuloBlock(b))
    : [];
  const mapOriginalIndexes = Array.isArray(structure)
    ? structure
        .map((b, i) => ({ b, i }))
        .filter(({ b }) => !isPreambuloBlock(b))
        .map(({ i }) => i)
    : [];

  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = "move";
  };
  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };
  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    if (draggedIndex === null) return;
    if (draggedIndex !== dropIndex) {
      const fromOriginal = mapOriginalIndexes[draggedIndex];
      const toOriginal = mapOriginalIndexes[dropIndex];
      if (typeof fromOriginal === "number" && typeof toOriginal === "number")
        onReorder(fromOriginal, toOriginal);
    }
    setDraggedIndex(null);
  };

  return (
    <div className="hidden xl:flex w-72 2xl:w-96 bg-white border-l border-gray-200 h-full flex-col shadow-xl z-20 flex-shrink-0">
      <div className="p-4 bg-gray-50 border-b border-gray-200 flex justify-between items-center">
        <h3 className="text-[10px] font-bold text-gray-500 uppercase tracking-wider flex items-center gap-2">
          <MapIcon className="w-3 h-3" /> Mapa da Procuração
        </h3>
        <span className="text-[9px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-bold">
          {mapBlocks.length} Blocos
        </span>
      </div>

      <div className="flex-1 overflow-y-auto p-2 custom-scrollbar space-y-1">
        {mapBlocks.map((block, idx) => (
          <div
            key={block.id}
            draggable
            onDragStart={(e) => handleDragStart(e, idx)}
            onDragOver={handleDragOver}
            onDrop={(e) => handleDrop(e, idx)}
            onClick={() => onScrollTo(block.id)}
            className={`relative pl-2 py-2 pr-2 cursor-pointer text-xs transition-all group rounded border border-transparent flex items-center gap-2 ${activeId === block.id ? "bg-blue-50 border-blue-200 text-brand-blue font-bold shadow-sm" : "hover:bg-gray-50 text-gray-600 hover:border-gray-200"} ${draggedIndex === idx ? "opacity-50 border-dashed border-blue-400 bg-blue-50" : ""}`}
          >
            <div className="text-gray-300 cursor-grab hover:text-gray-500">
              <GripVertical className="w-3 h-3" />
            </div>
            <div className="w-6 text-[10px] font-bold text-gray-300 text-right select-none">
              {idx + 1}
            </div>
            <div
              className="flex-1 truncate"
              dangerouslySetInnerHTML={{
                __html: block.__displayTitle || block.title || "",
              }}
            />
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDelete(block.id);
              }}
              className="opacity-0 group-hover:opacity-100 text-gray-300 hover:text-red-500 p-1 rounded hover:bg-white transition-all"
              title="Excluir"
            >
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
        ))}
      </div>
      <div className="p-2 bg-gray-50 text-[9px] text-center text-gray-400 border-t border-gray-200">
        Arraste para reordenar
      </div>
    </div>
  );
};

const ClientImportModal = ({ onClose, onSelect }) => {
  return (
    <CrmClientPickerModal
      isOpen={true}
      onClose={onClose}
      onSelect={onSelect}
      db={db}
      appId={appId}
      title="Base de Clientes (CRM)"
    />
  );
};

const PartnerImportModal = ({ onClose, onSelect }) => {
  return (
    <CrmClientPickerModal
      isOpen={true}
      onClose={onClose}
      onSelect={onSelect}
      db={db}
      appId={appId}
      title="Base de Clientes (CRM) — Parceiros"
      requiredRole="PARTNER"
    />
  );
};

export const ProcurationFactory = () => {
  const [mode, setMode] = useState("producao");
  const [selectedTemplateId, setSelectedTemplateId] = useState(
    SYSTEM_TEMPLATES[0]?.id || null,
  );
  const [showImport, setShowImport] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [showAdvancedActions, setShowAdvancedActions] = useState(false);
  const {
    cloudSyncState,
    setCloudSyncState,
    cloudSyncMeta,
    setCloudSyncMeta,
    isCloudAutosaveInFlightRef,
    lastCloudAutosavePayloadRef,
  } = useCloudSync();

  const [isModified, setIsModified] = useState(false);
  const [dataSource, setDataSource] = useState("local_template");
  const [dialogState, setDialogState] = useState({
    open: false,
    title: "",
    message: "",
    mode: "confirm",
    onConfirm: null,
    onCancel: null,
  });
  const [preFlightState, setPreFlightState] = useState({
    open: false,
    publishType: "simple",
    format: "html",
    cadernoId: "",
    cadernoTitle: "",
  });

  const [openSection, setOpenSection] = useState("client");

  const [showPartnerImportModal, setShowPartnerImportModal] = useState(false);
  const [importTargetPartnerIndex, setImportTargetPartnerIndex] = useState(0);
  const [importTargetPartyId, setImportTargetPartyId] = useState(null);
  const advancedMenuAnchorRef = useRef(null);
  const advancedMenuRef = useRef(null);
  const [advancedMenuPosition, setAdvancedMenuPosition] = useState({
    top: 0,
    left: 0,
  });

  const [editingBlockId, setEditingBlockId] = useState(null);
  const [activeBlockData, setActiveBlockData] = useState(null);
  const [tempTitle, setTempTitle] = useState("");

  const { data: officeData } = useOfficeData();
  const [officeQualificationText, setOfficeQualificationText] = useState("");
  const [officeQualificationClient, setOfficeQualificationClient] =
    useState(null);

  const [formData, setFormData] = useState({
    parties: [
      {
        id: `party_${Date.now()}`,
        clientId: null,
        name: "",
        cpf: "",
        email: "",
        phone: "",
        role: "OUTORGANTE",
        qualificationText: "",
        qualificationData: null,
      },
    ],
    city: "Curitiba",
    ramoDireito: "Civil",
    ramosDireito: ["Civil"],
    posicaoCliente: "",
    posicoesCliente: [],
    objetoPrincipal: "",
    quereladoName: "",
    parteContrariaName: "",
    dataFato: "",
    descricaoFato: "",
    autoridadeCoatoraNome: "",
    autoridadeCoatoraOrgao: "",
    atoCoatorData: "",
    atoCoatorDescricao: "",
    patrimonialImobiliarioEnabled: false,
    bensImoveisEspecificos: "",
    bemImovelMatricula: "",
    bemImovelCartorio: "",
    bemImovelEndereco: "",
    bemImovelNaturezaDireito: "",
    bemImovelObservacoes: "",
    envolveCriptoEnabled: false,
    investigacaoDefensivaEnabled: false,
    arbitragemEnabled: false,
    complianceInternoEnabled: false,
    validadeMandatoTipo: "INDETERMINADO",
    validadeMandatoData: "",
    validadeMandatoAto: "",
  });
  const [editingPublicationMeta, setEditingPublicationMeta] = useState(null);

  const publishedEditLockRef = useRef(false);
  const activeDocumentLockRef = useRef(false);
  const draftPromptedRef = useRef(false);

  useEffect(() => {
    publishedEditLockRef.current = Boolean(editingPublicationMeta?.documentId);
  }, [editingPublicationMeta]);

  useEffect(() => {
    const source = String(dataSource || "").trim();
    activeDocumentLockRef.current =
      Boolean(editingPublicationMeta?.documentId) ||
      [
        "published_document",
        "cloud_autosave",
        "localStorage",
        "migrated_from_cloud_autosave",
        "migrated_from_localStorage",
        "backup_json",
        "migrated_from_backup_json",
      ].includes(source);
  }, [dataSource, editingPublicationMeta]);

  const openPreFlightForProcuration = async (
    publishType = "simple",
    format = "html",
  ) => {
    const safeCadernoId = String(
      preFlightState.cadernoId || editingPublicationMeta?.cadernoId || "",
    ).trim();
    if (!safeCadernoId) {
      setDialogState({
        open: true,
        title: "Caderno de Autos obrigatório",
        message:
          "Selecione o Caderno de Autos (Vincular a Processo) antes de salvar. Toda procuração deve estar vinculada a um processo (caderno).",
        mode: "info",
        onConfirm: null,
      });
      return;
    }

    // ── GUARD: Detectar template desatualizado antes de salvar ──
    // Compara o updatedAt do master na nuvem com o que foi carregado na memória.
    // Se o master foi atualizado (deploy, edição remota, etc.), força resync
    // e BLOQUEIA o save atual. O usuário deve clicar novamente após o DOM
    // re-renderizar com o template atualizado.
    try {
      if (appId) {
        const freshData = await fetchProcurationMasterV1();
        if (freshData) {
          const freshVersion =
            freshData?.updatedAt?.toMillis?.() ||
            freshData?.updatedAt?.seconds * 1000 ||
            0;
          const localVersion = masterVersionRef.current || 0;
          // Detecta se o master foi atualizado na nuvem (deploy, edição remota, etc.)
          // Funciona mesmo quando activeDocumentLockRef bloqueia o onSnapshot
          if (freshVersion && (!localVersion || freshVersion > localVersion)) {
            console.log(
              "[ProcurationEngine] Template master atualizado na nuvem (local:",
              localVersion,
              "→ cloud:",
              freshVersion,
              "). Resincronizando e bloqueando save...",
            );
            const freshStructure = Array.isArray(freshData?.structure)
              ? freshData.structure
              : null;
            if (freshStructure && freshStructure.length > 0) {
              masterStructureRef.current = freshStructure;
              masterVersionRef.current = freshVersion;
              // 🚨 CORREÇÃO: Sempre carregar a estrutura como está, sem
              // looksLikeLegacyStructure. A função substitui versões válidas
              // pelo template padrão de 8 cláusulas (mesmo bug já corrigido
              // em outros pontos deste arquivo).
              setTemplateData(buildMasterTemplatePayload(freshStructure));
              setIsModified(false);
              setDataSource("cloud_master");
            }
            // BLOQUEAR: o DOM ainda está com o template antigo.
            // Informar o usuário e retornar — na próxima tentativa o DOM já estará correto.
            setDialogState({
              open: true,
              title: "Template atualizado",
              message:
                'O modelo da procuração foi atualizado na nuvem desde a última vez que você carregou esta página.\n\nO sistema já sincronizou a versão mais recente. Aguarde o documento recarregar na tela e clique em "Salvar HTML" novamente para garantir que o conteúdo salvo reflita as últimas alterações.',
              mode: "info",
              onConfirm: null,
            });
            return;
          }
        }
      }
    } catch (versionCheckErr) {
      console.warn(
        "[ProcurationEngine] Falha no version check (non-blocking):",
        versionCheckErr?.message,
      );
    }

    setPreFlightState((prev) => ({
      ...prev,
      open: true,
      publishType:
        String(publishType || "simple")
          .trim()
          .toLowerCase() === "consent"
          ? "consent"
          : "simple",
      format:
        String(format || "html")
          .trim()
          .toLowerCase() === "pdf"
          ? "pdf"
          : "html",
      cadernoId: safeCadernoId,
      cadernoTitle: String(
        prev.cadernoTitle || editingPublicationMeta?.cadernoTitle || "",
      ).trim(),
    }));
  };

  const closePreFlightForProcuration = () => {
    setPreFlightState({
      open: false,
      publishType: "simple",
      format: "html",
      cadernoId: "",
      cadernoTitle: "",
    });
  };

  const confirmPreFlightForProcuration = () => {
    const nextPublishType = preFlightState.publishType;
    const nextFormat = preFlightState.format;
    const nextCadernoId = String(preFlightState.cadernoId || "").trim();
    const nextCadernoTitle = String(preFlightState.cadernoTitle || "").trim();
    if (!nextCadernoId) {
      setDialogState({
        open: true,
        title: "Caderno de Autos obrigatório",
        message:
          "Selecione o Caderno de Autos antes de publicar. Toda procuração deve estar vinculada a um processo (caderno) para que o documento apareça corretamente no portal do cliente.",
        mode: "info",
        onConfirm: null,
      });
      return;
    }
    closePreFlightForProcuration();
    handlePublishProcuration(
      nextPublishType,
      nextFormat,
      nextCadernoId,
      nextCadernoTitle,
    );
  };

  const [partnerData, setPartnerData] = useState({ ...DEFAULT_PARTNER });
  const [partnerExtras, setPartnerExtras] = useState([]);

  useEffect(() => {
    let alive = true;

    const hydrateOffice = async () => {
      if (!officeData) {
        if (!alive) return;
        setOfficeQualificationText("");
        setOfficeQualificationClient(null);
        return;
      }

      try {
        const canonical = await hydrateAndBuildCanonicalClientQualification(
          officeData,
          {},
          { isAttorneyOfRecord: true },
        );
        if (!alive) return;
        setOfficeQualificationText(String(canonical?.text || "").trim());
        setOfficeQualificationClient(canonical?.client || officeData);
      } catch {
        if (!alive) return;
        setOfficeQualificationText(
          String(
            buildCanonicalClientQualification(officeData, {}, {
              isAttorneyOfRecord: true,
            })?.text || "",
          ).trim(),
        );
        setOfficeQualificationClient(officeData);
      }
    };

    hydrateOffice();

    return () => {
      alive = false;
    };
  }, [officeData]);

  useEffect(() => {
    if (!showAdvancedActions) return undefined;

    const updateMenuPosition = () => {
      const anchor = advancedMenuAnchorRef.current;
      if (!anchor) return;
      const rect = anchor.getBoundingClientRect();
      const menuWidth = 256;
      const margin = 12;
      const left = Math.min(
        Math.max(margin, rect.right - menuWidth),
        Math.max(margin, window.innerWidth - menuWidth - margin),
      );
      const top = Math.max(margin, rect.top - 8);
      setAdvancedMenuPosition({ top, left });
    };

    const handlePointerDown = (event) => {
      const menuNode = advancedMenuRef.current;
      const anchorNode = advancedMenuAnchorRef.current;
      const target = event.target;
      if (menuNode && menuNode.contains(target)) return;
      if (anchorNode && anchorNode.contains(target)) return;
      setShowAdvancedActions(false);
    };

    updateMenuPosition();
    browserEnv.addEventListener("resize", updateMenuPosition);
    browserEnv.addEventListener("scroll", updateMenuPosition, true);
    document.addEventListener("mousedown", handlePointerDown);
    return () => {
      window.removeEventListener("resize", updateMenuPosition);
      window.removeEventListener("scroll", updateMenuPosition, true);
      document.removeEventListener("mousedown", handlePointerDown);
    };
  }, [showAdvancedActions]);

  const masterStructureRef = useRef(null);
  const masterVersionRef = useRef(null);

  const ALLOWED_RAMO_KEYS = useMemo(
    () => [
      "Civil",
      "Penal",
      "Trabalho",
      "Previdenciario",
      "SocietarioEmpresarial",
      "Administrativo",
      "Tributario",
      "Familia",
      "Animal",
      "MandadoSeguranca",
    ],
    [],
  );

  // MIGRAÇÃO PARA FONTE ÚNICA (22/08/2026): RAMO_TAG_MAP e POSICOES_POR_RAMO
  // eram definidos aqui, divergentes de cópias equivalentes em
  // ContractEngine.jsx e CadernoCockpit.jsx — unificados em
  // src/config/PosicoesProcessuais.js (união das 3, nada removido).
  const RAMO_TAG_MAP = RAMO_TAG_MAP_SHARED;
  const POSICOES_POR_RAMO = POSICOES_POR_RAMO_SHARED;

  const normalizeStringArray = useCallback((value) => {
    if (Array.isArray(value)) {
      return Array.from(
        new Set(value.map((item) => String(item || "").trim()).filter(Boolean)),
      );
    }
    const single = String(value || "").trim();
    return single ? [single] : [];
  }, []);

  const selectedRamoKeys = useMemo(() => {
    const normalized = normalizeStringArray(formData?.ramosDireito);
    const filtered = normalized.filter((ramo) =>
      ALLOWED_RAMO_KEYS.includes(ramo),
    );
    if (filtered.length > 0) return filtered;
    const legacy = String(formData?.ramoDireito || "").trim();
    return ALLOWED_RAMO_KEYS.includes(legacy) ? [legacy] : ["Civil"];
  }, [
    ALLOWED_RAMO_KEYS,
    formData?.ramoDireito,
    formData?.ramosDireito,
    normalizeStringArray,
  ]);

  const selectedPosicoes = useMemo(() => {
    const normalized = normalizeStringArray(formData?.posicoesCliente);
    if (normalized.length > 0) return normalized;
    const legacy = String(formData?.posicaoCliente || "").trim();
    return legacy ? [legacy] : [];
  }, [
    formData?.posicaoCliente,
    formData?.posicoesCliente,
    normalizeStringArray,
  ]);

  const hasPenalSelected = useMemo(
    () => selectedRamoKeys.includes("Penal"),
    [selectedRamoKeys],
  );

  const hasMandadoSegurancaSelected = useMemo(
    () => selectedRamoKeys.includes("MandadoSeguranca"),
    [selectedRamoKeys],
  );

  const hasNonPenalSelected = useMemo(
    () =>
      selectedRamoKeys.some(
        (ramo) => ramo !== "Penal" && ramo !== "MandadoSeguranca",
      ),
    [selectedRamoKeys],
  );

  const getPosicaoOptionsByRamo = (ramoRaw) => {
    const ramos = normalizeStringArray(ramoRaw).filter((ramo) =>
      ALLOWED_RAMO_KEYS.includes(ramo),
    );
    const keys = ramos.length > 0 ? ramos : ["Civil"];
    const merged = Array.from(
      new Set(
        keys.flatMap(
          (ramo) => POSICOES_POR_RAMO[ramo] || POSICOES_POR_RAMO.ALL,
        ),
      ),
    );
    return merged.map((p) => ({ value: p, label: p }));
  };

  const posicaoOptions = useMemo(() => {
    return getPosicaoOptionsByRamo(
      formData?.ramosDireito || formData?.ramoDireito,
    );
  }, [formData?.ramoDireito, formData?.ramosDireito]);

  const parties = useMemo(
    () => (Array.isArray(formData?.parties) ? formData.parties : []),
    [formData?.parties],
  );
  const firstParty = useMemo(() => parties[0] || {}, [parties]);
  const outorganteGender = useMemo(() => {
    const physical = firstParty?.qualificationData?.qualification?.physical;
    const raw = String(
      physical?.grammaticalGender || physical?.pronoun || "",
    )
      .trim()
      .toUpperCase();
    if (raw === "F" || raw === "ELA" || raw === "A") return "F";
    return "M";
  }, [firstParty]);
  const structuredParties = useMemo(
    () =>
      parties
        .filter(
          (party) =>
            party?.qualificationData &&
            party?.qualificationData?.qualification &&
            party?.qualificationData?.type,
        )
        .map((party) => party.qualificationData),
    [parties],
  );
  const legalOutorgantes = useMemo(
    () =>
      structuredParties.filter(
        (party) => String(party?.type || "").trim() === "Pessoa Jurídica",
      ),
    [structuredParties],
  );
  const hasAnyLegalOutorgante = legalOutorgantes.length > 0;
  const legalRepresentativeQualification = useMemo(() => {
    if (!hasAnyLegalOutorgante) return "";
    const rep = legalOutorgantes.find(
      (party) => party?.qualification?.legal?.representative,
    )?.qualification?.legal?.representative;
    if (!rep) return "[Representante legal não informado]";
    return String(
      buildCanonicalClientQualification({
        type: "Pessoa Física",
        qualification: {
          physical: {
            name: String(rep?.name || "").trim(),
            socialName: String(rep?.socialName || "").trim(),
            pronoun: String(rep?.pronoun || "").trim(),
            grammaticalGender: String(
              rep?.grammaticalGender || rep?.gender || "",
            ).trim(),
            nationality: String(rep?.nationality || "").trim(),
            naturalness: String(rep?.naturalness || "").trim(),
            civilStatus: String(rep?.civilStatus || "").trim(),
            maritalRegime: String(rep?.maritalRegime || "").trim(),
            profession: String(rep?.profession || "").trim(),
            rg: String(rep?.rg || "").trim(),
            orgaoEmissor: String(rep?.orgaoEmissor || "").trim(),
            cpf: String(rep?.cpf || "").trim(),
            hasProfessionalRegister: Boolean(rep?.hasProfessionalRegister),
            registerType: String(rep?.registerType || "").trim(),
            registerUf: String(rep?.registerUf || "").trim(),
            professionalRegister: String(
              rep?.professionalRegister || "",
            ).trim(),
          },
        },
        contact: rep?.contact || {},
        location: {
          address: rep?.address || {},
        },
      })?.text || "[Representante legal não informado]",
    ).trim();
  }, [hasAnyLegalOutorgante, legalOutorgantes]);
  const outorgadoNomeExibicao = useMemo(() => {
    const activePartners = [
      partnerData,
      ...(Array.isArray(partnerExtras) ? partnerExtras : []),
    ]
      .filter((p) => p && (p.active || p.name || p.oab || p.cpf || p.address))
      .map((p) => String(p?.name || "").trim())
      .filter(Boolean);
    return [
      String(
        officeQualificationClient?.qualification?.physical?.socialName ||
          officeQualificationClient?.qualification?.physical?.name ||
          officeQualificationClient?.name ||
          "OUTORGADO",
      ).trim(),
      ...activePartners,
    ]
      .filter(Boolean)
      .join(" E ");
  }, [officeQualificationClient, partnerData, partnerExtras]);

  const formatPartyRoleLabel = (roleRaw) => {
    const role = String(roleRaw || "OUTORGANTE").trim();
    if (!role) return "Outorgante";
    return role
      .toLowerCase()
      .split("_")
      .map((chunk) =>
        chunk ? chunk.charAt(0).toUpperCase() + chunk.slice(1) : "",
      )
      .join(" ");
  };

  const addParty = () => {
    setFormData((prev) => {
      const parties = Array.isArray(prev?.parties) ? prev.parties : [];
      return {
        ...(prev || {}),
        parties: [
          ...parties,
          {
            id: `party_${Date.now()}`,
            clientId: null,
            name: "",
            cpf: "",
            email: "",
            phone: "",
            role: "OUTORGANTE",
            qualificationText: "",
            qualificationData: null,
          },
        ],
      };
    });
    setIsModified(true);
  };

  const removeParty = (id) => {
    setFormData((prev) => {
      const parties = Array.isArray(prev?.parties) ? prev.parties : [];
      if (parties.length <= 1) return prev;
      return { ...(prev || {}), parties: parties.filter((p) => p?.id !== id) };
    });
    setIsModified(true);
  };

  const updateParty = (id, field, value) => {
    setFormData((prev) => {
      const parties = Array.isArray(prev?.parties) ? prev.parties : [];
      return {
        ...(prev || {}),
        parties: parties.map((p) =>
          p?.id === id ? { ...(p || {}), [field]: value } : p,
        ),
      };
    });
    setIsModified(true);
  };

  const updatePartner = (field, value) => {
    setPartnerData((p) => ({ ...(p || {}), [field]: value }));
    setIsModified(true);
  };

  const [templateData, setTemplateData] = useState(() => {
    const tpl = SYSTEM_TEMPLATES[0];
    const map = new Map(POWERS_LIBRARY.map((b) => [b.id, b]));
    const structure = (tpl?.structure || [])
      .map((id) => map.get(id))
      .filter(Boolean);
    return {
      id: tpl?.id || "local",
      title: tpl?.title || "Procuração",
      structure,
    };
  });

  const selectedTemplate = useMemo(() => {
    return (
      SYSTEM_TEMPLATES.find((t) => t.id === selectedTemplateId) ||
      SYSTEM_TEMPLATES[0] ||
      null
    );
  }, [selectedTemplateId]);

  const buildTemplateFromSystem = (tpl) => {
    const safeTpl = tpl || SYSTEM_TEMPLATES[0] || null;
    const map = new Map(POWERS_LIBRARY.map((b) => [b.id, b]));
    return {
      id: safeTpl?.id || "local",
      title: safeTpl?.title || "Procuração",
      structure: (safeTpl?.structure || [])
        .map((id) => map.get(id))
        .filter(Boolean),
    };
  };

  const sanitizePersistedFormData = (rawFormData) => {
    const normalizedFormData = normalizeLegacyTextDeep(rawFormData || {});
    const next = { ...(normalizedFormData || {}) };
    next.parties = Array.isArray(normalizedFormData?.parties)
      ? normalizedFormData.parties.map((party) => {
          const hasStructured = Boolean(
            party?.qualificationData?.qualification &&
            party?.qualificationData?.type,
          );
          return {
            ...(party || {}),
            qualificationText: hasStructured
              ? ""
              : String(party?.qualificationText || "").trim(),
          };
        })
      : [
          {
            id: `party_${Date.now()}`,
            clientId: null,
            name: "",
            cpf: "",
            email: "",
            phone: "",
            role: "OUTORGANTE",
            qualificationText: "",
            qualificationData: null,
          },
        ];
    next.ramosDireito = normalizeStringArray(
      normalizedFormData?.ramosDireito || normalizedFormData?.ramoDireito,
    ).filter((ramo) => ALLOWED_RAMO_KEYS.includes(ramo));
    if (next.ramosDireito.length === 0) next.ramosDireito = ["Civil"];
    next.ramoDireito = next.ramosDireito[0] || "Civil";
    next.posicoesCliente = normalizeStringArray(
      normalizedFormData?.posicoesCliente || normalizedFormData?.posicaoCliente,
    );
    next.posicaoCliente = next.posicoesCliente[0] || "";
    return next;
  };

  const sanitizePersistedTemplateData = (rawTemplateData) => {
    const template = normalizeLegacyTextDeep(rawTemplateData || {});
    const structure = Array.isArray(template?.structure)
      ? template.structure.map((block) => {
          if (block?.id !== "cl_01_partes") return block;
          return {
            ...(block || {}),
            content: `
            <p><strong>1.1. PARTE OUTORGADA:</strong> [[QUALIFICACAO_CONTRATADO]]</p>
            <p><strong>1.2. PARTE OUTORGANTE:</strong> [[QUALIFICACAO_CONTRATANTE]]</p>
        `,
          };
        })
      : [];

    // --- Migração: separar cláusulas combinadas em cláusulas distintas ---
    const migrateSetorialChildren = (children) => {
      if (!Array.isArray(children)) return children;
      let next = [...children];

      // Migração 1: RAMO_TRIBUTARIO_ADMINISTRATIVO → RAMO_ADMINISTRATIVO + RAMO_TRIBUTARIO
      const oldAdmTribIdx = next.findIndex(
        (c) => c?.condition === "RAMO_TRIBUTARIO_ADMINISTRATIVO",
      );
      if (oldAdmTribIdx !== -1) {
        next.splice(
          oldAdmTribIdx,
          1,
          {
            id: "cl_05_08_administrativo",
            type: "sub_clause",
            level: 0,
            condition: "RAMO_ADMINISTRATIVO",
            content:
              "<strong>Advocacia Administrativa:</strong> Confere-se poderes específicos para representar a parte <strong>OUTORGANTE</strong> perante entes da Administração Direta e Indireta (federal, estadual e municipal), autarquias, fundações públicas, empresas públicas, sociedades de economia mista e conselhos de classe. A parte <strong>OUTORGADA</strong> fica expressamente autorizada a atuar em processos licitatórios, sindicâncias, processos administrativos disciplinares (PAD), defesas contra autos de infração e multas administrativas, impetrar Mandados de Segurança e Habeas Data contra atos de autoridade pública, bem como firmar termos de ajustamento de conduta ou compromisso administrativo perante qualquer órgão ou entidade integrante da Administração Pública.",
          },
          {
            id: "cl_05_08b_tributario",
            type: "sub_clause",
            level: 0,
            condition: "RAMO_TRIBUTARIO",
            content:
              "<strong>Advocacia Tributária (e-CAC da Receita Federal):</strong> Confere-se poderes para representar a parte <strong>OUTORGANTE</strong> perante a Secretaria da Receita Federal do Brasil, PGFN, CARF, Secretarias de Fazenda estaduais e municipais e demais órgãos tributários. A parte <strong>OUTORGADA</strong> fica expressamente autorizada a atuar no Centro Virtual de Atendimento (e-CAC), convalidar, aceitar, validar ou rejeitar autorizações de acesso delegadas eletronicamente, gerir dossiês digitais de atendimento, transigir em programas de regularização tributária e praticar atos de defesa administrativa, inclusive em sindicâncias e autos de infração fiscal.",
          },
        );
      }

      // Migração 2: RAMO_TRABALHISTA_PREVIDENCIARIO → RAMO_TRABALHISTA + RAMO_PREVIDENCIARIO
      const oldTrabPrevIdx = next.findIndex(
        (c) => c?.condition === "RAMO_TRABALHISTA_PREVIDENCIARIO",
      );
      if (oldTrabPrevIdx !== -1) {
        next.splice(
          oldTrabPrevIdx,
          1,
          {
            id: "cl_05_02_trabalhista",
            type: "sub_clause",
            level: 0,
            condition: "RAMO_TRABALHISTA",
            content:
              "<strong>Advocacia Trabalhista:</strong> Em observância à Súmula 395 do TST, pactua-se que a vigência deste mandato na seara laboral estende-se ininterruptamente até o trânsito em julgado e arquivamento definitivo da lide. Confere-se poderes para representar a parte <strong>OUTORGANTE</strong> perante a Justiça do Trabalho em todas as instâncias, Ministério Público do Trabalho, Superintendências Regionais do Trabalho e Emprego e demais órgãos administrativos trabalhistas, podendo firmar acordos em audiências de conciliação, participar de mediações e negociações coletivas, requerer a expedição de alvarás e levantar depósitos recursais.",
          },
          {
            id: "cl_05_02b_previdenciario",
            type: "sub_clause",
            level: 0,
            condition: "RAMO_PREVIDENCIARIO",
            content:
              "<strong>Advocacia Previdenciária:</strong> Perante o Instituto Nacional do Seguro Social (INSS), nos estritos termos da Instrução Normativa PRES/INSS n. 128/2022, confere-se poderes específicos para requerer, acompanhar e recorrer de benefícios previdenciários e assistenciais, cadastrar e recuperar senhas no portal Meu INSS, realizar comprovação de vida perante a rede bancária, receber mensalidades e verbas pretéritas em atraso, bem como representar a parte <strong>OUTORGANTE</strong> perante Juntas de Recursos e Câmaras de Julgamento do Conselho de Recursos da Previdência Social (CRPS).",
          },
        );
      }

      return next;
    };
    const migratedStructure = structure.map((block) => {
      if (block?.id === "cl_05_setorial" && Array.isArray(block?.children)) {
        return { ...block, children: migrateSetorialChildren(block.children) };
      }
      // --- Migração da Cláusula 11 (Validade): duas fontes possíveis ---
      // Fonte A: cl_08_eficacia (antiga local, com cl_08_01_validade)
      // Fonte B: cl_09_eficacia (antiga na nuvem, com cl_09_01_validade)
      // Ambas devem ser convertidas para a nova estrutura de 4 filhos condicionais
      const isOldEficacia =
        (block?.id === "cl_08_eficacia" || block?.id === "cl_09_eficacia") &&
        Array.isArray(block?.children);
      if (isOldEficacia) {
        const childIds = block.children.map((c) => String(c?.id || ""));
        const hasOldValidade = childIds.some(
          (id) => id.includes("_validade") && !id.includes("_indeterminado"),
        );
        const hasNewStructure = childIds.includes("cl_08_01_indeterminado");
        if (hasOldValidade && !hasNewStructure) {
          // Preservar o conteúdo da subcláusula de assinatura, se existir
          const oldAssinaturaContent = block.children.find(
            (c) =>
              c.id === "cl_08_02_assinatura_presuncao" ||
              c.id === "cl_09_02_assinatura_presuncao",
          )?.content;
          const oldValidadeContent = block.children.find(
            (c) => c.id === "cl_08_01_validade" || c.id === "cl_09_01_validade",
          )?.content;

          const powerBlock = POWERS_LIBRARY.find(
            (b) => b.id === "cl_08_eficacia",
          );
          if (powerBlock && Array.isArray(powerBlock.children)) {
            return {
              ...block,
              id: "cl_08_eficacia",
              children: powerBlock.children.map((child) => ({
                ...child,
                content:
                  child.id === "cl_08_04_assinatura_presuncao"
                    ? String(oldAssinaturaContent || child.content)
                    : child.content,
              })),
            };
          }
        }
      }
      return block;
    });

    return {
      ...(template || {}),
      structure: migratedStructure,
    };
  };

  const buildCloudAutosavePayload = () => ({
    selectedTemplateId,
    templateData: sanitizePersistedTemplateData(templateData),
    formData: sanitizePersistedFormData(formData),
    partnerData,
    partnerExtras,
  });

  const applyPublishedDocumentForEdit = useCallback(
    (publishedData) => {
      publishedEditLockRef.current = true;
      activeDocumentLockRef.current = true;
      const contentData =
        publishedData?.contentData &&
        typeof publishedData.contentData === "object"
          ? publishedData.contentData
          : {};
      const sanitizedTemplate = sanitizePersistedTemplateData(
        contentData?.templateData || publishedData?.templateData,
      );
      const sanitizedForm = sanitizePersistedFormData(
        contentData?.formData ||
          contentData?.form ||
          publishedData?.formData ||
          publishedData?.form,
      );
      const nextSelectedTemplateId = String(
        contentData?.selectedTemplateId ||
          publishedData?.selectedTemplateId ||
          publishedData?.context?.selectedTemplateId ||
          "",
      ).trim();
      const nextPartnerData =
        contentData?.partnerData || publishedData?.partnerData
          ? {
              ...DEFAULT_PARTNER,
              ...(contentData?.partnerData || publishedData?.partnerData),
            }
          : { ...DEFAULT_PARTNER };
      const nextPartnerExtras = Array.isArray(contentData?.partnerExtras)
        ? contentData.partnerExtras
        : Array.isArray(publishedData?.partnerExtras)
          ? publishedData.partnerExtras
          : [];

      let finalTemplate = sanitizedTemplate;
      if (!finalTemplate?.structure?.length) {
        const defaultTpl = SYSTEM_TEMPLATES[0];
        const blockMap = new Map(POWERS_LIBRARY.map((b) => [b.id, b]));
        finalTemplate = {
          id: defaultTpl?.id || "recovered",
          title: defaultTpl?.title || finalTemplate?.title || "Procuração",
          structure: (defaultTpl?.structure || [])
            .map((id) => blockMap.get(id))
            .filter(Boolean),
        };
      }

      if (!finalTemplate?.structure?.length) {
        throw new Error(
          "O documento publicado não possui snapshot editável completo para o Procuration Engine.",
        );
      }

      if (
        containsIrrecoverableCorruption(finalTemplate) ||
        containsIrrecoverableCorruption(sanitizedForm)
      ) {
        throw new Error(
          "O snapshot editável da procuração publicada está corrompido e não pode ser reaberto com segurança.",
        );
      }

      // [CIRURGIA RDA V1.2] Desidratação do Call Stack para Abertura de Procurações
      setTimeout(() => {
        if (nextSelectedTemplateId)
          setSelectedTemplateId(nextSelectedTemplateId);
        setTemplateData(finalTemplate);
        setFormData(sanitizedForm);
        setPartnerData(nextPartnerData);
        setPartnerExtras(nextPartnerExtras);
        setEditingBlockId(null);
        setActiveBlockData(null);
        setDataSource("published_document");
        setIsModified(false);
        setCloudSyncState("saved");
        setCloudSyncMeta({
          updatedAt:
            publishedData?.updatedAt ||
            publishedData?.publication?.publishedAtIso ||
            null,
          updatedBy:
            publishedData?.updatedBy ||
            publishedData?.publication?.publishedBy ||
            publishedData?.createdBy ||
            null,
          source: "published_document",
          error: null,
        });
        lastCloudAutosavePayloadRef.current = JSON.stringify({
          selectedTemplateId: nextSelectedTemplateId || selectedTemplateId,
          templateData: finalTemplate,
          formData: sanitizedForm,
          partnerData: nextPartnerData,
          partnerExtras: nextPartnerExtras,
        });
        setEditingPublicationMeta({
          documentId: String(publishedData?.id || "").trim(),
          publicationSeriesId: String(
            publishedData?.publicationSeriesId || publishedData?.id || "",
          ).trim(),
          publicationVersion: Math.max(
            1,
            Number(publishedData?.publicationVersion || 1) || 1,
          ),
          cadernoId: String(publishedData?.cadernoId || "").trim() || null,
          cadernoTitle:
            String(publishedData?.cadernoTitle || "").trim() || null,
        });
        window.scrollTo({ top: 0, behavior: "smooth" });
      }, 150);
    },
    [selectedTemplateId],
  );

  const loadPublishedDocumentForEdit = useCallback(
    async (requestLike) => {
      const documentId = String(requestLike?.documentId || "").trim();
      if (!documentId) return false;

      const loaded = await readLatestPublishedDocumentInSeries({
        documentId,
        publicationSeriesId: String(
          requestLike?.publicationSeriesId || "",
        ).trim(),
      });
      const publishedData = loaded?.data || null;
      if (!publishedData) {
        throw new Error(
          `Documento não localizado no banco de dados (ID: ${documentId.slice(0, 8)}…). ` +
            `Isso ocorre quando a procuração foi criada antes da migração do sistema e seus dados editáveis não foram preservados no restore. ` +
            `Clique em OK e crie uma nova procuração para este cliente — o histórico de publicação anterior continuará visível no prontuário.`,
        );
      }

      const moduleName = String(
        publishedData?.module || publishedData?.context?.module || "",
      )
        .trim()
        .toLowerCase();
      const typeName = String(
        publishedData?.type || publishedData?.contentData?.documentType || "",
      )
        .trim()
        .toUpperCase();
      if (
        moduleName &&
        moduleName !== "procurations" &&
        typeName !== "PROCURATION"
      ) {
        throw new Error(
          "O documento selecionado não pertence ao módulo de procurações.",
        );
      }

      applyPublishedDocumentForEdit({
        id: loaded?.id || documentId,
        ...publishedData,
      });
      clearQueuedPublishedDocumentEdit("procurations");
      return true;
    },
    [applyPublishedDocumentForEdit],
  );

  const buildMasterTemplatePayload = (structure) =>
    sanitizePersistedTemplateData({
      id: "cloud_master",
      title: "Procuração Mestre",
      structure: normalizeLegacyTextDeep(
        Array.isArray(structure) ? structure : [],
      ),
    });

  const loadSystemTemplate = (tplId) => {
    const next =
      SYSTEM_TEMPLATES.find((t) => t.id === tplId) ||
      SYSTEM_TEMPLATES[0] ||
      null;
    setSelectedTemplateId(next?.id || null);
    setTemplateData(buildTemplateFromSystem(next));
    try {
      const tags = Array.isArray(next?.tags)
        ? next.tags.map((t) => String(t || "").trim())
        : [];
      const mapTagToRamo = (t) => {
        if (!t) return null;
        const s = String(t).trim();
        if (ALLOWED_RAMO_KEYS.includes(s)) return s;
        const inv = Object.entries(RAMO_TAG_MAP).find(([, v]) => v === s);
        return inv ? inv[0] : null;
      };
      const order = [
        "Penal",
        "Trabalho",
        "Previdenciario",
        "Administrativo",
        "Familia",
        "Civil",
      ];
      const chosen =
        order.find((ramoKey) =>
          tags.some((t) => mapTagToRamo(t) === ramoKey),
        ) || null;
      if (chosen)
        setFormData((p) => ({
          ...(p || {}),
          ramoDireito: chosen,
          ramosDireito: [chosen],
          posicaoCliente: "",
          posicoesCliente: [],
        }));
    } catch {}
    setEditingBlockId(null);
    setActiveBlockData(null);
    setIsModified(false);
    setDataSource("local_template");
  };

  useEffect(() => {
    let unsub = null;

    const boot = async () => {
      const queuedEditRequest = readQueuedPublishedDocumentEdit("procurations");
      if (queuedEditRequest?.documentId) {
        setDialogState({
          open: false,
          title: "",
          message: "",
          mode: "confirm",
          onConfirm: null,
          onCancel: null,
        });
        try {
          const loadedFromPublication =
            await loadPublishedDocumentForEdit(queuedEditRequest);
          if (loadedFromPublication) return;
        } catch (e) {
          clearQueuedPublishedDocumentEdit("procurations");
          publishedEditLockRef.current = false;
          activeDocumentLockRef.current = false;
          draftPromptedRef.current = true;
          setEditingPublicationMeta(null);
          setDialogState({
            open: true,
            title: "Erro ao reabrir procuração publicada",
            message: "Erro: " + (e?.message || String(e)),
            mode: "info",
            onConfirm: null,
            onCancel: null,
          });
        }
      }

      if (FORCE_LOCAL_PROCURATION_RECOVERY) {
        setSelectedTemplateId(SYSTEM_TEMPLATES[0]?.id || null);
        setTemplateData(buildTemplateFromSystem(SYSTEM_TEMPLATES[0]));
        setDataSource("forced_local_recovery");
        setIsModified(false);
        return;
      }

      try {
        if (!appId) return;
        const masterData = await fetchProcurationMasterV1();
        if (masterData) {
          const masterStructure = Array.isArray(masterData?.structure)
            ? masterData.structure
            : null;
          if (masterStructure && masterStructure.length > 0) {
            masterStructureRef.current = masterStructure;
            masterVersionRef.current =
              masterData?.updatedAt?.toMillis?.() ||
              masterData?.updatedAt?.seconds * 1000 ||
              Date.now();
            if (!publishedEditLockRef.current) {
              // 🚨 CORREÇÃO: Sempre carregar a estrutura como está, sem
              // looksLikeLegacyStructure. A função substitui versões válidas
              // (ex: 11 cláusulas) pelo template padrão de 8.
              setTemplateData(buildMasterTemplatePayload(masterStructure));
              setIsModified(false);
              setDataSource("cloud_master");
            }
          }
        }
      } catch {
        console.warn(
          "[PROCURATION] fetchProcurationMasterV1 falhou (esperado se sem master ainda)",
        );
      }

      try {
        if (publishedEditLockRef.current) return;

        let pendingLocalEnc = null;
        try {
          pendingLocalEnc = localStorage.getItem(DRAFT_KEY);
          if (pendingLocalEnc) localStorage.removeItem(DRAFT_KEY);
        } catch {
          pendingLocalEnc = null;
        }

        let cloudDraft = null;
        try {
          if (appId) {
            cloudDraft = await fetchProcurationAutosave(CLOUD_AUTOSAVE_DOC_ID);
            if (cloudDraft) {
              const data = cloudDraft || {};
              if (data?.templateData?.structure && data?.formData)
                cloudDraft = data;
            }
          }
        } catch {
          cloudDraft = null;
        }

        if (!draftPromptedRef.current && (cloudDraft || pendingLocalEnc)) {
          draftPromptedRef.current = true;
          setDialogState({
            open: true,
            title: "Rascunho Encontrado",
            message:
              'Detectamos uma procuração que estava sendo editada anteriormente.\n\nDeseja restaurar este rascunho para continuar o trabalho?\n\nSe clicar em "Cancelar", você iniciará um documento em branco limpo.',
            mode: "confirm",
            onConfirm: async () => {
              try {
                if (cloudDraft) {
                  activeDocumentLockRef.current = true;
                  const restoredTemplate = cloudDraft?.templateData;
                  const restoredForm = cloudDraft?.formData;
                  const sanitizedRestoredTemplate =
                    sanitizePersistedTemplateData(restoredTemplate);
                  const sanitizedRestoredForm =
                    sanitizePersistedFormData(restoredForm);
                  if (
                    containsIrrecoverableCorruption(
                      sanitizedRestoredTemplate,
                    ) ||
                    containsIrrecoverableCorruption(sanitizedRestoredForm)
                  ) {
                    return;
                  }
                  if (cloudDraft?.selectedTemplateId)
                    setSelectedTemplateId(cloudDraft.selectedTemplateId);
                  // 🚨 CORREÇÃO: Sempre carregar a estrutura como está, sem
                  // looksLikeLegacyStructure (mesmo bug já corrigido em
                  // outros pontos deste arquivo).
                  setTemplateData(sanitizedRestoredTemplate);
                  setDataSource("cloud_autosave");
                  setIsModified(false);
                  setFormData(sanitizedRestoredForm);
                  if (cloudDraft?.partnerData)
                    setPartnerData(cloudDraft.partnerData);
                  if (Array.isArray(cloudDraft?.partnerExtras))
                    setPartnerExtras(cloudDraft.partnerExtras);
                  lastCloudAutosavePayloadRef.current = JSON.stringify({
                    selectedTemplateId:
                      cloudDraft?.selectedTemplateId || selectedTemplateId,
                    templateData: restoredTemplate,
                    formData: sanitizePersistedFormData(restoredForm),
                    partnerData: cloudDraft?.partnerData || {
                      ...DEFAULT_PARTNER,
                    },
                    partnerExtras: Array.isArray(cloudDraft?.partnerExtras)
                      ? cloudDraft.partnerExtras
                      : [],
                  });
                  setCloudSyncMeta({
                    updatedAt: cloudDraft?.updatedAt || null,
                    updatedBy: cloudDraft?.updatedBy || null,
                    source: "cloud_autosave",
                    error: null,
                  });
                  setCloudSyncState("saved");
                  return;
                }

                if (pendingLocalEnc) {
                  activeDocumentLockRef.current = true;
                  const dec = await CryptoShield.decrypt(pendingLocalEnc);
                  if (dec?.templateData && dec?.formData) {
                    const sanitizedLocalTemplate =
                      sanitizePersistedTemplateData(dec.templateData);
                    const sanitizedLocalForm = sanitizePersistedFormData(
                      dec.formData,
                    );
                    if (
                      containsIrrecoverableCorruption(sanitizedLocalTemplate) ||
                      containsIrrecoverableCorruption(sanitizedLocalForm)
                    ) {
                      return;
                    }
                    if (dec?.selectedTemplateId)
                      setSelectedTemplateId(dec.selectedTemplateId);
                    // 🚨 CORREÇÃO: Sempre carregar a estrutura como está,
                    // sem looksLikeLegacyStructure (mesmo bug já corrigido
                    // em outros pontos deste arquivo).
                    setTemplateData(sanitizedLocalTemplate);
                    setDataSource("localStorage");
                    setFormData(sanitizedLocalForm);
                    if (dec?.partnerData) setPartnerData(dec.partnerData);
                    if (Array.isArray(dec?.partnerExtras))
                      setPartnerExtras(dec.partnerExtras);
                    setIsModified(true);
                  }
                }
              } catch (e) {
                console.error("Erro ao restaurar draft:", e);
              }
            },
            onCancel: () => {
              try {
                localStorage.removeItem(DRAFT_KEY);
              } catch {
                /* ignore */
              }
            },
          });
        }
      } catch {}

      try {
        if (!appId) return;
        unsub = subscribeProcurationMaster(appId, (_freshMasterData) => {
          // 🚨 GUARD: Se o usuário está restaurando um backup manualmente,
          // não sobrescrever o template com dados da nuvem.
          if (activeDocumentLockRef.current) return;

          if (!_freshMasterData) return;
          const data = _freshMasterData || {};
          const nextStructure = Array.isArray(data?.structure)
            ? data.structure
            : null;
          if (nextStructure && nextStructure.length > 0) {
            masterStructureRef.current = nextStructure;
            masterVersionRef.current =
              data?.updatedAt?.toMillis?.() ||
              data?.updatedAt?.seconds * 1000 ||
              Date.now();
            // 🚨 CORREÇÃO: Carregar a estrutura diretamente, sem verificar
            // looksLikeLegacyStructure. A função detecta como "legacy"
            // qualquer estrutura que use IDs diferentes do template original
            // de 8 cláusulas, substituindo versões válidas (ex: 11 cláusulas)
            // pelo template padrão. Agora a estrutura é preservada como está.
            setTemplateData(buildMasterTemplatePayload(nextStructure));
            setIsModified(false);
            setDataSource("cloud_master");
          }
        });
      } catch {}
    };

    boot();
    return () => {
      try {
        if (typeof unsub === "function") unsub();
      } catch {
        /* ignore */
      }
    };
  }, []);

  useEffect(() => {
    const handler = (event) => {
      const detail =
        event?.detail && typeof event.detail === "object" ? event.detail : {};
      if (String(detail?.tab || "").trim() !== "procurations") return;
      if (detail?.documentId) {
        setDialogState({
          open: false,
          title: "",
          message: "",
          mode: "confirm",
          onConfirm: null,
          onCancel: null,
        });
      }
      void loadPublishedDocumentForEdit(detail).catch((e) => {
        clearQueuedPublishedDocumentEdit("procurations");
        publishedEditLockRef.current = false;
        activeDocumentLockRef.current = false;
        setEditingPublicationMeta(null);
        setDialogState({
          open: true,
          title: "Erro ao reabrir procuração publicada",
          message: "Erro: " + (e?.message || String(e)),
          mode: "info",
          onConfirm: null,
          onCancel: null,
        });
      });
    };

    browserEnv.addEventListener(publishedDocumentEditEventName, handler);
    return () =>
      window.removeEventListener(publishedDocumentEditEventName, handler);
  }, [loadPublishedDocumentForEdit]);

  useEffect(() => {
    const save = async () => {
      if (!isModified) return;
      const enc = await CryptoShield.encrypt({
        selectedTemplateId,
        templateData: sanitizePersistedTemplateData(templateData),
        formData: sanitizePersistedFormData(formData),
        partnerData,
        partnerExtras,
      });
      if (enc) localStorage.setItem(DRAFT_KEY, enc);
    };
    const t = setTimeout(save, 1000);
    return () => clearTimeout(t);
  }, [
    templateData,
    formData,
    selectedTemplateId,
    isModified,
    partnerData,
    partnerExtras,
  ]);

  useEffect(() => {
    if (!appId) return undefined;

    const autoSave = async () => {
      if (!isModified) return;
      if (isCloudAutosaveInFlightRef.current) return;

      const payloadObject = buildCloudAutosavePayload();
      const payloadString = JSON.stringify(payloadObject);
      if (
        !payloadString ||
        payloadString === lastCloudAutosavePayloadRef.current
      )
        return;

      isCloudAutosaveInFlightRef.current = true;
      setCloudSyncState("saving");
      setCloudSyncMeta((prev) => ({ ...(prev || {}), error: null }));

      try {
        await saveProcurationAutosave(
          {
            ...payloadObject,
            updatedAt: serverTimestamp(),
            updatedBy: auth?.currentUser?.email || null,
          },
          { merge: true },
        );
        lastCloudAutosavePayloadRef.current = payloadString;
        setCloudSyncState("saved");
        setCloudSyncMeta({
          updatedAt: new Date().toISOString(),
          updatedBy: auth?.currentUser?.email || null,
          source: "cloud_autosave",
          error: null,
        });
      } catch (e) {
        setCloudSyncState("error");
        setCloudSyncMeta((prev) => ({
          ...(prev || {}),
          error: e?.message || String(e),
          source: "cloud_autosave",
        }));
      } finally {
        isCloudAutosaveInFlightRef.current = false;
      }
    };

    const timer = setInterval(() => {
      autoSave();
    }, CLOUD_AUTOSAVE_INTERVAL_MS);

    return () => clearInterval(timer);
  }, [
    templateData,
    formData,
    selectedTemplateId,
    isModified,
    partnerData,
    partnerExtras,
  ]);

  const handleImportClient = async (picked) => {
    const raw = picked?.raw || picked || {};
    let hydrated = raw;
    let fullQual = "";
    try {
      const canonical = await hydrateAndBuildCanonicalClientQualification(raw);
      hydrated = canonical?.client || raw;
      fullQual = String(canonical?.text || "").trim();
    } catch {
      hydrated = raw;
      fullQual = String(
        buildCanonicalClientQualification(raw)?.text ||
          "[Qualificação não gerada]",
      ).trim();
    }
    const q =
      hydrated?.qualification ||
      raw?.qualification ||
      picked?.qualification ||
      {};
    const mainName =
      hydrated?.name ||
      raw?.name ||
      picked?.name ||
      q.physical?.name ||
      q.legal?.companyName ||
      q.minor?.name ||
      q.animal?.name ||
      "";
    const cpf =
      hydrated?.cpf ||
      raw?.cpf ||
      picked?.cpf ||
      q.physical?.cpf ||
      q.legal?.cnpj ||
      "";
    const email =
      hydrated?.contact?.email ||
      raw?.contact?.email ||
      picked?.email ||
      picked?.contact?.email ||
      "";
    const phone =
      hydrated?.contact?.phonePrimary ||
      hydrated?.contact?.phone ||
      raw?.contact?.phonePrimary ||
      raw?.contact?.phone ||
      picked?.phone ||
      picked?.contact?.phonePrimary ||
      picked?.contact?.phone ||
      "";

    const targetId =
      importTargetPartyId ||
      (Array.isArray(formData?.parties) ? formData.parties?.[0]?.id : null);
    if (targetId) {
      updateParty(
        targetId,
        "clientId",
        hydrated?.id || raw?.id || picked?.id || picked?.clientId || null,
      );
      updateParty(targetId, "name", mainName);
      updateParty(targetId, "cpf", cpf);
      updateParty(targetId, "email", email);
      updateParty(targetId, "phone", phone);
      updateParty(targetId, "qualificationData", hydrated);
      updateParty(targetId, "qualificationText", fullQual);
    }

    setShowImport(false);
  };

  const handlePartnerImport = (picked, targetIndex = 0) => {
    const raw = picked?.raw || picked || {};
    const name =
      picked?.name ||
      raw.qualification?.physical?.name ||
      raw.qualification?.legal?.companyName ||
      raw.qualification?.minor?.name ||
      "";
    const cpf =
      picked?.cpf ||
      raw.qualification?.physical?.cpf ||
      raw.qualification?.legal?.cnpj ||
      "";
    const oab =
      raw?.oab || raw?.qualification?.professional?.oab || picked?.oab || "";
    const address =
      picked?.address ||
      raw?.fullAddress ||
      raw?.address ||
      raw?.location?.address?.street ||
      "";

    const importedClientId =
      String(
        raw?.id || raw?.clientId || picked?.id || picked?.clientId || "",
      ).trim() || null;

    const applyPartnerImport = async () => {
      let hydratedPartner = raw;
      try {
        const canonical =
          await hydrateAndBuildCanonicalClientQualification(raw);
        hydratedPartner = canonical?.client || raw;
      } catch {
        hydratedPartner = raw;
      }

      if (targetIndex === 0) {
        setPartnerData((p) => ({
          ...(p || {}),
          active: true,
          name,
          cpf,
          oab,
          address: typeof address === "string" ? address : "",
          clientId: importedClientId,
          qualificationData: hydratedPartner,
        }));
      } else {
        setPartnerExtras((prev) => {
          const arr = Array.isArray(prev) ? [...prev] : [];
          const idx = Math.max(0, Number(targetIndex) - 1);
          arr[idx] = {
            ...(arr[idx] || { ...DEFAULT_PARTNER }),
            active: true,
            name,
            cpf,
            oab,
            address: typeof address === "string" ? address : "",
            clientId: importedClientId,
            qualificationData: hydratedPartner,
          };
          return arr;
        });
      }

      setShowPartnerImportModal(false);
      setIsModified(true);
    };

    applyPartnerImport();
  };

  const handleRelinkFromCrm = () => {
    setDialogState({
      open: true,
      title: "Revincular do CRM",
      message:
        "Esta ação não apaga dados do cadastro. O sistema irá recarregar do CRM os clientes vinculados por clientId e substituir snapshots antigos da procuração pelos dados canônicos atuais. Deseja continuar?",
      mode: "confirm",
      onConfirm: async () => {
        const currentParties = Array.isArray(formData?.parties)
          ? formData.parties
          : [];
        const refreshedParties = await Promise.all(
          currentParties.map(async (party) => {
            const linkedId = String(party?.clientId || "").trim();
            if (!linkedId) return party;
            try {
              const raw = await fetchClientData(linkedId);
              if (!raw) return party;
              let hydrated = raw;
              let qualificationText = "";
              try {
                const canonical =
                  await hydrateAndBuildCanonicalClientQualification(raw);
                hydrated = canonical?.client || raw;
                qualificationText = String(canonical?.text || "").trim();
              } catch {
                hydrated = raw;
                qualificationText = String(
                  buildCanonicalClientQualification(raw)?.text ||
                    party?.qualificationText ||
                    "",
                ).trim();
              }

              return {
                ...party,
                name: String(
                  hydrated?.name ||
                    hydrated?.qualification?.physical?.name ||
                    hydrated?.qualification?.legal?.companyName ||
                    hydrated?.qualification?.animal?.name ||
                    party?.name ||
                    "",
                ).trim(),
                cpf: String(
                  hydrated?.qualification?.physical?.cpf ||
                    hydrated?.qualification?.legal?.cnpj ||
                    hydrated?.cpf ||
                    party?.cpf ||
                    "",
                ).trim(),
                email: String(
                  hydrated?.contact?.email || party?.email || "",
                ).trim(),
                phone: String(
                  hydrated?.contact?.phonePrimary ||
                    hydrated?.contact?.phone ||
                    party?.phone ||
                    "",
                ).trim(),
                qualificationData: hydrated,
                qualificationText,
              };
            } catch {
              return party;
            }
          }),
        );

        setFormData((prev) => ({ ...(prev || {}), parties: refreshedParties }));
        setIsModified(true);
      },
    });
  };

  const handleResetDraft = () => {
    const message =
      "VOCÊ TEM CERTEZA QUE QUER FAZER ISSO?\n\nEssa ação apagará o rascunho local (draft) e restaurará a procuração para o estado de fábrica.\n\nEsta alteração não poderá ser desfeita.";
    setDialogState({
      open: true,
      title: "Confirmar Reset",
      message,
      mode: "confirm",
      onConfirm: () => {
        // [CIRURGIA RDA V1.1] Desidratação do Call Stack via setTimeout (Cura do Zombie State)
        setTimeout(() => {
          try {
            localStorage.removeItem(DRAFT_KEY);
          } catch {
            /* ignore */
          }

          const master = Array.isArray(masterStructureRef.current)
            ? masterStructureRef.current
            : null;
          if (!FORCE_LOCAL_PROCURATION_RECOVERY && master) {
            setTemplateData(buildMasterTemplatePayload(master));
            setDataSource("cloud_master");
          } else {
            setTemplateData(buildTemplateFromSystem(selectedTemplate));
            setDataSource(
              FORCE_LOCAL_PROCURATION_RECOVERY
                ? "forced_local_recovery"
                : "local_template",
            );
          }

          setFormData({
            parties: [
              {
                id: `party_${Date.now()}`,
                clientId: null,
                name: "",
                cpf: "",
                email: "",
                phone: "",
                role: "OUTORGANTE",
                qualificationText: "",
                qualificationData: null,
              },
            ],
            city: "Curitiba",
            ramoDireito: "Civil",
            ramosDireito: ["Civil"],
            posicaoCliente: "",
            posicoesCliente: [],
            objetoPrincipal: "",
            quereladoName: "",
            parteContrariaName: "",
            dataFato: "",
            descricaoFato: "",
            autoridadeCoatoraNome: "",
            autoridadeCoatoraOrgao: "",
            atoCoatorData: "",
            atoCoatorDescricao: "",
            patrimonialImobiliarioEnabled: false,
            bensImoveisEspecificos: "",
            bemImovelMatricula: "",
            bemImovelCartorio: "",
            bemImovelEndereco: "",
            bemImovelNaturezaDireito: "",
            bemImovelObservacoes: "",
            envolveCriptoEnabled: false,
            investigacaoDefensivaEnabled: false,
            arbitragemEnabled: false,
            complianceInternoEnabled: false,
            validadeMandatoTipo: "INDETERMINADO",
            validadeMandatoData: "",
            validadeMandatoAto: "",
          });
          setPartnerData({ ...DEFAULT_PARTNER });
          setPartnerExtras([]);
          setActiveBlockData(null);
          setEditingBlockId(null);
          setMode("producao");
        }, 150);
      },
    });
  };

  const handleDownloadBackup = () => {
    try {
      const structureToSave = templateData?.structure || [];
      if (!Array.isArray(structureToSave) || structureToSave.length === 0) {
        alert(
          "A procuração está vazia ou ainda carregando. Nada para fazer backup.",
        );
        return;
      }

      const payload = JSON.stringify(
        {
          selectedTemplateId,
          templateData: sanitizePersistedTemplateData(templateData),
          formData: sanitizePersistedFormData(formData),
          partnerData,
          partnerExtras,
        },
        null,
        2,
      );
      const blob = new Blob([payload], { type: "application/json" });
      const url = URL.createObjectURL(blob);

      const dateStr = new Date().toISOString().split("T")[0];
      const a = document.createElement("a");
      a.href = url;
      a.download = `RDA_Procuracao_Backup_${dateStr}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      setDialogState({
        open: true,
        title: "Backup Concluído",
        message:
          "✅ SUCESSO!\n\nUma cópia de segurança da procuração foi baixada para o seu computador (pasta Downloads).\n\nVocê está seguro para realizar qualquer operação de sistema.",
        mode: "info",
        onConfirm: null,
      });
    } catch (e) {
      console.error("Erro ao gerar backup:", e);
      alert("Erro ao tentar gerar o arquivo de backup.");
    }
  };

  const handleDownloadCloudMaster = async () => {
    try {
      if (!appId) throw new Error("ProjectId do Firebase não encontrado.");
      const data = await getProcurationMaster(appId);
      if (!data)
        throw new Error("Nenhum mestre de procuração encontrado na nuvem.");

      const structure = Array.isArray(data?.structure) ? data.structure : [];
      if (structure.length === 0)
        throw new Error("O mestre da nuvem está vazio ou inválido.");

      const payload = JSON.stringify(
        {
          source: "cloud_master",
          downloadedAt: new Date().toISOString(),
          updatedBy: data?.updatedBy || null,
          updatedAt: data?.updatedAt || null,
          templateData: {
            id: "cloud_master",
            title: "Procuração Mestre (Nuvem)",
            structure,
          },
        },
        null,
        2,
      );

      const blob = new Blob([payload], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const dateStr = new Date().toISOString().split("T")[0];
      const a = document.createElement("a");
      a.href = url;
      a.download = `RDA_Procuracao_CloudMaster_${dateStr}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      setDialogState({
        open: true,
        title: "Cloud Master Baixado",
        message:
          "✅ SUCESSO!\n\nO mestre da nuvem foi baixado como JSON para inspeção local, sem sobrescrever nada no sistema.",
        mode: "info",
        onConfirm: null,
      });
    } catch (e) {
      setDialogState({
        open: true,
        title: "Erro ao Baixar Mestre da Nuvem",
        message: "Erro: " + (e?.message || String(e)),
        mode: "info",
        onConfirm: null,
      });
    }
  };

  const handleForceSyncCloud = async () => {
    try {
      if (!appId) throw new Error("ProjectId do Firebase não encontrado.");
      const data = await getProcurationMaster(appId);
      if (!data) throw new Error("Mestre da nuvem não encontrado.");
      const nextStructure = Array.isArray(data?.structure)
        ? data.structure
        : null;
      if (!nextStructure || nextStructure.length === 0)
        throw new Error("Estrutura vazia na nuvem.");

      try {
        localStorage.removeItem(DRAFT_KEY);
      } catch {
        /* ignore */
      }

      masterStructureRef.current = nextStructure;
      masterVersionRef.current =
        data?.updatedAt?.toMillis?.() ||
        data?.updatedAt?.seconds * 1000 ||
        Date.now();
      setTemplateData({ structure: nextStructure });
      setIsModified(false);
      setDataSource("cloud_master");
      setCloudSyncState("saved");
      setEditingBlockId(null);
      setActiveBlockData(null);

      setDialogState({
        open: true,
        title: "Sincronização concluída",
        message: `✅ Template sincronizado com a nuvem!\n\n${nextStructure.length} blocos carregados do master_v1.\nCache local limpo com sucesso.`,
        mode: "info",
        onConfirm: null,
      });
    } catch (e) {
      setDialogState({
        open: true,
        title: "Falha na sincronização",
        message: String(
          e?.message || e || "Não foi possível sincronizar com a nuvem.",
        ),
        mode: "alert",
        onConfirm: null,
      });
    }
  };

  const handleRestoreBackup = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".json";

    input.onchange = (e) => {
      const file = e.target.files[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          // 🚨 TRAVAR O SUBSCRIBE IMEDIATAMENTE: antes de qualquer setState,
          // para que o onSnapshot do Firestore NÃO sobrescreva o restore.
          activeDocumentLockRef.current = true;

          const restored = JSON.parse(event.target.result);
          const restoredTemplate = restored?.templateData || null;
          const restoredForm = restored?.formData || null;
          const restoredPartnerData = restored?.partnerData || null;
          const restoredPartnerExtras = restored?.partnerExtras || null;
          const normalizedRestoredForm =
            sanitizePersistedFormData(restoredForm);

          if (
            !restoredTemplate ||
            !Array.isArray(restoredTemplate?.structure)
          ) {
            throw new Error(
              "O arquivo não contém uma estrutura válida de procuração.",
            );
          }

          if (restored?.selectedTemplateId)
            setSelectedTemplateId(restored.selectedTemplateId);

          // 🚨 CORREÇÃO: NÃO verificar looksLikeLegacyStructure no restore.
          // O JSON baixado pelo próprio sistema ("Baixar Mestre da Nuvem")
          // pode usar uma estrutura de IDs diferente da atual, mas o conteúdo
          // é válido e deve ser carregado DIRETAMENTE na tela.
          setTemplateData(restoredTemplate);
          if (normalizedRestoredForm) setFormData(normalizedRestoredForm);
          if (restoredPartnerData) setPartnerData(restoredPartnerData);
          if (Array.isArray(restoredPartnerExtras))
            setPartnerExtras(restoredPartnerExtras);

          setIsModified(true);
          setDataSource("backup_json");

          setDialogState({
            open: true,
            title: "Restauração Concluída",
            message:
              '✅ SUCESSO!\n\nA procuração foi restaurada a partir do backup.\n\nO sistema está em modo de edição com os dados recuperados.\n\nUse "Publicar Mestre" para salvar na nuvem se desejar.',
            mode: "info",
            onConfirm: null,
          });
        } catch (error) {
          console.error("Erro ao restaurar backup:", error);
          alert(
            "Erro ao ler o arquivo de backup. Verifique se é um JSON válido.",
          );
        }
      };
      reader.readAsText(file);
    };

    input.click();
  };

  const handleSaveAsMaster = async () => {
    const message =
      "☢️ OPERAÇÃO LÁZARO (ADMIN) ☢️\n\n" +
      "ATENÇÃO: Esta ação vai sobrescrever o Mestre de Procurações na nuvem com o que está na tela.\n\n" +
      "Depois, o sistema pode ser resetado com segurança para voltar ao mestre.\n\n" +
      "Continuar?";

    setDialogState({
      open: true,
      title: "Publicar Mestre (Admin)",
      message,
      mode: "confirm",
      onConfirm: async () => {
        setIsSaving(true);
        // 🚨 TRAVAR O SUBSCRIBE: impede que o onSnapshot dispare
        // looksLikeLegacyStructure e substitua as 11 cláusulas por 8.
        activeDocumentLockRef.current = true;

        try {
          const structure = Array.isArray(templateData?.structure)
            ? templateData.structure
            : [];
          if (structure.length === 0) throw new Error("Estrutura vazia.");
          await setProcurationMaster(
            { structure, updatedBy: auth?.currentUser?.email || null },
            appId,
          );
          masterStructureRef.current = structure;
          masterVersionRef.current = Date.now();
          setDataSource("cloud_master");
          setIsModified(false);
          try {
            localStorage.removeItem(DRAFT_KEY);
          } catch {
            /* ignore */
          }
          lastCloudAutosavePayloadRef.current = JSON.stringify(
            buildCloudAutosavePayload(),
          );
          setCloudSyncState("saved");
          setCloudSyncMeta({
            updatedAt: new Date().toISOString(),
            updatedBy: auth?.currentUser?.email || null,
            source: "cloud_master",
            error: null,
          });
          setDialogState({
            open: true,
            title: "Sucesso",
            message:
              "✅ SUCESSO! O Mestre de Procurações foi publicado na nuvem.",
            mode: "info",
            onConfirm: null,
          });
        } catch (e) {
          setDialogState({
            open: true,
            title: "Erro ao Publicar Mestre",
            message: "Erro: " + (e?.message || String(e)),
            mode: "info",
            onConfirm: null,
          });
        } finally {
          setIsSaving(false);
        }
      },
    });
  };

  const handleExportMD = () => {
    if (!templateData?.structure) return alert("Nenhuma estrutura carregada.");

    const htmlToMarkdown = (html) => {
      let text = String(html || "");
      text = text.replace(
        /<span[^>]*class="variable-highlight"[^>]*>(.*?)<\/span>/gi,
        "`$1`",
      );
      text = text.replace(/<strong>(.*?)<\/strong>/gi, "**$1**");
      text = text.replace(/<b>(.*?)<\/b>/gi, "**$1**");
      text = text.replace(/<em>(.*?)<\/em>/gi, "*$1*");
      text = text.replace(/<i>(.*?)<\/i>/gi, "*$1*");
      text = text.replace(/<br\s*[\/]?>/gi, "\n");
      text = text.replace(/<\/p>\s*<p>/gi, "\n\n");
      text = text.replace(/<\/?p>/gi, "");
      text = text.replace(/<ul>/gi, "\n");
      text = text.replace(/<\/ul>/gi, "\n");
      text = text.replace(/<li>/gi, "- ");
      text = text.replace(/<\/li>/gi, "\n");
      text = text.replace(/<[^>]*>?/gm, "");
      text = text.replace(/&nbsp;/g, " ");
      return text.trim();
    };

    const partyNames =
      (Array.isArray(formData?.parties) ? formData.parties : [])
        .map((p) => String(p?.name || "").trim())
        .filter(Boolean)
        .join(", ") || "(não informado)";
    const ramoDisplay =
      (Array.isArray(formData?.ramosDireito)
        ? formData.ramosDireito
        : [formData?.ramoDireito]
      )
        .filter(Boolean)
        .join(" / ") || "(não informado)";
    const posicaoDisplay =
      (Array.isArray(formData?.posicoesCliente) &&
      formData.posicoesCliente.length > 0
        ? formData.posicoesCliente
        : [formData?.posicaoCliente]
      )
        .filter(Boolean)
        .join(" / ") || "(não informado)";

    let md = "# PROCURAÇÃO — REVISÃO COMPLETA (RDA SYSTEM)\n\n";
    md += `> **Data da exportação:** ${new Date().toLocaleDateString("pt-BR", { day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" })}\n\n`;
    md += `## FICHA RESUMO\n`;
    md += `| Campo | Valor |\n|---|---|\n`;
    md += `| **Outorgante(s)** | ${partyNames} |\n`;
    md += `| **Ramo do Direito** | ${ramoDisplay} |\n`;
    md += `| **Posição do Cliente** | ${posicaoDisplay} |\n`;
    md += `| **Objeto Principal** | ${String(formData?.objetoPrincipal || "").trim() || "(não informado)"} |\n`;
    md += `| **Parte Contrária** | ${String(formData?.parteContrariaName || "").trim() || "(não informado)"} |\n`;
    if (String(formData?.autoridadeCoatoraNome || "").trim()) {
      md += `| **Autoridade Coatora** | ${String(formData.autoridadeCoatoraNome).trim()} |\n`;
      md += `| **Órgão Vinculado** | ${String(formData?.autoridadeCoatoraOrgao || "").trim() || "(não informado)"} |\n`;
      md += `| **Data do Ato Coator** | ${String(formData?.atoCoatorData || "").trim() || "(não informado)"} |\n`;
    }
    md += `| **Cidade** | ${String(formData?.city || "Curitiba")} |\n`;
    md += `\n---\n\n`;

    const structure = Array.isArray(templateData.structure)
      ? templateData.structure
      : [];

    structure.forEach((block) => {
      if (!isBlockVisible(block)) return;

      const clauseTitle = getStandardClauseTitle(block);
      md += `## ${clauseTitle}\n`;

      const cond = String(block?.condition || "ALWAYS").trim();
      if (cond !== "ALWAYS") {
        md += `> *Condição:* \`${cond}\``;
        if (Array.isArray(block?.tags) && block.tags.length > 0) {
          md += ` | *Tags:* \`${block.tags.join(", ")}\``;
        }
        md += `\n\n`;
      }

      const hasChildren =
        Array.isArray(block?.children) && block.children.length > 0;

      if (hasChildren) {
        const resolvedParent = String(block?.content || "").trim();
        if (resolvedParent) {
          const parentResolved = parseText(resolvedParent);
          const parentMd = htmlToMarkdown(parentResolved);
          if (parentMd) md += `${parentMd}\n\n`;
        }

        block.children.forEach((child, cIdx) => {
          if (!isChildVisibleInHierarchy(block.children, cIdx)) return;
          const childContent = String(child?.content || "").trim();
          if (!childContent) return;

          const childResolved = parseText(childContent);
          const childMd = htmlToMarkdown(childResolved);
          const level = normalizeClauseLevel(child?.level);
          const indent = level > 0 ? "  ".repeat(level) : "";
          const prefix = child?.title
            ? `**${String(child.title).trim()}:** `
            : "";

          md += `${indent}- ${prefix}${childMd}\n\n`;
        });
      } else {
        const content = String(block?.content || "").trim();
        if (content) {
          const resolved = parseText(content);
          md += `${htmlToMarkdown(resolved)}\n\n`;
        }
      }

      md += `---\n\n`;
    });

    const blob = new Blob([md], { type: "text/markdown;charset=utf-8" });
    const url = URL.createObjectURL(blob);

    const dateStr = new Date().toISOString().split("T")[0];
    const a = document.createElement("a");
    a.href = url;
    a.download = `Revisao_Procuracao_RDA_${dateStr}.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const buildFriendlyProcurationName = () => {
    const sourceParties = Array.isArray(parties) ? parties : [];
    const primaryParty = sourceParties[0] || {};
    const principalNames = sourceParties
      .map((party) =>
        String(
          party?.qualificationData?.qualification?.animal?.name ||
            party?.qualificationData?.qualification?.legal?.companyName ||
            party?.qualificationData?.qualification?.minor?.name ||
            party?.qualificationData?.qualification?.estate?.deceasedName ||
            party?.qualificationData?.qualification?.estate?.name ||
            party?.qualificationData?.qualification?.physical?.socialName ||
            party?.qualificationData?.qualification?.physical?.name ||
            party?.qualificationData?.name ||
            party?.name ||
            "",
        ).trim(),
      )
      .filter(Boolean);
    const representativeNames = sourceParties
      .map((party) =>
        String(
          party?.qualificationData?.qualification?.animal?.responsible?.name ||
            party?.qualificationData?.qualification?.animal?.guardian?.name ||
            party?.qualificationData?.qualification?.minor?.representative
              ?.name ||
            party?.qualificationData?.qualification?.legal?.representative
              ?.name ||
            party?.qualificationData?.qualification?.estate?.inventorier
              ?.name ||
            party?.qualificationData?.qualification?.estate?.representative
              ?.name ||
            party?.qualificationData?.qualification?.estate?.inventor?.name ||
            "",
        ).trim(),
      )
      .filter(Boolean);
    return generateDocumentFileName("Procuracao", primaryParty, {
      mainPartyName: String(primaryParty?.name || "").trim(),
      mainPartyNames: principalNames,
      representativeName: String(
        primaryParty?.qualificationData?.qualification?.animal?.responsible
          ?.name ||
          primaryParty?.qualificationData?.qualification?.animal?.guardian
            ?.name ||
          primaryParty?.qualificationData?.qualification?.minor?.representative
            ?.name ||
          primaryParty?.qualificationData?.qualification?.legal?.representative
            ?.name ||
          "",
      ).trim(),
      representativeNames,
      quereladoName: String(formData?.quereladoName || "").trim(),
      parteContrariaName: String(formData?.parteContrariaName || "").trim(),
      objetoPrincipal: String(formData?.objetoPrincipal || "").trim(),
      ramoDireito: selectedRamoKeys.join("_").trim(),
    });
  };

  const handleImportMD = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".md,text/markdown";

    input.onchange = (e) => {
      const file = e.target.files?.[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const md = String(event.target.result || "");
          const structure = Array.isArray(templateData?.structure)
            ? templateData.structure
            : [];
          if (structure.length === 0)
            throw new Error(
              "Estrutura vazia. Carregue uma procuração antes de importar.",
            );

          const markdownToHtml = (raw) => {
            const src = String(raw || "")
              .replace(/\r\n/g, "\n")
              .trim();
            if (!src) return "";

            const lines = src.split("\n");
            const htmlParts = [];
            let i = 0;
            while (i < lines.length) {
              const line = lines[i];
              if (/^\s*-\s+/.test(line)) {
                const items = [];
                while (i < lines.length && /^\s*-\s+/.test(lines[i])) {
                  items.push(lines[i].replace(/^\s*-\s+/, "").trim());
                  i += 1;
                }
                const li = items
                  .map((t) => {
                    let x = t;
                    x = x.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
                    x = x.replace(/\*([^*]+)\*/g, "<em>$1</em>");
                    return `<li>${x}</li>`;
                  })
                  .join("");
                htmlParts.push(`<ul>${li}</ul>`);
                continue;
              }

              const paraLines = [];
              while (i < lines.length && String(lines[i] || "").trim() !== "") {
                paraLines.push(lines[i]);
                i += 1;
              }
              while (i < lines.length && String(lines[i] || "").trim() === "")
                i += 1;

              if (paraLines.length > 0) {
                let text = paraLines.join("\n");
                text = text.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
                text = text.replace(/\*([^*]+)\*\*/g, "<em>$1</em>");
                text = text.replace(/\n/g, "<br>");
                htmlParts.push(`<p>${text}</p>`);
              } else {
                i += 1;
              }
            }

            return htmlParts.join("");
          };

          const stripMetaLines = (raw) => {
            return String(raw || "")
              .split(/\r?\n/g)
              .filter((l) => {
                const s = String(l || "").trim();
                if (!s) return true;
                if (s.startsWith(">")) return false;
                if (s === "---") return false;
                if (s.startsWith("#")) return false;
                return true;
              })
              .join("\n")
              .trim();
          };

          const blocksById = new Map();
          structure.forEach((b) => {
            if (!b) return;
            blocksById.set(String(b.id), b);
          });

          const lines = md.replace(/\r\n/g, "\n").split("\n");
          let currentBlockId = null;
          const buffer = [];
          const flush = () => {
            const raw = buffer.join("\n").trim();
            buffer.length = 0;
            if (!raw) return;
            if (!currentBlockId || !blocksById.has(currentBlockId)) return;
            const cleaned = stripMetaLines(raw);
            blocksById.get(currentBlockId).content = markdownToHtml(cleaned);
          };

          for (const line of lines) {
            const blockMatch = line.match(/^##\s+\[ID:\s*([^\]]+)\]\s*-/);
            if (blockMatch) {
              flush();
              currentBlockId = String(blockMatch[1] || "").trim();
              continue;
            }
            buffer.push(line);
          }
          flush();

          setTemplateData({ structure: [...structure] });
          setIsModified(true);
          setDataSource("localStorage");

          setDialogState({
            open: true,
            title: "Importação Concluída",
            message:
              "✅ SUCESSO!\n\nA revisão (.MD) foi importada e aplicada à procuração que está na tela.\n\nRevise visualmente e, se desejar, publique o Mestre.",
            mode: "info",
            onConfirm: null,
          });
        } catch (err) {
          console.error("Erro ao importar revisão (.MD):", err);
          alert(
            "Erro ao importar revisão (.MD): " + (err?.message || String(err)),
          );
        }
      };
      reader.readAsText(file);
    };

    input.click();
  };

  const getBlockNumberById = (blockId) => {
    const blocks = templateData?.structure || [];
    let n = 0;
    for (const b of blocks) {
      if (!b) continue;
      const upper = String(b?.title || "").toUpperCase();
      const isPreambulo = b?.id === "preambulo" || upper.includes("PREÂMBULO");
      if (!isPreambulo && b?.id !== "assinaturas" && isBlockVisible(b)) n += 1;
      if (b?.id === blockId)
        return !isPreambulo && b?.id !== "assinaturas" ? n : null;
    }
    return null;
  };

  const stripNumericPrefix = (raw) => {
    return String(raw || "")
      .replace(/^\s*\d+(?:\.\d+)?\s*-\s*/, "")
      .trim();
  };

  const getQuickNavMeta = (block) => {
    const t = String(block?.title || "");
    const cleaned = stripNumericPrefix(t) || "Seção";

    const normalizeQuickNavLabel = (raw) => {
      const s = String(raw || "").trim();
      if (!s) return "";
      const hasLower = /[a-zà-ÿ]/.test(s);
      const hasUpper = /[A-ZÀ-Ÿ]/.test(s);
      const shouldTitleCase = hasUpper && !hasLower;
      if (!shouldTitleCase) return s;

      const words = s.toLowerCase().split(/\s+/g);
      const small = new Set([
        "de",
        "do",
        "da",
        "dos",
        "das",
        "e",
        "ou",
        "a",
        "o",
        "as",
        "os",
        "em",
        "no",
        "na",
        "nos",
        "nas",
        "por",
        "para",
        "com",
        "sem",
        "ao",
        "aos",
        "à",
        "às",
      ]);
      return words
        .map((w, idx) => {
          const clean = w.replace(/[^a-zà-ÿ0-9]+/gi, "");
          if (clean && clean.length <= 4 && /^[a-z]+$/i.test(clean)) {
            if (
              ["lgpd", "oab", "cpf", "cnpj", "stj", "stf"].includes(
                clean.toLowerCase(),
              )
            )
              return w.toUpperCase();
          }
          if (idx !== 0 && small.has(clean)) return w;
          return w.charAt(0).toUpperCase() + w.slice(1);
        })
        .join(" ");
    };

    const meta = buildQuickNavMeta(block, () => cleaned);
    return { ...meta, label: normalizeQuickNavLabel(meta.label) };
  };

  const getStandardClauseTitle = (block) => {
    if (!block) return "";
    const upper = String(block?.title || "").toUpperCase();
    const isPreambulo =
      block?.id === "preambulo" || upper.includes("PREÂMBULO");
    if (isPreambulo) return "PREÂMBULO";
    if (block?.id === "assinaturas")
      return stripNumericPrefix(block?.title || "ASSINATURAS");
    const n = getBlockNumberById(block.id);
    const label = stripNumericPrefix(block?.title || "").toUpperCase();
    if (!n) return label;
    return `CLÁUSULA ${n}ª - ${label}`;
  };

  const parseText = (text) => {
    if (!text) return "";
    let out = repairMojibakeText(text);

    // Compatibilidade: textos antigos podem ter markdown **...**
    out = out.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");

    out = out.replace(/\[\[REF_CLAUSE:([^\]]+)\]\]/g, (_, rawId) => {
      const id = String(rawId || "").trim();
      const num = getBlockNumberById(id);
      if (!num) return "Cláusula ?";
      return `Cláusula ${num}ª`;
    });

    const partiesNamesUpper =
      parties.length > 0
        ? parties
            .map((p) => String(p?.name || "").trim())
            .filter(Boolean)
            .join(" E ")
            .toUpperCase()
        : "OUTORGANTE";
    const firstPartyCpf = String(firstParty?.cpf || "").trim();
    const firstPartyEmail = String(firstParty?.email || "").trim();

    const resolvePartyQualification = (party) => {
      const structured = party?.qualificationData;
      if (structured && structured?.qualification && structured?.type) {
        return String(
          buildCanonicalClientQualification(structured)?.text || "",
        ).trim();
      }
      // Fallback sem cadastro estruturado: nome digitado direto, precisa
      // ser escapado (vira HTML confiável via [[*_QUALIFICACAO]] mais abaixo).
      return escapeHtml(String(party?.name || "...").trim());
    };

    const formatQualificationList = (items) => {
      const list = (Array.isArray(items) ? items : [])
        .map((item) => String(item || "").trim())
        .filter(Boolean);

      if (list.length === 0) return "";
      if (list.length === 1) return list[0];

      const stripFinalPunctuation = (value) =>
        String(value || "")
          .trim()
          .replace(/[\s;.]+$/g, "");

      if (list.length === 2) {
        return `${stripFinalPunctuation(list[0])} e ${list[1]}`;
      }

      const head = list.slice(0, -1).map(stripFinalPunctuation).join("; ");
      const tail = list[list.length - 1];
      return `${head}; e ${tail}`;
    };

    const outorganteQual = resolvePartyQualification(firstParty);
    const outorganteQualAll =
      parties.length > 0
        ? formatQualificationList(
            parties.map((p) => resolvePartyQualification(p)),
          )
        : outorganteQual;
    const activePartners = [
      partnerData,
      ...(Array.isArray(partnerExtras) ? partnerExtras : []),
    ]
      .filter(
        (p) =>
          p &&
          (p.active ||
            p.name ||
            p.oab ||
            p.cpf ||
            p.address ||
            p.qualificacaoCompleta),
      )
      .map((p) => ({
        ...p,
        name: String(p?.name || "").trim(),
        oab: String(p?.oab || "").trim(),
        cpf: String(p?.cpf || "").trim(),
        address: String(p?.address || "").trim(),
      }))
      .filter(
        (p) => p.name || p.oab || p.cpf || p.address || p.qualificacaoCompleta,
      );
    const contractedQualificationText = (() => {
      const partnerQualifications = activePartners
        .map((partner) => {
          // Se o parceiro tem qualificacaoCompleta preenchida, usa o texto manual
          // (campo livre digitado pelo operador — escapar antes de virar HTML)
          const qualText = partner?.qualificacaoCompleta || "";
          if (qualText.trim().length > 0) {
            return escapeHtml(qualText.trim());
          }
          const structured = partner?.qualificationData;
          if (structured && structured?.qualification && structured?.type) {
            return String(
              buildCanonicalClientQualification(structured)?.text || "",
            ).trim();
          }
          return String(partner?.name || "").trim();
        })
        .filter(Boolean);

      return formatQualificationList([
        officeQualificationText,
        ...partnerQualifications,
      ]);
    })();

    const outorgadoQual = contractedQualificationText;
    const outorgadoNames = [
      String(
        officeQualificationClient?.qualification?.physical?.socialName ||
          officeQualificationClient?.qualification?.physical?.name ||
          officeQualificationClient?.name ||
          "OUTORGADO",
      ).trim(),
      ...activePartners
        .map((partner) => String(partner?.name || "").trim())
        .filter(Boolean),
    ].filter(Boolean);
    const outorgadoNomeExibicao = outorgadoNames.join(" E ");
    const nomeacaoOutorgadaTexto =
      "nomeia a parte <strong>OUTORGADA</strong> como sua bastante procuradora, conferindo-lhe poderes específicos para a representação técnica e defesa de seus interesses atrelados ao seguinte escopo:";

    const fmtDateBr = (raw) => {
      const s = String(raw || "").trim();
      if (!s) return "";
      const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (m) return `${m[3]}/${m[2]}/${m[1]}`;
      const m2 = s.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
      if (m2) return s;
      return s;
    };

    const mandateType = String(
      formData?.validadeMandatoTipo || "INDETERMINADO",
    ).toUpperCase();
    let validadeMandatoText =
      "Em observância à jurisprudência pacificada do Superior Tribunal de Justiça (STJ), esta procuração possui validade por prazo indeterminado, produzindo efeitos até formal revogação, não sendo o simples decurso do tempo causa lícita para a recusa deste instrumento por qualquer juízo, cartório ou instituição financeira.";
    if (mandateType === "DETERMINADO") {
      const d = fmtDateBr(formData?.validadeMandatoData);
      validadeMandatoText = d
        ? `A presente procuração possui validade estrita até o dia <strong>${escapeHtml(d)}</strong>.`
        : "A presente procuração possui validade por prazo determinado.";
    }
    if (mandateType === "ATO_ESPECIFICO") {
      const ato = String(formData?.validadeMandatoAto || "").trim();
      validadeMandatoText = ato
        ? `A presente procuração é outorgada exclusivamente para o seguinte fim específico: <strong>${escapeHtml(ato)}</strong>.`
        : "A presente procuração é outorgada exclusivamente para fim específico.";
    }

    const getVisibleHierarchicalChildren = (children) => {
      const arr = Array.isArray(children) ? children : [];
      return arr.filter((child, index) =>
        isChildVisibleInHierarchy(arr, index),
      );
    };

    const getPoderesSetoriaisHtml = () => {
      const blocks = Array.isArray(templateData?.structure)
        ? templateData.structure
        : [];
      const clause5 = blocks.find((block) => block?.id === "cl_05_setorial");
      const children = Array.isArray(clause5?.children) ? clause5.children : [];
      const visibleChildren = getVisibleHierarchicalChildren(children);
      if (visibleChildren.length === 0) {
        return "<p>(Selecione manualmente ao menos um poder setorial na Cláusula 5.)</p>";
      }
      return visibleChildren
        .map((child) => `<p>${String(child?.content || "").trim()}</p>`)
        .join("");
    };

    const getContextoProcessual = () => {
      const isMandadoSeguranca =
        selectedRamoKeys.includes("MandadoSeguranca");
      const coatora = String(formData?.autoridadeCoatoraNome || "").trim();
      if (isMandadoSeguranca && coatora && coatora !== "...") {
        const O = "<strong>OUTORGANTE</strong>";
        const coatoraBold = `<strong>${escapeHtml(coatora.toUpperCase())}</strong>`;
        const orgao = String(formData?.autoridadeCoatoraOrgao || "").trim();
        const orgaoText =
          orgao && orgao !== "..."
            ? ` vinculada a <strong>${escapeHtml(orgao.toUpperCase())}</strong>`
            : "";
        const isImpetrado = (
          selectedPosicoes.length > 0
            ? selectedPosicoes
            : [formData?.posicaoCliente]
        ).some((r) => /IMPETRADO|COATORA/i.test(String(r || "")));
        if (isImpetrado) {
          return `A presente outorga visa a defesa dos interesses da parte ${O} em Mandado de Segurança relativo ao ato praticado por ${coatoraBold}${orgaoText}, na qualidade de autoridade coatora, litisconsorte passivo necessário ou terceiro juridicamente interessado.`;
        }
        return `A presente outorga visa a impetração e o acompanhamento de Mandado de Segurança em favor da parte ${O}, contra ato ilegal ou abusivo praticado por ${coatoraBold}, na qualidade de autoridade coatora${orgaoText}.`;
      }
      const pc = String(formData?.parteContrariaName || "").trim();
      if (!pc || pc === "...") return "";
      const pcBold = `<strong>${escapeHtml(pc.toUpperCase())}</strong>`;
      const O = "<strong>OUTORGANTE</strong>";
      const roles = (
        selectedPosicoes.length > 0
          ? selectedPosicoes
          : [formData?.posicaoCliente]
      )
        .map((r) =>
          String(r || "")
            .trim()
            .toUpperCase(),
        )
        .filter(Boolean);
      const role = roles[0] || "";
      const tokens = roles.flatMap((r) =>
        r
          .split("/")
          .map((t) => t.trim())
          .filter(Boolean),
      );
      const has = (keyword) =>
        tokens.some((t) => t.includes(keyword)) ||
        roles.some((r) => r.includes(keyword));
      const isPenal = selectedRamoKeys.includes("Penal");
      const isTrabalhista = selectedRamoKeys.includes("Trabalho");
      const isPrevidenciario = selectedRamoKeys.includes("Previdenciario");
      const isAdministrativo = selectedRamoKeys.includes("Administrativo");
      const isFamilia = selectedRamoKeys.includes("Familia");
      const isSocietario = selectedRamoKeys.includes("SocietarioEmpresarial");
      const isPenalPubAtivo =
        isPenal && (has("ASSISTENTE") || /V[ÍI]TIMA|OFENDIDO/i.test(role));
      const isPenalPubPassivo =
        isPenal &&
        (has("INVESTIGADO") ||
          has("INDICIADO") ||
          /R[ÉE]U.*ACUSADO|ACUSADO/i.test(role));
      const isPenalPrivAtivo = isPenal && has("QUERELANTE");
      const isPenalPrivPassivo = isPenal && has("QUERELADO");
      if (isPenal && has("DEPOSIT"))
        return `A presente outorga visa a atuação da parte ${O}, na qualidade de fiel depositária judicial, no incidente de restituição de coisa apreendida que envolve ${pcBold}, nos termos dos arts. 118 a 124-A do Decreto-Lei n. 3.689/1941 (Código de Processo Penal).`;
      if (isPenalPrivAtivo)
        return `A presente outorga visa o oferecimento de queixa-crime (ação penal de iniciativa privada) em face de ${pcBold}, nos termos do Art. 44 do CPP.`;
      if (isPenalPrivPassivo)
        return `A presente outorga visa a defesa da parte ${O} na queixa-crime (ação penal de iniciativa privada) oferecida por ${pcBold}.`;
      if (isPenalPubAtivo)
        return `A presente outorga visa a representação dos interesses da parte ${O}, na qualidade de vítima/assistente de acusação, na persecução penal de iniciativa pública que envolve ${pcBold}.`;
      if (isPenalPubPassivo)
        return `A presente outorga visa a defesa da parte ${O} na ação penal de iniciativa pública, no procedimento que envolve ${pcBold}.`;
      if (isPenal)
        return `A presente outorga visa a atuação da parte ${O} no procedimento criminal que envolve ${pcBold}.`;
      if (isTrabalhista && has("RECLAMANTE"))
        return `A presente outorga visa a propositura de reclamação trabalhista em face de ${pcBold}.`;
      if (isTrabalhista && has("RECLAMADA"))
        return `A presente outorga visa a defesa da parte ${O} na reclamação trabalhista movida por ${pcBold}.`;
      if (isTrabalhista && has("RESPONSÁVEL"))
        return `A presente outorga visa a atuação da parte ${O}, na qualidade de responsável solidário/subsidiário, na reclamação trabalhista que envolve ${pcBold}.`;
      if (isTrabalhista)
        return `A presente outorga visa a atuação da parte ${O} na demanda trabalhista que envolve ${pcBold}.`;
      if (isPrevidenciario && has("REQUERENTE"))
        return `A presente outorga visa a propositura de ação previdenciária em face de ${pcBold}.`;
      if (isPrevidenciario)
        return `A presente outorga visa a defesa da parte ${O} na ação previdenciária movida por ${pcBold}.`;
      if (isAdministrativo && has("IMPETRANTE"))
        return `A presente outorga visa a impetração de mandado de segurança ou medida administrativa contra ${pcBold}.`;
      if (isAdministrativo)
        return `A presente outorga visa a defesa da parte ${O} no procedimento administrativo instaurado por ${pcBold}.`;
      const isTributario = selectedRamoKeys.includes("Tributario");
      if (isTributario && has("CONTRIBUINTE"))
        return `A presente outorga visa a defesa dos interesses tributários da parte ${O} em face de ${pcBold}.`;
      if (isTributario)
        return `A presente outorga visa a atuação da parte ${O} no procedimento tributário que envolve ${pcBold}.`;
      if (isFamilia && (has("AUTOR") || has("REQUERENTE")))
        return `A presente outorga visa a propositura de ação de família em face de ${pcBold}.`;
      if (isFamilia)
        return `A presente outorga visa a defesa dos interesses da parte ${O} na ação de família movida por ${pcBold}.`;
      if (isSocietario)
        return `A presente outorga visa a atuação nos interesses societários e empresariais da parte ${O} na demanda que envolve ${pcBold}.`;
      if (has("EXEQUENTE"))
        return `A presente outorga visa a promoção de execução judicial em face de ${pcBold}.`;
      if (has("EXECUTADO"))
        return `A presente outorga visa a defesa da parte ${O} na execução promovida por ${pcBold}.`;
      if (has("LITISCONSORTE") || has("TERCEIRO"))
        return `A presente outorga visa a atuação da parte ${O} como terceiro interessado na demanda que envolve ${pcBold}.`;
      if (has("FIADOR") || has("AVALISTA"))
        return `A presente outorga visa a atuação da parte ${O}, na qualidade de fiador/avalista, na demanda que envolve ${pcBold}.`;
      if (has("DEPOSIT"))
        return `A presente outorga visa a atuação da parte ${O}, na qualidade de fiel depositária judicial de bem constrito, na demanda que envolve ${pcBold}.`;
      if (has("AUTOR") || has("REQUERENTE"))
        return `A presente outorga visa a propositura de ação em face de ${pcBold}.`;
      return `A presente outorga visa a defesa dos interesses da parte ${O} na demanda movida por ${pcBold}.`;
    };

    const map = {
      "[[NOME_CLIENTE]]": partiesNamesUpper,
      "[[CPF_CLIENTE]]": firstPartyCpf,
      "[[EMAIL_CLIENTE]]": firstPartyEmail,
      "[[QUALIFICACAO_REPRESENTANTE_LEGAL]]": legalRepresentativeQualification,
      "[[OUTORGANTE_NOME]]": partiesNamesUpper,
      "[[OUTORGADO_NOME]]": outorgadoNomeExibicao.toUpperCase(),
      "[[OUTORGANTE_QUALIFICACAO]]": outorganteQualAll,
      "[[OUTORGADO_QUALIFICACAO]]": outorgadoQual,
      "[[CRM_QUALIFICACAO_COMPLETA]]": outorganteQualAll,
      "[[QUALIFICACAO_CONTRATANTE]]": outorganteQualAll,
      "[[QUALIFICACAO_CONTRATADO]]": outorgadoQual,
      "[[OBJETO_PRINCIPAL]]":
        String(formData?.objetoPrincipal || "...").trim() || "...",
      "[[BENS_IMOVEIS_ESPECIFICOS]]":
        patrimonialImobiliarioDescricao ||
        "[Bens imóveis específicos não informados]",
      "[[VALIDADE_MANDATO]]": validadeMandatoText,
      "[[VALIDADE_DATA]]":
        fmtDateBr(formData?.validadeMandatoData) || "[data não informada]",
      "[[VALIDADE_ATO]]":
        String(formData?.validadeMandatoAto || "").trim() ||
        "[ato específico não informado]",
      "[[PODERES_SETORIAIS]]": getPoderesSetoriaisHtml(),
      "[[RAMO_DIREITO]]": selectedRamoKeys
        .map((ramo) => String(RAMO_TAG_MAP[ramo] || ramo).toUpperCase())
        .join(" / "),
      "[[POSICAO_CLIENTE]]": selectedPosicoes
        .map((posicao) => String(posicao || "").toUpperCase())
        .join(" / "),
      "[[DEPOSITARIO_GENERO]]":
        outorganteGender === "F" ? "Depositária" : "Depositário",
      "[[depositario_genero]]":
        outorganteGender === "F" ? "depositária" : "depositário",
      "[[CIDADE]]": formData.city || "Curitiba",
      "[[DATA_EXTENSO]]": new Date().toLocaleDateString("pt-BR", {
        day: "numeric",
        month: "long",
        year: "numeric",
      }),
      "[[NOME_DO_QUERELADO_REU]]": formData.quereladoName || "...",
      "[[DATA_FATO]]": formData.dataFato || "...",
      "[[DESCRICAO_SUCINTA_DO_FATO]]": formData.descricaoFato || "...",
      "[[NOME_DA_PARTE_CONTRARIA]]": formData.parteContrariaName || "...",
      "[[AUTORIDADE_COATORA_NOME]]": formData.autoridadeCoatoraNome || "...",
      "[[AUTORIDADE_COATORA_ORGAO]]":
        formData.autoridadeCoatoraOrgao || "...",
      "[[ATO_COATOR_DATA]]":
        fmtDateBr(formData?.atoCoatorData) || "[data não informada]",
      "[[ATO_COATOR_DESCRICAO]]": String(formData.atoCoatorDescricao || "...")
        .trim()
        .replace(/\.+$/, ""),
      "[[CONTEXTO_PROCESSUAL]]": getContextoProcessual(),
    };

    // rawKeys: HTML pré-montado pelo próprio motor (conteúdo de cláusula do
    // template, ou textos acima já com escapeHtml aplicado nos trechos
    // digitados pelo usuário) — inserido como está, sem escapar de novo.
    const rawKeys = new Set([
      "[[PODERES_SETORIAIS]]",
      "[[VALIDADE_MANDATO]]",
      "[[CONTEXTO_PROCESSUAL]]",
    ]);
    // htmlTrustedKeys: mesma ideia, mas ainda passam pelo span de destaque.
    // Confiáveis porque toda a cadeia até aqui (resolvePartyQualification,
    // buildCanonicalClientQualification/strongUpper, contractedQualificationText)
    // já escapa o texto livre do usuário antes de montar o <strong>.
    const htmlTrustedKeys = new Set([
      "[[OUTORGANTE_QUALIFICACAO]]",
      "[[OUTORGADO_QUALIFICACAO]]",
      "[[CRM_QUALIFICACAO_COMPLETA]]",
      "[[QUALIFICACAO_CONTRATANTE]]",
      "[[QUALIFICACAO_CONTRATADO]]",
      "[[QUALIFICACAO_REPRESENTANTE_LEGAL]]",
    ]);
    Object.keys(map).forEach((k) => {
      const v = map[k];
      if (rawKeys.has(k)) {
        out = out.split(k).join(String(v));
        return;
      }
      // Todo campo fora das duas listas acima é considerado texto puro
      // (nome, CPF, e-mail, objeto, cidade, datas etc.) e é sempre escapado
      // antes de virar HTML — fecha o vetor de injeção via campo livre.
      const safeValue = htmlTrustedKeys.has(k)
        ? String(v)
        : escapeHtml(String(v));
      out = out
        .split(k)
        .join(`<span class="variable-highlight">${safeValue}</span>`);
    });
    out = out.replace(
      /nomeia a parte <strong>OUTORGADA<\/strong> como sua bastante procuradora, conferindo-lhe poderes específicos para a representação técnica e defesa de seus interesses atrelados ao seguinte escopo:/g,
      nomeacaoOutorgadaTexto,
    );
    return repairMojibakeText(out);
  };

  const handlePublishProcuration = (
    publishType = "simple",
    format = "html",
    cadernoId = "",
    cadernoTitle = "",
  ) => {
    const publicationParties = (Array.isArray(parties) ? parties : []).filter(
      (party) => {
        const linkedId = String(
          party?.clientId || party?.qualificationData?.id || "",
        ).trim();
        const displayName = String(
          party?.name ||
            party?.qualificationData?.name ||
            party?.qualificationData?.qualification?.physical?.name ||
            party?.qualificationData?.qualification?.legal?.companyName ||
            party?.qualificationData?.qualification?.animal?.name ||
            "",
        ).trim();
        return linkedId || displayName;
      },
    );

    if (publicationParties.length === 0) {
      setDialogState({
        open: true,
        title: "Publicação indisponível",
        message:
          "Importe ao menos um outorgante do CRM antes de publicar a procuração no sistema do cliente.",
        mode: "info",
        onConfirm: null,
      });
      return;
    }

    const primaryClientId = String(
      publicationParties?.[0]?.clientId ||
        publicationParties?.[0]?.qualificationData?.id ||
        "",
    ).trim();
    if (!primaryClientId) {
      setDialogState({
        open: true,
        title: "Vínculo obrigatório",
        message:
          "A publicação exige que a parte outorgante principal esteja vinculada ao CRM por `clientId`, para que o documento apareça corretamente no Portal do Cliente e no prontuário.",
        mode: "info",
        onConfirm: null,
      });
      return;
    }

    const safePublishType = "simple";
    const safeFormat =
      String(format || "html")
        .trim()
        .toLowerCase() === "pdf"
        ? "pdf"
        : "html";
    const requiresConsent = false;
    const documentStatus = "PUBLISHED";
    const consentStatus = "not_required";
    const eventType = "DOCUMENT_PUBLISHED";
    const dialogTitle =
      safeFormat === "pdf"
        ? "Publicar PDF da Procuração"
        : "Salvar Procuração (HTML)";
    const dialogMessage =
      safeFormat === "pdf"
        ? "Esta ação irá publicar a procuração em PDF no Google Drive do cliente, criar o slot no caderno de autos e registrar o evento no prontuário. Deseja continuar?"
        : "Esta ação irá salvar a procuração em HTML no prontuário interno para edição futura. O documento NÃO será enviado ao portal do cliente. Deseja continuar?";

    setDialogState({
      open: true,
      title: dialogTitle,
      message: dialogMessage,
      mode: "confirm",
      onConfirm: async () => {
        const _operatorName =
          String(
            auth?.currentUser?.displayName ||
              auth?.currentUser?.email ||
              "Operador",
          ).trim() || "Operador";
        // ── FRESH MASTER FETCH: Garantir que o template mais recente está no DOM ──
        // Busca o master da nuvem e aplica ao templateData ANTES de capturar
        // o DOM para publicação. Antes, esta função só atualizava refs de
        // versão sem tocar templateData — o log dizia "synced" mas o
        // snapshot publicado podia sair com conteúdo desatualizado se a
        // aba estivesse aberta desde antes de uma correção no master
        // (Art. 357/§10.1). Mesmo padrão já usado em ContractEngine.jsx.
        try {
          if (appId) {
            const _freshMasterRef = await fetchProcurationMasterV1();
            if (_freshMasterRef) {
              const _freshStructure = Array.isArray(_freshMasterRef?.structure)
                ? _freshMasterRef.structure
                : null;
              if (_freshStructure && _freshStructure.length > 0) {
                masterStructureRef.current = _freshStructure;
                masterVersionRef.current =
                  _freshMasterRef?.updatedAt?.toMillis?.() ||
                  _freshMasterRef?.updatedAt?.seconds * 1000 ||
                  Date.now();
                if (!isModified) {
                  setTemplateData(buildMasterTemplatePayload(_freshStructure));
                  console.log(
                    "[ProcurationEngine] Fresh master aplicado ao DOM antes da captura (sem edições locais pendentes).",
                  );
                } else {
                  console.warn(
                    "[ProcurationEngine] Há edições locais não salvas — master fresco NÃO aplicado para não sobrescrevê-las. Publicando com o conteúdo atual da tela.",
                  );
                }
                await new Promise((resolve) => {
                  requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                      setTimeout(resolve, 800);
                    });
                  });
                });
              }
            }
          }
        } catch (_freshErr) {
          console.warn(
            "[ProcurationEngine] Fresh master fetch falhou (non-blocking, usando DOM atual):",
            _freshErr?.message,
          );
        }

        const resolveSignerClientId = (clientLike) => {
          const q = clientLike?.qualification || {};
          const type = String(clientLike?.type || "").trim();
          if (type === "Pessoa Jurídica")
            return String(q?.legal?.representative?.id || "").trim();
          if (type === "Animal Não-Humano")
            return String(
              q?.animal?.responsible?.id || q?.animal?.guardian?.id || "",
            ).trim();
          if (type === "Menor / Incapaz")
            return String(q?.minor?.representative?.id || "").trim();
          if (type === "Espólio")
            return String(
              q?.estate?.inventorier?.id ||
                q?.estate?.representative?.id ||
                q?.estate?.inventor?.id ||
                "",
            ).trim();
          return String(clientLike?.id || "").trim();
        };

        const resolveSignerRole = (clientLike) => {
          const type = String(clientLike?.type || "").trim();
          if (type === "Pessoa Jurídica") return "representante legal";
          if (type === "Animal Não-Humano") return "responsável legal";
          if (type === "Menor / Incapaz") return "representante legal";
          if (type === "Espólio") return "inventariante";
          return "outorgante";
        };

        const escapeHtml = (value) =>
          String(value || "")
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/"/g, "&quot;")
            .replace(/'/g, "&#39;");

        const normalizeAbsoluteUrl = (raw) => {
          const value = String(raw || "").trim();
          if (!value) return "";
          try {
            return new URL(value, window.location.origin).href;
          } catch {
            return value;
          }
        };

        const buildPublicationHtml = ({ title, signers }) => {
          const paperSource = document.querySelector(".contract-print-area");
          const paperClone = paperSource ? paperSource.cloneNode(true) : null;

          if (
            paperClone &&
            !paperClone.querySelector("#contrato-legal-pronto")
          ) {
            const readyAnchor = document.createElement("div");
            readyAnchor.id = "contrato-legal-pronto";
            readyAnchor.setAttribute("data-status", "ready");
            readyAnchor.style.display = "none";
            paperClone.appendChild(readyAnchor);
          }

          if (paperClone) {
            paperClone.querySelectorAll("[src]").forEach((node) => {
              try {
                const current = node.getAttribute("src");
                if (current)
                  node.setAttribute("src", normalizeAbsoluteUrl(current));
              } catch {}
            });

            paperClone.querySelectorAll("[href]").forEach((node) => {
              try {
                const current = node.getAttribute("href");
                if (current && !String(current).startsWith("#"))
                  node.setAttribute("href", normalizeAbsoluteUrl(current));
              } catch {}
            });
          }

          const styleTags = Array.from(document.querySelectorAll("style"))
            .map((node) => `<style>${String(node?.textContent || "")}</style>`)
            .join("\n");

          const linkTags = Array.from(
            document.querySelectorAll('link[rel="stylesheet"]'),
          )
            .map((node) => {
              const href = normalizeAbsoluteUrl(node?.getAttribute("href"));
              return href
                ? `<link rel="stylesheet" href="${escapeHtml(href)}" />`
                : "";
            })
            .filter(Boolean)
            .join("\n");

          const bodyHtml =
            paperClone?.outerHTML ||
            `<div class="published-paper-fallback"><h1>${escapeHtml(title)}</h1><div>${sanitizeScreenHtml(parseText("<p>Snapshot visual indisponível no momento da publicação.</p>"))}</div></div>`;

          return `<!doctype html>
<html lang="pt-BR">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>${escapeHtml(title)}</title>
  ${linkTags}
  ${styleTags}
  <link rel="preconnect" href="https://fonts.googleapis.com">
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
  <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Roboto:wght@300;400;500;700&display=swap">
  <style>
    html, body { background: #f3f4f6; margin: 0; padding: 0; }
    body { font-family: 'Roboto', sans-serif; -webkit-font-smoothing: antialiased; color: #20243B; }
    .published-shell { max-width: 1120px; margin: 0 auto; padding: 24px; }
    .publication-banner { margin-bottom: 16px; border: 1px solid #dbe2ea; background: #fff; border-radius: 16px; padding: 16px 18px; }
    .publication-banner h1 { margin: 0 0 8px 0; font-size: 18px; font-weight: 800; }
    .publication-banner p { margin: 4px 0; font-size: 12px; line-height: 1.6; }
    .publication-banner ul { margin: 8px 0 0 20px; padding: 0; font-size: 12px; }
    .screen-paper { margin: 0 auto; }
    .no-print { display: none !important; }
    .quadro-resumo-icon { display: none !important; }
    .ghost-marker { display: none !important; }
    @media print {
      html, body { background: white !important; }
      .published-shell { max-width: none !important; margin: 0 !important; padding: 0 !important; }
      .publication-banner { display: none !important; }
      .contract-print-area, .print-only-view, .print-layout-table, .document-content { margin-top: 0 !important; }
      #guia-rapido-print, #guia-rapido { margin-top: 0 !important; }
    }
  </style>
</head>
<body>
  <div class="published-shell">
    <div class="publication-banner">
      <h1>${escapeHtml(title)}</h1>
      <p>Documento publicado no sistema interno do Portal do Cliente em ${escapeHtml(new Date().toLocaleString("pt-BR"))} por ${escapeHtml(_operatorName)}.</p>
    </div>
    ${bodyHtml}
  </div>
</body>
</html>`;
        };

        const actorName = _operatorName;
        const principalParties = publicationParties.map((party) => {
          const clientData = party?.qualificationData || party || {};
          const signature = buildSignatureLine(clientData);
          return {
            clientId: String(party?.clientId || clientData?.id || "").trim(),
            signerClientId: resolveSignerClientId(clientData),
            type: String(clientData?.type || "").trim(),
            principalName: String(party?.name || clientData?.name || "").trim(),
            signerName: String(signature?.nameLine || "").trim(),
            signerCpf: String(signature?.cpfLine || "").trim(),
            signerRole: resolveSignerRole(clientData),
            subtitle: String(signature?.subtitle || "").trim(),
            email: String(
              party?.email || clientData?.contact?.email || "",
            ).trim(),
          };
        });

        const relatedClientIds = Array.from(
          new Set(
            principalParties
              .flatMap((item) => [
                String(item?.clientId || "").trim(),
                String(item?.signerClientId || "").trim(),
              ])
              .filter(Boolean),
          ),
        );
        const primaryParty = principalParties[0] || {};
        const primaryClientData =
          publicationParties[0]?.qualificationData ||
          publicationParties[0] ||
          {};
        const targetFolderId = readText(
          primaryClientData?.driveFolders?.processos ||
            primaryClientData?.drive?.processosFolderId ||
            primaryClientData?.driveFolders?.contratos ||
            primaryClientData?.drive?.contratosFolderId ||
            primaryClientData?.driveFolderId ||
            primaryClientData?.drive?.rootFolderId,
        );
        const title = buildFriendlyProcurationName();
        const allPrincipalNames = principalParties
          .map((item) => String(item?.principalName || "").trim())
          .filter(Boolean);
        const allRepresentativeNames = principalParties
          .filter((item) =>
            [
              "responsável legal",
              "representante legal",
              "inventariante",
            ].includes(String(item?.signerRole || "").trim()),
          )
          .map((item) => String(item?.signerName || "").trim())
          .filter(Boolean);
        const currentVersion = await (async () => {
          const localCurrentVersion = Math.max(
            0,
            Number(
              editingPublicationMeta?.publicationVersion ||
                editingPublicationMeta?.version ||
                0,
            ) || 0,
          );
          let cloudCurrentVersion = 0;
          try {
            const seedId = String(
              editingPublicationMeta?.documentId || "",
            ).trim();
            const seedSeriesId = String(
              editingPublicationMeta?.publicationSeriesId || "",
            ).trim();
            if (seedId || seedSeriesId) {
              const loaded = await readLatestPublishedDocumentInSeries({
                documentId: seedId || seedSeriesId,
                publicationSeriesId: seedSeriesId,
              });
              cloudCurrentVersion = Math.max(
                0,
                Number(loaded?.data?.publicationVersion || 0) || 0,
              );
            }
          } catch (e) {
            void e;
          }
          return Math.max(localCurrentVersion, cloudCurrentVersion);
        })();
        const newVersion = currentVersion + 1;
        const fileBase = generatePublishedArtifactFileStem(
          "Procuracao",
          publicationParties[0] || {},
          {
            mainPartyName: String(primaryParty?.principalName || "").trim(),
            mainPartyNames: allPrincipalNames,
          },
          newVersion,
        );
        const publicationHtml = buildPublicationHtml({
          title,
          signers: principalParties.map((item) => ({
            name: item?.signerName,
            role: item?.signerRole,
          })),
        });
        const editableContentData = {
          ...buildCloudAutosavePayload(),
          engine: "procuration_engine",
          documentType: "PROCURATION",
          snapshotFormat: "dual-save-v1",
        };

        setIsPublishing(true);
        try {
          if (!appId) throw new Error("ProjectId do Firebase não encontrado.");

          const existingDocId = String(
            editingPublicationMeta?.documentId || "",
          ).trim();
          const existingDocIdForConsent = requiresConsent ? existingDocId : "";
          const existingDocIdForHtml =
            safeFormat === "html" ? existingDocId : "";
          const _useExistingId =
            existingDocIdForConsent || existingDocIdForHtml;
          // Idempotência: PDF+caderno usa ID determinístico proc_{cadernoId}
          // para evitar duplicação na collection documents a cada republicação.
          const _deterministicId = cadernoId ? `proc_${cadernoId}` : "";
          const documentRef = _useExistingId
            ? doc(documentsCol, _useExistingId)
            : _deterministicId
              ? doc(documentsCol, _deterministicId)
              : doc(documentsCol);
          const publicationSeriesId =
            String(
              editingPublicationMeta?.publicationSeriesId || documentRef.id,
            ).trim() || documentRef.id;
          const previousPublicationId = _useExistingId
            ? null
            : existingDocId || null;
          const htmlFileName = `${fileBase}.html`;
          const pdfFileName = `${fileBase}.pdf`;
          let fileName = htmlFileName;
          let fileUrl = "";
          let downloadUrl = "";
          let storagePath = "";
          let mimeType = "text/html";
          let extension = "html";
          let storageMode = "html_archive";
          let storageWarning = "";
          let storageEngine = {
            provider: "firestore",
            access: "internal",
            mode: "html_archive",
          };

          // Art. 321: Procuração NÃO vai para Drive na publicação.
          // Fica apenas em Firebase Storage (SSOT). Drive só após assinatura.
          if (safeFormat === "pdf") {
            // ══ BARREIRA MFA/AUTH: Validar sessão antes do fetch ══
            const _pdfAuthUser = auth.currentUser;
            if (!_pdfAuthUser) {
              throw new Error(
                "Sessão não iniciada. Faça login novamente antes de gerar o PDF.",
              );
            }
            try {
              await _pdfAuthUser.getIdToken(true);
            } catch (_mfaErr) {
              console.error(
                "[RDA PDF Auth] Token inválido ou MFA pendente:",
                _mfaErr,
              );
              throw new Error(
                "Sessão expirada ou 2FA pendente. Faça logout e login novamente antes de gerar o PDF.",
              );
            }

            // ── Passo 1: Gerar PDF (mesmo padrão do ContractEngine — retorna blob) ──
            let pdfBlob = null;
            try {
              const genRes = await fetchWithWarmup(
                `${PUBLISHING_API_BASE_URL}/drive/generate-pdf?t=${Date.now()}`,
                {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({
                    html: publicationHtml,
                    fileName: pdfFileName,
                    parentId: null,
                    makePublic: false,
                  }),
                },
                {
                  onWaking: () =>
                    console.log(
                      "⏳ Backend acordando... aguarde até 40s na primeira vez.",
                    ),
                  onRetry: (attempt, max) =>
                    console.log(
                      `🔄 Tentativa ${attempt + 1}/${max}... aguarde.`,
                    ),
                },
              );
              if (!genRes?.ok) {
                const errText = await genRes.text().catch(() => "");
                throw new Error(
                  errText ||
                    genRes?.statusText ||
                    "Falha ao gerar o PDF da procuração.",
                );
              }
              pdfBlob = await genRes.blob();
            } catch (uploadError) {
              const msg = String(uploadError?.message || uploadError || "");
              const isNetworkError =
                msg.includes("Failed to fetch") ||
                msg.includes("NetworkError") ||
                msg.includes("CORS") ||
                msg.includes("ERR_FAILED");
              if (isNetworkError) {
                throw new Error(
                  "O servidor de geração de PDF (backend) está fora do ar ou inacessível. Verifique se o serviço no Render está ativo em https://dashboard.render.com e tente novamente em alguns minutos.",
                );
              }
              throw new Error(msg || "Falha ao gerar o PDF da procuração.");
            }
            if (!pdfBlob || !pdfBlob.size) {
              throw new Error(
                "O motor Python retornou um PDF vazio para a procuração.",
              );
            }

            // ── Passo 2: Upload para Firebase Storage (SSOT — igual ContractEngine) ──
            const _primaryClientIdForStorage =
              String(principalParties[0]?.clientId || "").trim() ||
              "unlinked-client";
            const _sRef = storageRef(
              storage,
              `artifacts/${appId}/public/documents/procurations/${_primaryClientIdForStorage}/${pdfFileName}`,
            );
            await uploadBytes(_sRef, pdfBlob, {
              contentType: "application/pdf",
            });
            const storageDownloadUrl = await getDownloadURL(_sRef);

            // Art. 321/322: Procuração NÃO é enviada ao Drive na publicação.
            // O PDF fica APENAS no Firebase Storage (SSOT).
            // A cópia para Drive só acontece APÓS o cliente assinar e re-enviar
            // (fluxo handleUploadSigned no PortalCaso.jsx).

            fileName = pdfFileName;
            fileUrl = storageDownloadUrl;
            downloadUrl = storageDownloadUrl;
            storagePath = _sRef.fullPath;
            mimeType = "application/pdf";
            extension = "pdf";
            storageMode = "pdf_archive";
            storageEngine = {
              provider: "firebase_storage",
              access: "authenticated_url",
              mode: "pdf_archive",
              storagePath: _sRef.fullPath,
              driveFileId: null,
              webViewLink: null,
            };
          }

          // Display name com acento (para exibição no portal)
          const displayName =
            `Procuração - ${allPrincipalNames[0] || "Cliente"}`.trim();

          const payload = {
            id: documentRef.id,
            type: "PROCURATION",
            status: documentStatus,
            module: "procurations",
            title: displayName,
            name: displayName,
            fileName,
            fileUrl,
            downloadUrl,
            storagePath,
            storageEngine,
            mimeType,
            extension,
            category: "Procuração",
            classification: { category: "Procuração" },
            clientVisible: safeFormat === "pdf",
            htmlContent: publicationHtml,
            contentData: editableContentData,
            publicationVersion: newVersion,
            publicationSeriesId,
            previousPublicationId,
            clientId: String(primaryParty?.clientId || "").trim(),
            relatedEmail: String(primaryParty?.email || "").trim() || null,
            relatedEntities: relatedClientIds,
            parties: principalParties,
            signers: principalParties.map((item) => ({
              clientId: String(
                item?.signerClientId || item?.clientId || "",
              ).trim(),
              name: String(item?.signerName || "").trim(),
              cpf: String(item?.signerCpf || "").trim(),
              role: String(item?.signerRole || "").trim(),
              subtitle: String(item?.subtitle || "").trim(),
            })),
            context: {
              module: "procurations",
              selectedTemplateId: String(selectedTemplateId || "").trim(),
              templateTitle: String(templateData?.title || "").trim(),
              ramoDireito: selectedRamoKeys.join(", ").trim(),
              ramosDireito: selectedRamoKeys,
              posicaoCliente: selectedPosicoes.join(", ").trim(),
              posicoesCliente: selectedPosicoes,
              objetoPrincipal: String(formData?.objetoPrincipal || "").trim(),
              parteContrariaName: String(
                formData?.parteContrariaName || "",
              ).trim(),
              relatedEntities: relatedClientIds,
              documentKind: "PROCURATION_PUBLICATION",
            },
            requiresConsent,
            consentStatus,
            publication: {
              source: "procuration_engine",
              publishType: safePublishType,
              format: safeFormat,
              version: newVersion,
              publishedAtIso: new Date().toISOString(),
              publishedBy: actorName,
              storageMode,
              storageWarning,
            },
            cadernoId: cadernoId || null,
            cadernoTitle: cadernoTitle || null,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            createdBy: auth?.currentUser?.email || null,
            updatedBy: auth?.currentUser?.email || null,
          };

          if (existingDocIdForConsent) {
            await updateProcurationDocument(documentRef, {
              requiresConsent: true,
              consentStatus: "pending_consent",
              status: "pending_consent",
              publishType: "consent",
              htmlContent: publicationHtml,
              contentData: editableContentData,
              publicationVersion: newVersion,
              consentRequestedAt: serverTimestamp(),
              consentRequestedBy: actorName,
              updatedAt: serverTimestamp(),
              updatedBy: auth?.currentUser?.email || null,
              "publication.publishType": "consent",
              "publication.version": newVersion,
            });
          } else {
            await publishProcurationDocument(documentRef, payload);
          }

          await Promise.all(
            relatedClientIds.map((clientId) => {
              const signerText = principalParties
                .filter(
                  (item) =>
                    String(item?.clientId || "").trim() ===
                      String(clientId || "").trim() ||
                    String(item?.signerClientId || "").trim() ===
                      String(clientId || "").trim(),
                )
                .map((item) => {
                  const rolePart = String(item?.signerRole || "").trim()
                    ? ` (${item.signerRole})`
                    : "";
                  return `${String(item?.signerName || "Signatário não informado").trim()}${rolePart}`;
                })
                .filter(Boolean)
                .join(", ");

              const noteHtml = requiresConsent
                ? `<p><strong>Consentimento solicitado:</strong> ${escapeHtml(title)}</p><p>Signatário(s): ${escapeHtml(signerText || "Não informado")}</p><p>Status: Consentimento pendente.</p>`
                : `<p><strong>Procuração publicada:</strong> ${escapeHtml(title)}</p><p>Signatário(s): ${escapeHtml(signerText || "Não informado")}</p><p>Formato: ${escapeHtml(safeFormat.toUpperCase())}</p>`;

              return addProcurationProntuarioNote(clientId, {
                category: "SYSTEM",
                type: eventType,
                payload: {
                  text: requiresConsent
                    ? `Consentimento solicitado para procuração: ${title}`
                    : `Procuração publicada: ${title}`,
                  html: noteHtml,
                  documentId: documentRef.id,
                  title,
                  fileName,
                  fileUrl,
                  publicationVersion: newVersion,
                  publicationSeriesId,
                  downloadUrl,
                  storagePath,
                  workspaceLabel: "Procuração",
                  module: "procurations",
                  requiresConsent,
                  consentStatus,
                  publishType: safePublishType,
                  format: safeFormat,
                  storageMode,
                },
                actor: { name: actorName, isBot: false },
                createdAt: serverTimestamp(),
              });
            }),
          );

          setEditingPublicationMeta({
            documentId: documentRef.id,
            publicationSeriesId,
            publicationVersion: newVersion,
            cadernoId: cadernoId || null,
            cadernoTitle: cadernoTitle || null,
          });

          // Art. 321: Procuração NÃO cria slot no organizer (Documentos e Provas).
          // Ela aparece APENAS no container fixo do topo (Firestore documents/ com type=PROCURATION).
          // O slot no organizer era criado erroneamente e causava duplicação na view do cliente.

          setDialogState({
            open: true,
            title:
              safeFormat === "pdf"
                ? "PDF da procuração publicado"
                : "Procuração salva (HTML)",
            message:
              safeFormat === "pdf"
                ? "✅ SUCESSO!\n\nA procuração foi publicada em PDF no Firebase Storage, disponível no container fixo do cliente. Uma cópia também foi enviada ao Drive (slot processual) e o evento correspondente já foi registrado no prontuário."
                : "✅ SUCESSO!\n\nA procuração foi salva em HTML no prontuário interno. O documento está disponível para edição futura, mas NÃO foi enviado ao portal do cliente.",
            mode: "info",
            onConfirm: null,
          });
        } catch (e) {
          setDialogState({
            open: true,
            title: "Erro ao publicar procuração",
            message: "Erro: " + (e?.message || String(e)),
            mode: "info",
            onConfirm: null,
          });
        } finally {
          setIsPublishing(false);
        }
      },
    });
  };

  const handleDeletePublication = () => {
    const pubId = String(editingPublicationMeta?.documentId || "").trim();
    if (!pubId) {
      setDialogState({
        open: true,
        title: "Nenhuma publicação ativa",
        message: "Não há publicação vinculada a esta sessão para excluir.",
        mode: "info",
        onConfirm: null,
      });
      return;
    }
    setDialogState({
      open: true,
      title: "Excluir publicação do acervo",
      message: `ATENÇÃO: Esta ação irá EXCLUIR PERMANENTEMENTE a publicação "${pubId}" do Firestore e remover as notas associadas do prontuário do cliente.\n\nEsta ação NÃO pode ser desfeita.\n\nDeseja continuar?`,
      mode: "confirm",
      onConfirm: async () => {
        try {
          if (!appId) throw new Error("ProjectId não encontrado.");
          const docData = await fetchProcurationDocumentMeta(pubId);
          if (!docData)
            throw new Error(
              "Documento não encontrado no Firestore. Pode já ter sido excluído.",
            );
          const clientIds = Array.isArray(docData?.relatedEntities)
            ? docData.relatedEntities
                .map((e) => (typeof e === "string" ? e : String(e?.id || "")))
                .filter(Boolean)
            : docData?.clientId
              ? [String(docData.clientId)]
              : [];
          let notesDeleted = 0;
          for (const clientId of clientIds) {
            try {
              notesDeleted += await deleteProcurationRelatedNotes(
                clientId,
                pubId,
              );
            } catch {
              /* ignore notes cleanup errors */
            }
          }
          await deleteProcurationDocument(documentRef);
          setEditingPublicationMeta(null);
          publishedEditLockRef.current = false;
          setDialogState({
            open: true,
            title: "Publicação excluída",
            message: `Documento "${pubId}" excluído do Firestore com sucesso.\n${notesDeleted} nota(s) de prontuário também removida(s).\n\nA sessão de edição foi desvinculada.`,
            mode: "info",
            onConfirm: null,
          });
        } catch (err) {
          setDialogState({
            open: true,
            title: "Erro ao excluir publicação",
            message: "Erro: " + (err?.message || String(err)),
            mode: "info",
            onConfirm: null,
          });
        }
      },
    });
  };

  const isPreambuloBlock = (block) => {
    return (
      block?.id === "preambulo" ||
      String(block?.title || "")
        .toUpperCase()
        .includes("PREÂMBULO")
    );
  };

  const getAnchorIdForBlock = (block) => {
    if (!block?.id) return null;
    if (isPreambuloBlock(block)) return null;
    if (block.id === "assinaturas") return null;
    return `proc-block-${block.id}`;
  };

  const getQuickNavBlocks = () => {
    const blocks = templateData?.structure || [];
    return blocks.filter(
      (b) =>
        b &&
        !isPreambuloBlock(b) &&
        b.id !== "assinaturas" &&
        isBlockVisible(b),
    );
  };

  const patrimonialImobiliarioDescricao = useMemo(() => {
    const partes = [
      String(formData?.bensImoveisEspecificos || "").trim(),
      String(formData?.bemImovelMatricula || "").trim()
        ? `Matrícula: ${String(formData?.bemImovelMatricula || "").trim()}`
        : "",
      String(formData?.bemImovelCartorio || "").trim()
        ? `Cartório: ${String(formData?.bemImovelCartorio || "").trim()}`
        : "",
      String(formData?.bemImovelEndereco || "").trim()
        ? `Endereço: ${String(formData?.bemImovelEndereco || "").trim()}`
        : "",
      String(formData?.bemImovelNaturezaDireito || "").trim()
        ? `Natureza do direito: ${String(formData?.bemImovelNaturezaDireito || "").trim()}`
        : "",
      String(formData?.bemImovelObservacoes || "").trim()
        ? `Observações: ${String(formData?.bemImovelObservacoes || "").trim()}`
        : "",
    ].filter(Boolean);

    return partes.join("; ").trim();
  }, [
    formData?.bensImoveisEspecificos,
    formData?.bemImovelMatricula,
    formData?.bemImovelCartorio,
    formData?.bemImovelEndereco,
    formData?.bemImovelNaturezaDireito,
    formData?.bemImovelObservacoes,
  ]);

  const doesBlockConditionPass = (block) => {
    if (!block) return false;

    if (block.minParties && parties.length < Number(block.minParties || 0))
      return false;

    const minValue = Number(block?.minValue || 0);
    const objectValue = Number(formData?.objectValue || 0);
    if (minValue > 0 && objectValue < minValue) return false;

    const ramoKeys = selectedRamoKeys;
    const ramoTags = ramoKeys
      .map((ramoKey) => RAMO_TAG_MAP[ramoKey] || ramoKey)
      .filter(Boolean);
    const tags = Array.isArray(block?.tags) ? block.tags : null;
    if (tags && tags.length > 0 && ramoTags.length > 0) {
      const normalized = tags.map((t) => String(t || "").trim());
      if (
        !normalized.includes("Universal") &&
        !ramoTags.some(
          (ramoTag) => ramoTag === "ALL" || normalized.includes(ramoTag),
        )
      )
        return false;
    }

    const libCond = POWERS_CONDITION_MAP.get(String(block?.id || ""));
    const rawCond = libCond !== undefined ? libCond : block?.condition || "";
    const cond = String(rawCond).trim().toUpperCase();
    if (!cond || cond === "ALWAYS") return true;

    const roleValues = (
      selectedPosicoes.length > 0
        ? selectedPosicoes
        : [formData?.posicaoCliente]
    )
      .map((role) =>
        String(role || "")
          .trim()
          .toUpperCase(),
      )
      .filter(Boolean);
    const role = roleValues[0] || "";
    const roleTokens = roleValues.flatMap((roleValue) =>
      roleValue
        .split("/")
        .map((t) => String(t || "").trim())
        .filter(Boolean),
    );
    const hasRamo = (ramo) => ramoKeys.includes(ramo);

    const POLO_ATIVO = [
      "AUTOR",
      "REQUERENTE",
      "RECLAMANTE",
      "VÍTIMA / ASSISTENTE DE ACUSAÇÃO",
      "QUERELANTE",
    ];
    const POLO_PASSIVO = [
      "RÉU",
      "REU",
      "REQUERIDO",
      "RECLAMADA",
      "INVESTIGADO",
      "INDICIADO",
      "RÉU / ACUSADO",
      "REU / ACUSADO",
      "QUERELADO",
    ];
    const isPenalPublicaAtivo = roleValues.some((roleValue) =>
      /ASSISTENTE|V[ÍI]TIMA|OFENDIDO/i.test(roleValue),
    );
    const isPenalPublicaPassivo = roleValues.some((roleValue) =>
      /INVESTIGADO|INDICIADO|R[ÉE]U|ACUSADO/i.test(roleValue),
    );
    const isPenalPrivadaAtivo = roleValues.some((roleValue) =>
      /QUERELANTE/i.test(roleValue),
    );
    const isPenalPrivadaPassivo = roleValues.some((roleValue) =>
      /QUERELADO/i.test(roleValue),
    );
    const isMandadoSegurancaImpetrante = roleValues.some((roleValue) =>
      /IMPETRANTE/i.test(roleValue),
    );
    const isMandadoSegurancaImpetrado = roleValues.some((roleValue) =>
      /IMPETRADO|COATORA/i.test(roleValue),
    );
    const isFielDepositario = roleValues.some((roleValue) =>
      /FIEL DEPOSIT[ÁA]RI/i.test(roleValue),
    );

    if (cond === "AUTOR_ONLY") {
      if (POLO_ATIVO.includes(role)) return true;
      return roleTokens.some((t) => POLO_ATIVO.includes(t));
    }

    if (cond === "REU_ONLY") {
      if (POLO_PASSIVO.includes(role)) return true;
      return roleTokens.some((t) => POLO_PASSIVO.includes(t));
    }

    if (cond === "PATRIMONIAL_IMOBILIARIO_ONLY") {
      return Boolean(
        formData?.patrimonialImobiliarioEnabled &&
        patrimonialImobiliarioDescricao,
      );
    }

    if (cond === "CRIPTO_ONLY") {
      return Boolean(formData?.envolveCriptoEnabled);
    }

    if (cond === "INVESTIGACAO_DEFENSIVA_ONLY") {
      return Boolean(formData?.investigacaoDefensivaEnabled);
    }

    if (cond === "ARBITRAGEM_ONLY") {
      return Boolean(formData?.arbitragemEnabled);
    }

    if (cond === "COMPLIANCE_INTERNO_ONLY") {
      return Boolean(formData?.complianceInternoEnabled);
    }

    if (cond === "LEGAL_OUTORGANTE_ONLY") {
      return Boolean(hasAnyLegalOutorgante);
    }

    if (cond === "RAMO_CIVIL_CONSUMIDOR") {
      return hasRamo("Civil");
    }

    if (cond === "RAMO_TRABALHISTA") {
      return hasRamo("Trabalho");
    }

    if (cond === "RAMO_TRABALHISTA_PREVIDENCIARIO") {
      return hasRamo("Trabalho") || hasRamo("Previdenciario");
    }

    if (cond === "RAMO_PREVIDENCIARIO") {
      return hasRamo("Previdenciario");
    }

    if (cond === "RAMO_PENAL_PUBLICA_PASSIVO") {
      return hasRamo("Penal") && isPenalPublicaPassivo;
    }

    if (cond === "RAMO_PENAL_PUBLICA_ATIVO") {
      return hasRamo("Penal") && isPenalPublicaAtivo;
    }

    if (cond === "RAMO_PENAL_PRIVADA_PASSIVO") {
      return hasRamo("Penal") && isPenalPrivadaPassivo;
    }

    if (cond === "RAMO_PENAL_PRIVADA_ATIVO") {
      return hasRamo("Penal") && isPenalPrivadaAtivo;
    }

    if (cond === "RAMO_CIVIL_FIEL_DEPOSITARIO") {
      return hasRamo("Civil") && isFielDepositario;
    }

    if (cond === "RAMO_PENAL_FIEL_DEPOSITARIO") {
      return hasRamo("Penal") && isFielDepositario;
    }

    if (cond === "RAMO_SOCIETARIO_EMPRESARIAL") {
      return hasRamo("SocietarioEmpresarial");
    }

    if (cond === "RAMO_ADMINISTRATIVO") {
      return hasRamo("Administrativo");
    }

    if (cond === "RAMO_TRIBUTARIO_ADMINISTRATIVO") {
      return hasRamo("Administrativo") || hasRamo("Tributario");
    }

    if (cond === "RAMO_TRIBUTARIO") {
      return hasRamo("Tributario");
    }

    if (cond === "RAMO_FAMILIA") {
      return hasRamo("Familia");
    }

    if (cond === "RAMO_ANIMAL") {
      return hasRamo("Animal");
    }

    if (cond === "RAMO_MANDADO_SEGURANCA_IMPETRANTE") {
      return hasRamo("MandadoSeguranca") && isMandadoSegurancaImpetrante;
    }

    if (cond === "RAMO_MANDADO_SEGURANCA_IMPETRADO") {
      return hasRamo("MandadoSeguranca") && isMandadoSegurancaImpetrado;
    }

    if (cond === "HAS_PARTE_CONTRARIA") {
      const pc = String(formData?.parteContrariaName || "").trim();
      const coatora = String(formData?.autoridadeCoatoraNome || "").trim();
      return (
        (pc.length > 0 && pc !== "...") ||
        (coatora.length > 0 && coatora !== "...")
      );
    }

    if (cond === "VALIDADE_INDETERMINADO") {
      return (
        String(
          formData?.validadeMandatoTipo || "INDETERMINADO",
        ).toUpperCase() === "INDETERMINADO"
      );
    }

    if (cond === "VALIDADE_DETERMINADO") {
      return (
        String(
          formData?.validadeMandatoTipo || "INDETERMINADO",
        ).toUpperCase() === "DETERMINADO"
      );
    }

    if (cond === "VALIDADE_ATO_ESPECIFICO") {
      return (
        String(
          formData?.validadeMandatoTipo || "INDETERMINADO",
        ).toUpperCase() === "ATO_ESPECIFICO"
      );
    }

    return true;
  };

  function isChildVisibleInHierarchy(children, childIndex) {
    const arr = Array.isArray(children) ? children : [];
    if (childIndex < 0 || childIndex >= arr.length) return false;

    const current = arr[childIndex] || {};
    if (!doesBlockConditionPass(current)) return false;

    let currentLevel = normalizeClauseLevel(current?.level);
    if (currentLevel <= 0) return true;

    for (let idx = childIndex - 1; idx >= 0; idx -= 1) {
      const candidate = arr[idx] || {};
      const candidateLevel = normalizeClauseLevel(candidate?.level);
      if (candidateLevel >= currentLevel) continue;
      if (!doesBlockConditionPass(candidate)) return false;
      currentLevel = candidateLevel;
      if (currentLevel <= 0) return true;
    }

    return true;
  }

  const isBlockVisible = (block) => {
    if (!block) return false;
    if (!doesBlockConditionPass(block)) return false;
    if (Array.isArray(block?.children) && block.children.length > 0) {
      return block.children.some((child, index) =>
        isChildVisibleInHierarchy(block.children, index),
      );
    }
    return true;
  };

  const getVisibilityBadgeMeta = (block) => {
    const cond = String(block?.condition || "")
      .trim()
      .toUpperCase();
    if (cond === "LEGAL_OUTORGANTE_ONLY") {
      return {
        hiddenLabel: "Oculta no documento atual",
        activeLabel: "Visível para PJ Outorgante",
        helperLabel: "Visível apenas para PJ Outorgante",
      };
    }
    if (cond && cond !== "ALWAYS") {
      return {
        hiddenLabel: "Oculta no documento atual",
        activeLabel: "Cláusula condicional ativa",
        helperLabel: "Visível por regra condicional",
      };
    }
    return null;
  };

  const startEditing = (block) => {
    setEditingBlockId(block.id);
    setTempTitle(block.title);
    setActiveBlockData(
      JSON.parse(
        JSON.stringify({
          ...block,
          tags: Array.isArray(block.tags) ? block.tags : ["Universal"],
          condition: block.condition || "ALWAYS",
          children: Array.isArray(block?.children) ? block.children : undefined,
        }),
      ),
    );
  };

  const addSubClauseAfterInBlock = (blockId, childId, childIndex) => {
    const newChild = {
      id: `sub_${Date.now()}`,
      type: "sub_clause",
      content: "",
      condition: "ALWAYS",
      requiredHonorarios: [],
    };

    setTemplateData((prev) => {
      const structure = Array.isArray(prev?.structure)
        ? [...prev.structure]
        : [];
      const bIdx = structure.findIndex(
        (b) => String(b?.id || "") === String(blockId || ""),
      );
      if (bIdx < 0) return prev;
      const blk = structure[bIdx] || {};
      const children = Array.isArray(blk?.children) ? [...blk.children] : [];

      const idx = (() => {
        if (typeof childIndex === "number" && childIndex >= 0)
          return childIndex;
        if (childId) {
          const found = children.findIndex(
            (c) => String(c?.id || "") === String(childId),
          );
          return found >= 0 ? found : children.length - 1;
        }
        return children.length - 1;
      })();

      if (idx >= 0) children.splice(idx + 1, 0, newChild);
      else children.push(newChild);

      structure[bIdx] = { ...blk, children };
      return { ...(prev || {}), structure };
    });

    setIsModified(true);
  };

  const addSubClauseAfter = (childId, childIndex) => {
    if (!editingBlockId) return;

    const newChild = {
      id: `sub_${Date.now()}`,
      type: "sub_clause",
      content: "",
      condition: "ALWAYS",
      requiredHonorarios: [],
    };

    setActiveBlockData((prev) => {
      if (!prev) return prev;
      const children = Array.isArray(prev?.children) ? [...prev.children] : [];
      const idx =
        typeof childIndex === "number"
          ? childIndex
          : childId
            ? children.findIndex((c) => String(c?.id || "") === String(childId))
            : children.length - 1;
      if (idx >= 0) children.splice(idx + 1, 0, newChild);
      else children.push(newChild);
      return { ...prev, children };
    });
    setIsModified(true);
  };

  const saveEditorContent = (newData) => {
    const normalized = (() => {
      const safe = newData || {};
      if (safe.type === "list" && Array.isArray(safe.items)) {
        const html = safe.items
          .map((it) => String(it || "").trim())
          .filter(Boolean)
          .map((it) => `<p>${it}</p>`)
          .join("");
        return { ...safe, type: "text", items: null, content: html };
      }
      return safe;
    })();

    setTemplateData((prev) => ({
      ...prev,
      structure: (prev.structure || []).map((b) =>
        b.id === editingBlockId ? { ...b, ...normalized, title: tempTitle } : b,
      ),
    }));
    setEditingBlockId(null);
    setActiveBlockData(null);
    setIsModified(true);
  };

  const addFromLibrary = (libraryId) => {
    const block = POWERS_LIBRARY.find((b) => b.id === libraryId);
    if (!block) return;
    const cloned = JSON.parse(
      JSON.stringify({
        ...block,
        id: `${block.id}_${Date.now()}`,
      }),
    );
    setTemplateData((prev) => ({
      ...prev,
      structure: [...(prev.structure || []), cloned],
    }));
    setTimeout(
      () =>
        window.scrollTo({
          top: document.body.scrollHeight,
          behavior: "smooth",
        }),
      100,
    );
    setIsModified(true);
  };

  const addClauseAfter = (afterId) => {
    const newBlock = {
      id: `proc_new_${Date.now()}`,
      title: "NOVA CLÁUSULA",
      type: "text",
      tags: ["Universal"],
      condition: "ALWAYS",
      content: "<p>Digite o texto...</p>",
    };

    setTemplateData((prev) => {
      const structure = Array.isArray(prev?.structure)
        ? [...prev.structure]
        : [];
      const idx = structure.findIndex(
        (b) => String(b?.id || "") === String(afterId || ""),
      );
      if (idx >= 0) structure.splice(idx + 1, 0, newBlock);
      else structure.push(newBlock);
      return { ...(prev || {}), structure };
    });
    setIsModified(true);
    setTimeout(() => {
      try {
        scrollToBlock(newBlock.id);
      } catch {
        /* ignore */
      }
      try {
        startEditing(newBlock);
      } catch {
        /* ignore */
      }
    }, 100);
  };

  const addCustomBlock = () => {
    const newBlock = {
      id: `proc_new_${Date.now()}`,
      title: "NOVO BLOCO",
      type: "text",
      tags: ["Universal"],
      content: "<p>Digite o texto...</p>",
    };
    setTemplateData((prev) => ({
      ...prev,
      structure: [...(prev.structure || []), newBlock],
    }));
    setTimeout(
      () =>
        window.scrollTo({
          top: document.body.scrollHeight,
          behavior: "smooth",
        }),
      100,
    );
    setIsModified(true);
  };

  const deleteBlock = (id) => {
    setDialogState({
      open: true,
      title: "Confirmar Exclusão",
      message:
        "VOCÊ TEM CERTEZA?\n\nEste bloco será removido da procuração.\n\nPara desfazer, será necessário restaurar o backup ou resetar o draft.",
      mode: "confirm",
      onConfirm: () => {
        setTemplateData((prev) => ({
          ...prev,
          structure: (prev.structure || []).filter((b) => b.id !== id),
        }));
        setIsModified(true);
      },
    });
    if (editingBlockId === id) {
      setEditingBlockId(null);
      setActiveBlockData(null);
    }
  };

  const handleReorder = (fromIndex, toIndex) => {
    setTemplateData((prev) => {
      const next = [...(prev.structure || [])];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return { ...prev, structure: next };
    });
    setIsModified(true);
  };

  const scrollToBlock = (id) => {
    const el = document.getElementById(`proc-block-${id}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const handlePrint = () => {
    const previousTitle = document.title;
    const nextTitle = buildFriendlyProcurationName();
    const restoreTitle = () => {
      document.title = previousTitle;
      window.removeEventListener("afterprint", restoreTitle);
    };

    document.title = nextTitle;
    browserEnv.addEventListener("afterprint", restoreTitle);
    window.print();
    window.setTimeout(restoreTitle, 1000);
  };

  // Espaçamento (mb-4/py-3/mb-2/mb-1) e vão de assinatura (1,6cm) são
  // canônicos e idênticos nos 5 documentos de fechamento (Contrato,
  // Procuração, Declaração, Termo de Renúncia, Termo de Revogação) — não
  // são valores arbitrários. Ver CONSTITUICAO_REGRAS.SH Art. 384-386 antes
  // de alterar.
  const renderExecutionClosing = () => (
    <div className="mt-12 mb-4">
      <p className="text-justify leading-7 text-[11.5pt] text-gray-800 mb-4">
        E, por corresponder à cristalina expressão da verdade legal e de sua
        livre vontade, a parte <strong>OUTORGANTE</strong> firma o presente
        instrumento.
      </p>

      <div className="rounded-xl border border-amber-200 bg-amber-50/80 px-4 py-3 text-[10.5pt] leading-6 text-amber-950 text-justify mb-4">
        <strong>
          Nota de Validade Híbrida (art. 654, § 1º, da Lei n. 10.406/2002 —
          Código Civil Brasileiro):
        </strong>{" "}
        Na hipótese de assinatura eletrônica remota, o local de emissão, a data
        e a hora oficiais do ato coincidem de forma inalterável com os dados de
        geolocalização, IP e Carimbo do Tempo registrados no relatório de
        auditoria da plataforma certificadora anexa, suprindo plenamente o
        preenchimento manual dos campos. Em caso de assinatura física
        presencial, inclusive no interior de estabelecimentos prisionais ou
        hospitalares, o local exato e a data deverão ser preenchidos de próprio
        punho pela parte <strong>OUTORGANTE</strong> ou por seu patrono nos
        campos abaixo.
      </div>

      <div className="mb-4 rounded-xl border border-dashed border-stone-300 bg-white px-4 py-3">
        <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-stone-500 mb-2">
          Local e Data de Emissão
        </p>
        <p className="text-[10.5pt] italic leading-6 text-stone-400 mb-1">
          (apenas para assinatura física presencial)
        </p>
        <p className="text-[11.5pt] leading-7 text-gray-800 break-words">
          <span className="inline-block min-w-[220px] border-b border-stone-400 align-baseline">
            &nbsp;
          </span>
          ,{" "}
          <span className="inline-block min-w-[42px] text-center border-b border-stone-400 align-baseline">
            &nbsp;
          </span>{" "}
          de{" "}
          <span className="inline-block min-w-[170px] border-b border-stone-400 align-baseline">
            &nbsp;
          </span>{" "}
          de{" "}
          <span className="inline-flex min-w-[76px] border-b border-stone-400 align-baseline justify-start">
            <span className="pl-1">20</span>
          </span>
          .
        </p>
      </div>
    </div>
  );

  const renderOutorganteSignatures = () => {
    const signatureParties = (
      Array.isArray(formData?.parties) ? formData.parties : []
    ).filter((party) => String(party?.name || "").trim());
    const resolvedParties =
      signatureParties.length > 0
        ? signatureParties
        : [{ id: "fallback_outorgante", name: "OUTORGANTE" }];
    const consolidatedSignatures = resolvedParties.reduce((acc, party, idx) => {
      const clientData = party?.qualificationData || party || {};
      const signature = buildSignatureLine(clientData);
      const signerName =
        String(signature?.nameLine || "").trim() ||
        "[Signatário não informado]";
      const signerCpf =
        String(signature?.cpfLine || "").trim() || "CPF: [Não informado]";
      const signerKey = `${signerName}__${signerCpf}`;
      const animalName = String(
        clientData?.qualification?.animal?.name || "",
      ).trim();
      const isAnimalParty = Boolean(animalName);
      const clientType = String(clientData?.type || "").trim();
      const isLegalEntity = clientType === "Pessoa Jurídica";
      const isMinor = clientType === "Menor";
      const isEstate = clientType === "Espólio";
      const needsRepresentative =
        isAnimalParty || isLegalEntity || isMinor || isEstate;
      const representedName = isAnimalParty
        ? animalName
        : String(
            clientData?.qualification?.legal?.companyName ||
              clientData?.qualification?.minor?.name ||
              clientData?.qualification?.estate?.deceasedName ||
              "",
          ).trim();

      if (!acc[signerKey]) {
        acc[signerKey] = {
          key: String(party?.id || `signature-party-${idx}`),
          signature,
          representedNames: [],
          hasRepresentativeRole: false,
        };
      }

      if (
        needsRepresentative &&
        representedName &&
        representedName.toUpperCase() !== signerName.toUpperCase()
      ) {
        const existingNames = acc[signerKey].representedNames;
        if (!existingNames.includes(representedName))
          existingNames.push(representedName);
        acc[signerKey].hasRepresentativeRole = true;
      }

      return acc;
    }, {});

    const signatureEntries = Object.values(consolidatedSignatures).map(
      (entry) => {
        const representedNames = Array.isArray(entry?.representedNames)
          ? entry.representedNames.filter(Boolean)
          : [];
        const representativeLine =
          entry?.hasRepresentativeRole && representedNames.length > 0
            ? `Responsável legal por ${representedNames.join(" e ")}`
            : "";

        return {
          key: entry?.key,
          signature: {
            ...(entry?.signature || {}),
            representativeLine,
          },
        };
      },
    );

    const isSingleSignature = signatureEntries.length === 1;

    return (
      <div
        className={
          isSingleSignature
            ? "flex justify-center"
            : "grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-14"
        }
      >
        {signatureEntries.map((entry, idx) => {
          const signature = entry?.signature || {};
          return (
            <div
              key={entry?.key || `signature-party-${idx}`}
              className={
                isSingleSignature
                  ? "w-full max-w-md text-center border-t border-black pt-2"
                  : "text-center border-t border-black pt-2"
              }
            >
              <span className="font-bold">
                {signature?.nameLine || "[Signatário não informado]"}
              </span>
              <br />
              <span className="text-[11px]">
                {(signature?.cpfLine || "CPF: [Não informado]").replace(
                  /^CPF:\s*/i,
                  "CPF ",
                )}{" "}
                — OUTORGANTE
              </span>
              {String(signature?.representativeLine || "").trim() ? (
                <>
                  <br />
                  <span className="text-[11px]">
                    {signature.representativeLine}
                  </span>
                </>
              ) : null}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="flex flex-col lg:flex-row h-[calc(100vh-64px)] font-sans bg-gray-100 overflow-hidden print:h-auto print:overflow-visible w-full z-0 relative">
      <div className="w-full lg:w-[360px] bg-white border-r border-gray-200 shadow-xl z-20 flex flex-col flex-shrink-0 no-print h-full transition-all duration-300">
        <div
          className="no-print shrink-0 flex flex-col"
          style={{ background: "#20243B" }}
        >
          <div className="flex items-baseline gap-2 px-4 py-3">
            <span className="text-white font-bold text-sm tracking-wide">
              Motor de Procurações
            </span>
          </div>
          <div className="h-0.5 w-full" style={{ background: "#C5A059" }} />
          <div className="flex overflow-x-auto px-2 py-1.5 gap-0.5 border-b border-white/10">
            {[
              ["client", "Partes"],
              ["partners", "Parceiros"],
              ["context", "Contexto"],
              ["validade", "Validade"],
            ].map(([id, label], i) => (
              <button
                key={id}
                type="button"
                onClick={() => setOpenSection(openSection === id ? null : id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-medium whitespace-nowrap border-b-2 transition-colors ${openSection === id ? "border-[#C5A059] text-[#C5A059] bg-white/10" : "border-transparent text-white/50 hover:text-white/80"}`}
              >
                <span
                  className="inline-flex items-center justify-center w-4 h-4 rounded-full text-[9px] font-bold flex-shrink-0"
                  style={{
                    background:
                      openSection === id ? "#C5A059" : "rgba(255,255,255,0.2)",
                    color: "white",
                  }}
                >
                  {i + 1}
                </span>
                {label}
              </button>
            ))}
          </div>
        </div>
        <div className="p-3 bg-white border-b border-gray-200 shrink-0">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 rounded-xl border border-gray-200 bg-gray-50 p-1">
              <button
                onClick={() => setMode("producao")}
                className={`px-3 py-1.5 text-[11px] font-bold rounded-lg transition-colors ${mode === "producao" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
                type="button"
              >
                DOCUMENTO
              </button>
              <button
                onClick={() => setMode("auditoria")}
                className={`px-3 py-1.5 text-[11px] font-bold rounded-lg transition-colors ${mode === "auditoria" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
                type="button"
              >
                ESTRUTURA
              </button>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto custom-scrollbar px-2.5 py-2.5 bg-gray-50/80">
          <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
            <ConfigSection
              id="client"
              title="Qualificação das Partes"
              icon="fa-user"
              openSection={openSection}
              setOpenSection={setOpenSection}
            >
              <div className="space-y-2.5">
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      const firstId =
                        (Array.isArray(formData?.parties)
                          ? formData.parties
                          : [])?.[0]?.id || null;
                      setImportTargetPartyId(firstId);
                      setShowImport(true);
                    }}
                    className="flex-1 bg-indigo-50 border border-indigo-200 text-indigo-700 py-1.5 rounded-lg text-[10px] font-semibold flex items-center justify-center gap-1.5 hover:bg-indigo-100 transition-colors shadow-sm"
                    type="button"
                  >
                    <Download className="w-3 h-3" /> IMPORTAR DO CRM
                  </button>
                  <button
                    onClick={() => {
                      addParty();
                    }}
                    className="bg-white border border-gray-200 text-gray-700 py-1.5 px-2.5 rounded-lg text-[11px] font-bold flex items-center justify-center gap-1.5 hover:bg-gray-50 transition-colors shadow-sm"
                    type="button"
                    title="Adicionar Outorgante"
                  >
                    <Plus className="w-3 h-3" />
                  </button>
                </div>

                <div className="space-y-2.5">
                  {(Array.isArray(formData?.parties)
                    ? formData.parties
                    : []
                  ).map((party, idx) => (
                    <div
                      key={party.id}
                      className="bg-white border border-gray-200 rounded-lg p-2 shadow-sm"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="text-[9px] font-semibold text-gray-500 uppercase tracking-[0.04em]">
                          {formatPartyRoleLabel(party?.role)} {idx + 1}
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => {
                              setImportTargetPartyId(party.id);
                              setShowImport(true);
                            }}
                            className="text-[10px] font-bold text-indigo-700 bg-indigo-50 border border-indigo-200 rounded px-2 py-1 hover:bg-indigo-100"
                            type="button"
                          >
                            Importar CRM
                          </button>
                          <button
                            onClick={() => {
                              removeParty(party.id);
                            }}
                            className="text-[10px] font-bold text-red-600 bg-white border border-gray-200 rounded px-2 py-1 hover:bg-red-50"
                            type="button"
                            disabled={
                              (Array.isArray(formData?.parties)
                                ? formData.parties
                                : []
                              ).length <= 1
                            }
                            title="Remover Outorgante"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-2 mt-2.5">
                        <div className="space-y-1 col-span-2">
                          <label className={labelBaseStyle}>Nome</label>
                          <input
                            className={
                              inputBaseStyle +
                              " bg-gray-50 text-[10px] font-normal text-gray-500 cursor-not-allowed"
                            }
                            value={party?.name || ""}
                            readOnly
                            disabled
                          />
                        </div>
                        <div className="space-y-1">
                          <label className={labelBaseStyle}>CPF/CNPJ</label>
                          <input
                            className={
                              inputBaseStyle +
                              " bg-gray-50 text-[10px] font-normal text-gray-500 cursor-not-allowed"
                            }
                            value={party?.cpf || ""}
                            readOnly
                            disabled
                          />
                        </div>
                        <div className="space-y-1">
                          <label className={labelBaseStyle}>Papel</label>
                          <select
                            className={
                              inputBaseStyle +
                              " bg-gray-50 text-[10px] font-normal text-gray-500 cursor-not-allowed"
                            }
                            value={party?.role || "OUTORGANTE"}
                            disabled
                          >
                            {PROCURATION_ROLE_OPTIONS.map((roleOption) => (
                              <option key={roleOption} value={roleOption}>
                                {roleOption}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </ConfigSection>

            <ConfigSection
              id="partners"
              title="Co-patrocínio"
              icon="fa-user-tie"
              openSection={openSection}
              setOpenSection={setOpenSection}
            >
              <div className="space-y-2.5">
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-[11px] text-amber-800">
                  A parte outorgada é injetada automaticamente com base nos
                  dados do escritório. Use esta seção apenas para adicionar
                  advogados parceiros à procuração.
                </div>

                <div className="bg-white border border-gray-200 rounded-lg p-2 shadow-sm space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-[10px] font-bold text-gray-500 uppercase tracking-widest">
                      Advogado Parceiro Principal
                    </div>
                    <button
                      onClick={() => {
                        setImportTargetPartnerIndex(0);
                        setShowPartnerImportModal(true);
                      }}
                      className="text-[10px] font-bold text-indigo-700 bg-indigo-50 border border-indigo-200 rounded px-2 py-1 hover:bg-indigo-100"
                      type="button"
                    >
                      Importar CRM
                    </button>
                  </div>
                  <label className="flex items-center gap-2 text-[11px] font-semibold text-gray-700">
                    <input
                      type="checkbox"
                      checked={Boolean(partnerData?.active)}
                      onChange={(e) =>
                        updatePartner("active", e.target.checked)
                      }
                    />
                    Habilitar parceiro principal
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1 col-span-2">
                      <label className={labelBaseStyle}>Nome</label>
                      <input
                        className={inputBaseStyle}
                        value={partnerData?.name || ""}
                        onChange={(e) => updatePartner("name", e.target.value)}
                      />
                    </div>
                    <div className="space-y-1">
                      <label className={labelBaseStyle}>OAB</label>
                      <input
                        className={inputBaseStyle}
                        value={partnerData?.oab || ""}
                        onChange={(e) => updatePartner("oab", e.target.value)}
                      />
                    </div>
                    <div className="space-y-1">
                      <label className={labelBaseStyle}>CPF</label>
                      <input
                        className={inputBaseStyle}
                        value={partnerData?.cpf || ""}
                        onChange={(e) => updatePartner("cpf", e.target.value)}
                      />
                    </div>
                    <div className="space-y-1 col-span-2">
                      <label className={labelBaseStyle}>
                        Endereço profissional
                      </label>
                      <input
                        className={inputBaseStyle}
                        value={partnerData?.address || ""}
                        onChange={(e) =>
                          updatePartner("address", e.target.value)
                        }
                      />
                    </div>
                  </div>

                  <div className="border-t border-gray-200 pt-2 mt-1 space-y-2">
                    <label className="flex items-center gap-2 text-[11px] font-semibold text-gray-700">
                      <input
                        type="checkbox"
                        checked={Boolean(partnerData?.qualificacaoCompleta)}
                        onChange={(e) =>
                          updatePartner(
                            "qualificacaoCompleta",
                            e.target.checked ? " " : "",
                          )
                        }
                      />
                      Fornecer qualificação completa manualmente
                    </label>
                    {partnerData?.qualificacaoCompleta ||
                    partnerData?.qualificacaoCompleta === " " ? (
                      <div className="space-y-1">
                        <div className="flex items-center gap-1 border border-gray-200 rounded-t-lg bg-gray-50 px-2 py-1">
                          <button
                            type="button"
                            className="px-2 py-0.5 text-xs font-bold bg-white border border-gray-300 rounded hover:bg-gray-100"
                            onClick={() => {
                              const sel = window.getSelection();
                              if (!sel.rangeCount) return;
                              const editor =
                                document.getElementById("qual-editor");
                              if (
                                !editor ||
                                !editor.contains(
                                  sel.getRangeAt(0).commonAncestorContainer,
                                )
                              )
                                return;
                              document.execCommand("bold", false, null);
                              editor.focus();
                              updatePartner(
                                "qualificacaoCompleta",
                                editor.innerHTML,
                              );
                            }}
                          >
                            <strong>B</strong>
                          </button>
                          <button
                            type="button"
                            className="px-2 py-0.5 text-xs italic font-bold bg-white border border-gray-300 rounded hover:bg-gray-100"
                            onClick={() => {
                              const sel = window.getSelection();
                              if (!sel.rangeCount) return;
                              const editor =
                                document.getElementById("qual-editor");
                              if (
                                !editor ||
                                !editor.contains(
                                  sel.getRangeAt(0).commonAncestorContainer,
                                )
                              )
                                return;
                              document.execCommand("italic", false, null);
                              editor.focus();
                              updatePartner(
                                "qualificacaoCompleta",
                                editor.innerHTML,
                              );
                            }}
                          >
                            <em>I</em>
                          </button>
                          <span className="text-[9px] text-gray-400 ml-auto">
                            Negrito e itálico
                          </span>
                        </div>
                        <div
                          id="qual-editor"
                          dir="ltr"
                          style={{
                            direction: "ltr",
                            textAlign: "left",
                            whiteSpace: "pre-wrap",
                          }}
                          className={
                            "border border-t-0 border-gray-200 rounded-b-lg px-3 py-2 text-[12px] outline-none focus:border-[#C5A059] min-h-[80px] text-left"
                          }
                          contentEditable
                          suppressContentEditableWarning
                          onBlur={(e) => {
                            const html = e.currentTarget.innerHTML;
                            const val =
                              html === "<br>" || html.trim() === ""
                                ? " "
                                : html;
                            updatePartner("qualificacaoCompleta", val);
                          }}
                        />
                      </div>
                    ) : null}
                  </div>
                </div>

                <div className="space-y-2">
                  {(Array.isArray(partnerExtras) ? partnerExtras : []).map(
                    (partner, idx) => (
                      <div
                        key={`partner-extra-${idx}`}
                        className="bg-white border border-gray-200 rounded-lg p-2 shadow-sm space-y-2"
                      >
                        <div className="flex items-center justify-between gap-2">
                          <div className="text-[10px] font-bold text-gray-500 uppercase tracking-widest">
                            Parceiro Extra {idx + 1}
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => {
                                setImportTargetPartnerIndex(idx + 1);
                                setShowPartnerImportModal(true);
                              }}
                              className="text-[10px] font-bold text-indigo-700 bg-indigo-50 border border-indigo-200 rounded px-2 py-1 hover:bg-indigo-100"
                              type="button"
                            >
                              Importar CRM
                            </button>
                            <button
                              onClick={() => {
                                setPartnerExtras((prev) =>
                                  Array.isArray(prev)
                                    ? prev.filter((_, pIdx) => pIdx !== idx)
                                    : [],
                                );
                                setIsModified(true);
                              }}
                              className="text-[10px] font-bold text-red-600 bg-white border border-gray-200 rounded px-2 py-1 hover:bg-red-50"
                              type="button"
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div className="space-y-1 col-span-2">
                            <label className={labelBaseStyle}>Nome</label>
                            <input
                              className={inputBaseStyle}
                              value={partner?.name || ""}
                              onChange={(e) => {
                                const value = e.target.value;
                                setPartnerExtras((prev) => {
                                  const arr = Array.isArray(prev)
                                    ? [...prev]
                                    : [];
                                  arr[idx] = {
                                    ...(arr[idx] || {}),
                                    name: value,
                                  };
                                  return arr;
                                });
                                setIsModified(true);
                              }}
                            />
                          </div>
                          <div className="space-y-1">
                            <label className={labelBaseStyle}>OAB</label>
                            <input
                              className={inputBaseStyle}
                              value={partner?.oab || ""}
                              onChange={(e) => {
                                const value = e.target.value;
                                setPartnerExtras((prev) => {
                                  const arr = Array.isArray(prev)
                                    ? [...prev]
                                    : [];
                                  arr[idx] = {
                                    ...(arr[idx] || {}),
                                    oab: value,
                                  };
                                  return arr;
                                });
                                setIsModified(true);
                              }}
                            />
                          </div>
                          <div className="space-y-1">
                            <label className={labelBaseStyle}>CPF</label>
                            <input
                              className={inputBaseStyle}
                              value={partner?.cpf || ""}
                              onChange={(e) => {
                                const value = e.target.value;
                                setPartnerExtras((prev) => {
                                  const arr = Array.isArray(prev)
                                    ? [...prev]
                                    : [];
                                  arr[idx] = {
                                    ...(arr[idx] || {}),
                                    cpf: value,
                                  };
                                  return arr;
                                });
                                setIsModified(true);
                              }}
                            />
                          </div>
                          <div className="space-y-1 col-span-2">
                            <label className={labelBaseStyle}>
                              Endereço profissional
                            </label>
                            <input
                              className={inputBaseStyle}
                              value={partner?.address || ""}
                              onChange={(e) => {
                                const value = e.target.value;
                                setPartnerExtras((prev) => {
                                  const arr = Array.isArray(prev)
                                    ? [...prev]
                                    : [];
                                  arr[idx] = {
                                    ...(arr[idx] || {}),
                                    address: value,
                                  };
                                  return arr;
                                });
                                setIsModified(true);
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    ),
                  )}
                </div>

                <button
                  onClick={() => {
                    setPartnerExtras((prev) => [
                      ...(Array.isArray(prev) ? prev : []),
                      { ...DEFAULT_PARTNER },
                    ]);
                    setIsModified(true);
                  }}
                  className="w-full bg-white border border-gray-200 text-gray-700 py-1.5 px-2.5 rounded-lg text-[11px] font-bold flex items-center justify-center gap-1.5 hover:bg-gray-50 transition-colors shadow-sm"
                  type="button"
                >
                  <Plus className="w-3 h-3" /> Adicionar Parceiro Extra
                </button>
              </div>
            </ConfigSection>

            <ConfigSection
              id="context"
              title="Contexto & Objeto"
              icon="fa-balance-scale"
              openSection={openSection}
              setOpenSection={setOpenSection}
            >
              <div className="space-y-2.5">
                <div className="space-y-1">
                  <label className={labelBaseStyle}>Ramo</label>
                  <CheckboxDropdownMultiSelect
                    options={ALLOWED_RAMO_KEYS.map((ramoKey) => ({
                      value: ramoKey,
                      label: RAMO_TAG_MAP[ramoKey] || ramoKey,
                    }))}
                    values={selectedRamoKeys}
                    placeholder="Selecionar ramos"
                    onUpdate={(nextRamos) => {
                      const safeRamos =
                        nextRamos.length > 0 ? nextRamos : ["Civil"];
                      const nextPosicaoOptions = getPosicaoOptionsByRamo(
                        safeRamos,
                      ).map((option) => option.value);
                      setFormData((prev) => {
                        const previousPosicoes = normalizeStringArray(
                          prev?.posicoesCliente || prev?.posicaoCliente,
                        ).filter((posicao) =>
                          nextPosicaoOptions.includes(posicao),
                        );
                        return {
                          ...(prev || {}),
                          ramoDireito: safeRamos[0] || "Civil",
                          ramosDireito: safeRamos,
                          posicaoCliente: previousPosicoes[0] || "",
                          posicoesCliente: previousPosicoes,
                        };
                      });
                      setIsModified(true);
                    }}
                  />
                  <p className="text-[10px] text-gray-500">
                    Marque quantos ramos forem necessários para procurações com
                    processos simultâneos.
                  </p>
                </div>

                <div className="space-y-1">
                  <label className={labelBaseStyle}>Posição</label>
                  <CheckboxDropdownMultiSelect
                    options={posicaoOptions}
                    values={selectedPosicoes}
                    placeholder="Selecionar posições"
                    onUpdate={(nextPosicoes) => {
                      setFormData((prev) => ({
                        ...(prev || {}),
                        posicaoCliente: nextPosicoes[0] || "",
                        posicoesCliente: nextPosicoes,
                      }));
                      setIsModified(true);
                    }}
                  />
                  <p className="text-[10px] text-gray-500">
                    Você pode marcar múltiplas posições para procurações com
                    processos simultâneos.
                  </p>
                </div>

                <div className="space-y-1">
                  <label className={labelBaseStyle}>Objeto Principal</label>
                  <textarea
                    className={inputBaseStyle + " resize-none min-h-[120px]"}
                    value={formData?.objetoPrincipal || ""}
                    onChange={(e) => {
                      setFormData((prev) => ({
                        ...(prev || {}),
                        objetoPrincipal: e.target.value,
                      }));
                      setIsModified(true);
                    }}
                  />
                </div>

                <div
                  className={`rounded-xl border px-2.5 py-2.5 transition-colors ${formData?.patrimonialImobiliarioEnabled ? "border-amber-300 bg-amber-50/70" : "border-gray-200 bg-gray-50"}`}
                >
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      className="mt-1 rounded text-brand-blue focus:ring-0"
                      checked={Boolean(formData?.patrimonialImobiliarioEnabled)}
                      onChange={(e) => {
                        const checked = Boolean(e.target.checked);
                        setFormData((prev) => ({
                          ...(prev || {}),
                          patrimonialImobiliarioEnabled: checked,
                          bensImoveisEspecificos: checked
                            ? String(prev?.bensImoveisEspecificos || "")
                            : "",
                          bemImovelMatricula: checked
                            ? String(prev?.bemImovelMatricula || "")
                            : "",
                          bemImovelCartorio: checked
                            ? String(prev?.bemImovelCartorio || "")
                            : "",
                          bemImovelEndereco: checked
                            ? String(prev?.bemImovelEndereco || "")
                            : "",
                          bemImovelNaturezaDireito: checked
                            ? String(prev?.bemImovelNaturezaDireito || "")
                            : "",
                          bemImovelObservacoes: checked
                            ? String(prev?.bemImovelObservacoes || "")
                            : "",
                        }));
                        setIsModified(true);
                      }}
                    />
                    <div className="flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-[11px] font-extrabold uppercase tracking-wider text-gray-700">
                          Cláusula de especialidade patrimonial imobiliária
                        </span>
                        <span
                          className={`text-[9px] font-bold uppercase px-2 py-1 rounded border ${formData?.patrimonialImobiliarioEnabled ? "bg-amber-100 text-amber-700 border-amber-200" : "bg-gray-100 text-gray-500 border-gray-200"}`}
                        >
                          {formData?.patrimonialImobiliarioEnabled
                            ? "Ativa"
                            : "Inativa"}
                        </span>
                      </div>
                      <p className="text-[11px] text-gray-600 mt-2">
                        Habilite apenas quando o mandato envolver alienação,
                        oneração, renúncia, promessa de venda, dação em
                        pagamento de bens imóveis ou disposição de direitos
                        reais, com individualização concreta do bem ou direito
                        atingido.
                      </p>
                    </div>
                  </div>
                </div>

                {[
                  {
                    key: "envolveCriptoEnabled",
                    label: "Gestão de ativos virtuais / criptoeconomia",
                    help: "Habilite apenas quando o cliente possuir criptoativos, carteiras digitais ou patrimônio custodiado em PSAVs (Res. BCB 520/2025) relevantes ao mandato.",
                  },
                  {
                    key: "investigacaoDefensivaEnabled",
                    label: "Investigação defensiva (Provimento 188/2018)",
                    help: "Habilite quando o caso exigir instauração de inquérito defensivo para proteger quem já é parte interessada (investigado, vítima ou terceiro) — Provimento 188/2018 CFOAB. Para apuração interna da própria empresa, use o toggle de Compliance Corporativo, abaixo.",
                  },
                  {
                    key: "arbitragemEnabled",
                    label: "Representação multi-portas / arbitragem (ODR)",
                    help: "Habilite apenas quando houver possibilidade real de mediação, conciliação ou arbitragem privada/ODR neste caso.",
                  },
                  {
                    key: "complianceInternoEnabled",
                    label: "Compliance corporativo / investigação interna",
                    help: "Habilite apenas para apuração interna da própria empresa (Lei 12.846/2013 + Decreto 11.129/2022) — diferente da investigação defensiva, que protege quem já é parte interessada.",
                  },
                ].map((toggle) => (
                  <div
                    key={toggle.key}
                    className={`rounded-xl border px-2.5 py-2.5 transition-colors ${formData?.[toggle.key] ? "border-amber-300 bg-amber-50/70" : "border-gray-200 bg-gray-50"}`}
                  >
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        className="mt-1 rounded text-brand-blue focus:ring-0"
                        checked={Boolean(formData?.[toggle.key])}
                        onChange={(e) => {
                          const checked = Boolean(e.target.checked);
                          setFormData((prev) => ({
                            ...(prev || {}),
                            [toggle.key]: checked,
                          }));
                          setIsModified(true);
                        }}
                      />
                      <div className="flex-1">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-[11px] font-extrabold uppercase tracking-wider text-gray-700">
                            {toggle.label}
                          </span>
                          <span
                            className={`text-[9px] font-bold uppercase px-2 py-1 rounded border ${formData?.[toggle.key] ? "bg-amber-100 text-amber-700 border-amber-200" : "bg-gray-100 text-gray-500 border-gray-200"}`}
                          >
                            {formData?.[toggle.key] ? "Ativa" : "Inativa"}
                          </span>
                        </div>
                        <p className="text-[11px] text-gray-600 mt-2">
                          {toggle.help}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}

                {formData?.patrimonialImobiliarioEnabled ? (
                  <div className="space-y-2.5 rounded-xl border border-amber-200 bg-white px-2.5 py-2.5">
                    <div className="grid grid-cols-1 gap-2.5">
                      <div className="space-y-1">
                        <label className={labelBaseStyle}>
                          Descrição-base do bem ou direito
                        </label>
                        <textarea
                          className={
                            inputBaseStyle + " resize-none min-h-[90px]"
                          }
                          value={formData?.bensImoveisEspecificos || ""}
                          placeholder="Descreva o bem, a fração ideal, a unidade, a transação ou o direito real envolvido."
                          onChange={(e) => {
                            setFormData((prev) => ({
                              ...(prev || {}),
                              bensImoveisEspecificos: e.target.value,
                            }));
                            setIsModified(true);
                          }}
                        />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
                        <div className="space-y-1">
                          <label className={labelBaseStyle}>Matrícula</label>
                          <input
                            className={inputBaseStyle}
                            value={formData?.bemImovelMatricula || ""}
                            onChange={(e) => {
                              setFormData((prev) => ({
                                ...(prev || {}),
                                bemImovelMatricula: e.target.value,
                              }));
                              setIsModified(true);
                            }}
                          />
                        </div>
                        <div className="space-y-1">
                          <label className={labelBaseStyle}>Cartório</label>
                          <input
                            className={inputBaseStyle}
                            value={formData?.bemImovelCartorio || ""}
                            onChange={(e) => {
                              setFormData((prev) => ({
                                ...(prev || {}),
                                bemImovelCartorio: e.target.value,
                              }));
                              setIsModified(true);
                            }}
                          />
                        </div>
                      </div>
                      <div className="space-y-1">
                        <label className={labelBaseStyle}>
                          Endereço do bem
                        </label>
                        <input
                          className={inputBaseStyle}
                          value={formData?.bemImovelEndereco || ""}
                          onChange={(e) => {
                            setFormData((prev) => ({
                              ...(prev || {}),
                              bemImovelEndereco: e.target.value,
                            }));
                            setIsModified(true);
                          }}
                        />
                      </div>
                      <div className="space-y-1">
                        <label className={labelBaseStyle}>
                          Natureza do direito
                        </label>
                        <input
                          className={inputBaseStyle}
                          value={formData?.bemImovelNaturezaDireito || ""}
                          placeholder="Ex.: propriedade plena, fração ideal, usufruto, posse, direito hereditário, promessa de compra e venda"
                          onChange={(e) => {
                            setFormData((prev) => ({
                              ...(prev || {}),
                              bemImovelNaturezaDireito: e.target.value,
                            }));
                            setIsModified(true);
                          }}
                        />
                      </div>
                      <div className="space-y-1">
                        <label className={labelBaseStyle}>
                          Observações complementares
                        </label>
                        <textarea
                          className={
                            inputBaseStyle + " resize-none min-h-[80px]"
                          }
                          value={formData?.bemImovelObservacoes || ""}
                          onChange={(e) => {
                            setFormData((prev) => ({
                              ...(prev || {}),
                              bemImovelObservacoes: e.target.value,
                            }));
                            setIsModified(true);
                          }}
                        />
                      </div>
                    </div>
                    <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                      <span className="block text-[10px] font-extrabold uppercase tracking-wider text-gray-500">
                        Prévia da individualização que irá para a cláusula 2.2
                      </span>
                      <p className="text-[11px] text-gray-700 mt-2">
                        {patrimonialImobiliarioDescricao ||
                          "[Bens imóveis específicos não informados]"}
                      </p>
                    </div>
                    {!patrimonialImobiliarioDescricao ? (
                      <p className="text-[10px] font-bold uppercase tracking-wider text-red-500">
                        Informe a individualização dos bens para liberar a
                        cláusula 2.2 no documento.
                      </p>
                    ) : null}
                  </div>
                ) : null}
              </div>
            </ConfigSection>

            <ConfigSection
              id="facts-adverse"
              title="Dados Fáticos e Adverso"
              icon="fa-circle-exclamation"
              openSection={openSection}
              setOpenSection={setOpenSection}
            >
              <div className="p-1 space-y-3">
                {hasPenalSelected ? (
                  <>
                    <div className="space-y-1">
                      <label className={labelBaseStyle}>
                        Nome do Investigado/Querelado/Réu
                      </label>
                      <input
                        className={inputBaseStyle}
                        value={formData?.quereladoName || ""}
                        onChange={(e) => {
                          setFormData((prev) => ({
                            ...(prev || {}),
                            quereladoName: e.target.value,
                          }));
                          setIsModified(true);
                        }}
                      />
                    </div>

                    <div className="space-y-1">
                      <label className={labelBaseStyle}>Data do Fato</label>
                      <input
                        type="date"
                        className={inputBaseStyle}
                        value={formData?.dataFato || ""}
                        onChange={(e) => {
                          setFormData((prev) => ({
                            ...(prev || {}),
                            dataFato: e.target.value,
                          }));
                          setIsModified(true);
                        }}
                      />
                    </div>

                    <div className="space-y-1">
                      <label className={labelBaseStyle}>
                        Descrição Sucinta do Fato
                      </label>
                      <textarea
                        className={
                          inputBaseStyle + " resize-none min-h-[120px]"
                        }
                        value={formData?.descricaoFato || ""}
                        onChange={(e) => {
                          setFormData((prev) => ({
                            ...(prev || {}),
                            descricaoFato: e.target.value,
                          }));
                          setIsModified(true);
                        }}
                      />
                    </div>
                  </>
                ) : null}

                {hasMandadoSegurancaSelected ? (
                  <>
                    <div className="space-y-1">
                      <label className={labelBaseStyle}>
                        Autoridade Coatora (nome e cargo)
                      </label>
                      <input
                        className={inputBaseStyle}
                        placeholder="Ex.: Juiz de Direito da 3ª Vara Criminal, Sr. Fulano de Tal"
                        value={formData?.autoridadeCoatoraNome || ""}
                        onChange={(e) => {
                          setFormData((prev) => ({
                            ...(prev || {}),
                            autoridadeCoatoraNome: e.target.value,
                          }));
                          setIsModified(true);
                        }}
                      />
                    </div>

                    <div className="space-y-1">
                      <label className={labelBaseStyle}>
                        Órgão / Pessoa Jurídica Vinculada
                      </label>
                      <input
                        className={inputBaseStyle}
                        placeholder="Ex.: Estado de Goiás, Município de Curitiba, União"
                        value={formData?.autoridadeCoatoraOrgao || ""}
                        onChange={(e) => {
                          setFormData((prev) => ({
                            ...(prev || {}),
                            autoridadeCoatoraOrgao: e.target.value,
                          }));
                          setIsModified(true);
                        }}
                      />
                      <p className="text-[10px] text-gray-500">
                        A condenação recai sobre a pessoa jurídica a que a
                        autoridade está vinculada, não sobre o agente
                        pessoalmente (art. 6º, Lei 12.016/2009).
                      </p>
                    </div>

                    <div className="space-y-1">
                      <label className={labelBaseStyle}>
                        Data do Ato Coator (ciência inequívoca)
                      </label>
                      <input
                        type="date"
                        className={inputBaseStyle}
                        value={formData?.atoCoatorData || ""}
                        onChange={(e) => {
                          setFormData((prev) => ({
                            ...(prev || {}),
                            atoCoatorData: e.target.value,
                          }));
                          setIsModified(true);
                        }}
                      />
                      <p className="text-[10px] text-gray-500">
                        Marco do prazo decadencial de 120 dias (art. 23, Lei
                        12.016/2009) — não se aplica a atos omissivos
                        continuados.
                      </p>
                    </div>

                    <div className="space-y-1">
                      <label className={labelBaseStyle}>
                        Descrição do Ato Coator
                      </label>
                      <textarea
                        className={
                          inputBaseStyle + " resize-none min-h-[120px]"
                        }
                        placeholder="Natureza do ato (comissivo/omissivo), decisão ou omissão impugnada, e o direito líquido e certo violado."
                        value={formData?.atoCoatorDescricao || ""}
                        onChange={(e) => {
                          setFormData((prev) => ({
                            ...(prev || {}),
                            atoCoatorDescricao: e.target.value,
                          }));
                          setIsModified(true);
                        }}
                      />
                    </div>
                  </>
                ) : null}

                {hasNonPenalSelected || hasPenalSelected ? (
                  <>
                    <div className="space-y-1">
                      <label className={labelBaseStyle}>
                        Nome da Parte Contrária
                      </label>
                      <input
                        className={inputBaseStyle}
                        value={formData?.parteContrariaName || ""}
                        onChange={(e) => {
                          setFormData((prev) => ({
                            ...(prev || {}),
                            parteContrariaName: e.target.value,
                          }));
                          setIsModified(true);
                        }}
                      />
                    </div>
                  </>
                ) : null}
              </div>
            </ConfigSection>

            <ConfigSection
              id="validade"
              title="Validade do Mandato"
              icon="fa-calendar-alt"
              openSection={openSection}
              setOpenSection={setOpenSection}
            >
              <div className="p-1 space-y-3">
                <div className="space-y-1">
                  <label className={labelBaseStyle}>Tipo de Validade</label>
                  <select
                    className={inputBaseStyle}
                    value={formData?.validadeMandatoTipo || "INDETERMINADO"}
                    onChange={(e) => {
                      const nextType = e.target.value;
                      setFormData((prev) => ({
                        ...(prev || {}),
                        validadeMandatoTipo: nextType,
                        validadeMandatoData:
                          nextType === "DETERMINADO"
                            ? prev?.validadeMandatoData || ""
                            : "",
                        validadeMandatoAto:
                          nextType === "ATO_ESPECIFICO"
                            ? prev?.validadeMandatoAto || ""
                            : "",
                      }));
                      setIsModified(true);
                    }}
                  >
                    <option value="INDETERMINADO">Prazo indeterminado</option>
                    <option value="DETERMINADO">Prazo determinado</option>
                    <option value="ATO_ESPECIFICO">Ato específico</option>
                  </select>
                </div>

                {String(
                  formData?.validadeMandatoTipo || "INDETERMINADO",
                ).toUpperCase() === "DETERMINADO" ? (
                  <div className="space-y-1">
                    <label className={labelBaseStyle}>
                      Data final da validade
                    </label>
                    <input
                      type="text"
                      className={inputBaseStyle}
                      placeholder="dd/mm/aaaa"
                      maxLength={10}
                      value={formData?.validadeMandatoData || ""}
                      onChange={(e) => {
                        const raw = e.target.value;
                        // Permite apenas dígitos e barras (formato dd/mm/aaaa)
                        const filtered = raw.replace(/[^0-9/]/g, "");
                        setFormData((prev) => ({
                          ...(prev || {}),
                          validadeMandatoData: filtered,
                        }));
                        setIsModified(true);
                      }}
                    />
                  </div>
                ) : null}

                {String(
                  formData?.validadeMandatoTipo || "INDETERMINADO",
                ).toUpperCase() === "ATO_ESPECIFICO" ? (
                  <div className="space-y-1">
                    <label className={labelBaseStyle}>
                      Descrição do ato específico
                    </label>
                    <textarea
                      className={inputBaseStyle + " resize-none min-h-[110px]"}
                      value={formData?.validadeMandatoAto || ""}
                      onChange={(e) => {
                        setFormData((prev) => ({
                          ...(prev || {}),
                          validadeMandatoAto: e.target.value,
                        }));
                        setIsModified(true);
                      }}
                    />
                  </div>
                ) : null}
              </div>
            </ConfigSection>

            <ConfigSection
              id="vincular"
              title="Vincular a Processo"
              icon="fa-link"
              openSection={openSection}
              setOpenSection={setOpenSection}
            >
              {parties?.[0]?.clientId || parties?.[0]?.qualificationData?.id ? (
                <div className="space-y-1">
                  <CadernoSelector
                    clientId={String(
                      parties?.[0]?.clientId ||
                        parties?.[0]?.qualificationData?.id ||
                        "",
                    ).trim()}
                    clientName={String(
                      parties?.[0]?.name ||
                        parties?.[0]?.qualificationData?.name ||
                        "",
                    ).trim()}
                    value={preFlightState.cadernoId}
                    onChange={(id, title) =>
                      setPreFlightState((prev) => ({
                        ...prev,
                        cadernoId: id,
                        cadernoTitle: title,
                      }))
                    }
                  />
                  <p className="text-[9px] text-gray-400 mt-0.5">
                    A procuração será vinculada ao caderno selecionado e
                    aparecerá no portal do cliente.
                  </p>
                </div>
              ) : (
                <p className="text-[10px] text-gray-400 italic">
                  Importe um cliente do CRM para vincular a um processo.
                </p>
              )}
            </ConfigSection>
          </div>
        </div>

        <div className="relative z-[260] p-3 border-t border-gray-200 bg-white shrink-0 space-y-2">
          <div className="grid grid-cols-1 gap-2 no-print">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <button
                onClick={() => openPreFlightForProcuration("simple", "html")}
                className="px-2.5 py-1.5 rounded-lg font-semibold text-[11px] whitespace-nowrap border border-violet-200 bg-white text-violet-700 hover:bg-violet-50 transition-colors flex items-center justify-center gap-1.5 leading-none shadow-sm"
                title="Salva a procuração em HTML no prontuário interno para edição futura (não envia ao cliente)"
                type="button"
                aria-disabled={isPublishing}
              >
                <i className="fas fa-save"></i>{" "}
                {isPublishing ? "Salvando..." : "Salvar HTML"}
              </button>

              <button
                onClick={() => {
                  if (!editingPublicationMeta?.documentId) {
                    setDialogState({
                      open: true,
                      title: "Salve o HTML primeiro",
                      message:
                        'A procuração precisa ser salva em HTML antes de ser publicada em PDF. Clique em "Salvar HTML", vincule ao caderno de autos e então publique o PDF.',
                      mode: "info",
                      onConfirm: null,
                    });
                    return;
                  }
                  openPreFlightForProcuration("simple", "pdf");
                }}
                className={`px-2.5 py-1.5 rounded-lg font-semibold text-[11px] whitespace-nowrap border flex items-center justify-center gap-1.5 leading-none shadow-sm transition-colors ${
                  editingPublicationMeta?.documentId
                    ? "border-rose-200 bg-white text-rose-700 hover:bg-rose-50"
                    : "border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed"
                }`}
                title={
                  editingPublicationMeta?.documentId
                    ? "Publica a procuração em PDF no acervo do cliente e registra no prontuário"
                    : "Salve o HTML primeiro para habilitar a publicação em PDF"
                }
                type="button"
                aria-disabled={
                  isPublishing || !editingPublicationMeta?.documentId
                }
              >
                <i className="fas fa-file-pdf"></i>{" "}
                {isPublishing ? "Publicando..." : "Publicar PDF"}
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 items-stretch">
              <div className="relative z-[320]">
                <button
                  ref={advancedMenuAnchorRef}
                  onClick={() => setShowAdvancedActions((prev) => !prev)}
                  className="w-full px-2.5 py-1.5 rounded-lg font-semibold text-[11px] whitespace-nowrap border border-gray-200 bg-gray-50 text-gray-700 hover:bg-gray-100 transition-colors flex items-center justify-center gap-1.5 leading-none shadow-sm"
                  type="button"
                  aria-expanded={showAdvancedActions}
                >
                  <i className="fas fa-cog"></i> Avançado
                  <ChevronDown
                    className={`w-3.5 h-3.5 transition-transform ${showAdvancedActions ? "rotate-180" : ""}`}
                  />
                </button>

                {showAdvancedActions
                  ? createPortal(
                      <div
                        ref={advancedMenuRef}
                        className="fixed w-64 bg-white border border-gray-200 shadow-2xl rounded-lg overflow-hidden z-[1200] flex flex-col"
                        style={{
                          top: advancedMenuPosition.top,
                          left: advancedMenuPosition.left,
                          transform: "translateY(-100%)",
                        }}
                      >
                        <button
                          onClick={() => {
                            setShowAdvancedActions(false);
                            handleResetDraft();
                          }}
                          className={`text-left px-4 py-3 text-xs font-bold w-full transition-colors ${isModified ? "text-orange-700 bg-orange-50 hover:bg-orange-100" : "text-gray-700 hover:bg-gray-50"}`}
                          title="Limpar Draft (com confirmação)"
                          type="button"
                        >
                          Limpar Draft
                        </button>
                        <button
                          onClick={() => {
                            setShowAdvancedActions(false);
                            handleDownloadBackup();
                          }}
                          className="text-left px-4 py-3 hover:bg-emerald-50 text-xs font-bold text-emerald-700 w-full transition-colors"
                          title="Baixar uma cópia de segurança da procuração para o seu computador"
                          type="button"
                        >
                          Backup Seguro (JSON)
                        </button>
                        <button
                          onClick={() => {
                            setShowAdvancedActions(false);
                            handleRestoreBackup();
                          }}
                          className="text-left px-4 py-3 hover:bg-amber-50 text-xs font-bold text-amber-700 w-full transition-colors"
                          title="Restaurar procuração a partir de um arquivo JSON salvo"
                          type="button"
                        >
                          Restaurar Backup
                        </button>
                        <button
                          onClick={() => {
                            setShowAdvancedActions(false);
                            handleRelinkFromCrm();
                          }}
                          className="text-left px-4 py-3 hover:bg-sky-50 text-xs font-bold text-sky-700 w-full transition-colors"
                          title="Recarrega do CRM os vínculos atuais e sobrescreve snapshots antigos da procuração"
                          type="button"
                        >
                          Revincular do CRM
                        </button>
                        <button
                          onClick={() => {
                            setShowAdvancedActions(false);
                            void handleForceSyncCloud();
                          }}
                          className="text-left px-4 py-3 hover:bg-green-50 text-xs font-bold text-green-700 w-full transition-colors border-b border-green-100"
                          title="Limpa o cache local e força o download do template atualizado da nuvem"
                          type="button"
                        >
                          ☁️ Sincronizar com a Nuvem
                        </button>
                        <button
                          onClick={() => {
                            setShowAdvancedActions(false);
                            handleDownloadCloudMaster();
                          }}
                          className="text-left px-4 py-3 hover:bg-sky-50 text-xs font-bold text-sky-700 w-full transition-colors"
                          title="Baixar o mestre atualmente salvo na nuvem como JSON, sem publicar nada"
                          type="button"
                        >
                          Baixar Mestre da Nuvem
                        </button>
                        <button
                          onClick={() => {
                            setShowAdvancedActions(false);
                            handleSaveAsMaster();
                          }}
                          className="text-left px-4 py-3 hover:bg-rose-50 text-xs font-bold text-rose-700 w-full transition-colors"
                          title="Sobrescreve o mestre de procurações na nuvem com a versão atual"
                          type="button"
                          aria-disabled={isSaving}
                        >
                          Publicar Mestre
                        </button>
                        <button
                          onClick={() => {
                            setShowAdvancedActions(false);
                            handleImportMD();
                          }}
                          className="text-left px-4 py-3 hover:bg-emerald-50 text-xs font-bold text-emerald-700 w-full transition-colors"
                          type="button"
                        >
                          Upload da Revisão (.MD)
                        </button>
                        <button
                          onClick={() => {
                            setShowAdvancedActions(false);
                            handleExportMD();
                          }}
                          className="text-left px-4 py-3 hover:bg-indigo-50 text-xs font-bold text-indigo-700 w-full transition-colors"
                          type="button"
                        >
                          Baixar para Revisão (.MD)
                        </button>
                        {editingPublicationMeta?.documentId ? (
                          <button
                            onClick={() => {
                              setShowAdvancedActions(false);
                              handleDeletePublication();
                            }}
                            className="text-left px-4 py-3 hover:bg-red-50 text-xs font-bold text-red-600 w-full transition-colors border-t border-red-200"
                            title={`Excluir permanentemente a publicação ${editingPublicationMeta.documentId} do Firestore e do prontuário do cliente`}
                            type="button"
                          >
                            <i className="fas fa-trash-alt mr-1.5"></i> Excluir
                            Publicação do Acervo
                          </button>
                        ) : null}
                      </div>,
                      document.body,
                    )
                  : null}
              </div>

              <button
                onClick={handlePrint}
                className="w-full px-2.5 py-1.5 rounded-lg font-semibold text-[11px] whitespace-nowrap shadow-sm flex justify-center gap-1.5 transition-colors items-center no-print border bg-[#20243B] text-white border-[#20243B] hover:brightness-95 leading-none"
                type="button"
              >
                <i className="fas fa-print"></i>
                PDF / Imprimir
              </button>
            </div>

            <div className="flex items-center justify-between gap-2 text-[9px] font-semibold uppercase tracking-[0.04em] text-gray-500 border-t border-gray-100 pt-1.5">
              <span>Sincronização automática</span>
              <span
                className={`px-2 py-1 rounded-full border whitespace-nowrap ${cloudSyncState === "saving" ? "border-sky-200 bg-sky-50 text-sky-700" : cloudSyncState === "saved" ? "border-emerald-200 bg-emerald-50 text-emerald-700" : cloudSyncState === "error" ? "border-red-200 bg-red-50 text-red-700" : "border-gray-200 bg-gray-50 text-gray-600"}`}
              >
                {cloudSyncState === "saving"
                  ? "Salvando..."
                  : cloudSyncState === "saved"
                    ? `Nuvem OK${cloudSyncMeta?.updatedAt ? ` · ${new Date(cloudSyncMeta.updatedAt).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}` : ""}`
                    : cloudSyncState === "error"
                      ? "Falha no auto-save"
                      : "Aguardando alterações"}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-auto relative p-4 md:p-8 bg-gray-100 min-w-0 min-h-0 print:p-0 print:overflow-visible print:bg-white">
        <div className="contract-print-area">
          <div className="screen-only-view p-8 bg-gray-100 flex justify-center">
            <div className="screen-paper">
              <div className="text-center mb-8 relative">
                <h1 className="contract-title">
                  PROCURAÇÃO <span className="italic">AD JUDICIA ET EXTRA</span>
                </h1>
              </div>

              <div className="document-content space-y-6">
                <div id="guia-rapido" className="quadro-resumo-section mb-8">
                  <h2 className="text-center font-bold text-sm mb-4 uppercase relative">
                    Guia Rápido do Documento
                  </h2>
                  <div className="quadro-resumo-grid">
                    {getQuickNavBlocks().map((b, idx) => {
                      const aid = getAnchorIdForBlock(b);
                      if (!aid) return null;
                      const meta = getQuickNavMeta(b);
                      const safeId = String(b?.id || "").replace(
                        /[^a-zA-Z0-9_\-]/g,
                        "_",
                      );
                      return (
                        <a
                          key={b.id}
                          href={`#${aid}`}
                          onClick={(e) => {
                            e.preventDefault();
                            scrollToBlock(b.id);
                          }}
                          className="quadro-resumo-card group relative"
                        >
                          <span
                            className="ghost-marker"
                            style={{ top: 0, left: 0 }}
                            aria-hidden="true"
                          >{`RDA_TL_BTN_${safeId}`}</span>
                          <span
                            className="ghost-marker"
                            style={{ bottom: 0, right: 0 }}
                            aria-hidden="true"
                          >{`RDA_BR_BTN_${safeId}`}</span>
                          <span
                            className="quadro-resumo-icon"
                            aria-hidden="true"
                          >
                            <i className={meta.icon} aria-hidden="true" />
                          </span>
                          <div className="quadro-resumo-content">
                            <h3 className="quadro-resumo-title-group">
                              <span className="quadro-resumo-clause-number">
                                Cláusula {idx + 1}ª
                              </span>
                              <span
                                className="quadro-resumo-label"
                                title={meta.label.replace(/<[^>]*>/g, "")}
                                dangerouslySetInnerHTML={{ __html: meta.label }}
                              />
                            </h3>
                          </div>
                          {meta.badge ? (
                            <span className="quadro-resumo-badge">
                              {meta.badge}
                            </span>
                          ) : null}
                        </a>
                      );
                    })}
                  </div>
                </div>

                {(() => {
                  return (templateData?.structure || [])
                    .filter((b) =>
                      mode === "auditoria" ? true : isBlockVisible(b),
                    )
                    .map((block) => {
                      const isPreambulo = isPreambuloBlock(block);
                      const n = getBlockNumberById(block?.id);
                      const label = stripNumericPrefix(
                        block?.title || "",
                      ).toUpperCase();

                      if (editingBlockId === block.id) {
                        const hasChildren =
                          Array.isArray((activeBlockData || block)?.children) &&
                          (activeBlockData || block)?.children?.length > 0;
                        const childNumbers = hasChildren
                          ? computeHierNumbers(
                              (activeBlockData || block)?.children || [],
                              n,
                            )
                          : [];

                        return (
                          <div
                            key={block.id}
                            className="clause-block mb-2"
                            style={{
                              "--indent-level": `${(block.level || 0) * 24}px`,
                              borderLeft: "none",
                              paddingLeft: block.level ? "1rem" : "0.5rem",
                            }}
                          >
                            <div
                              id={`proc-block-${block.id}`}
                              className="clause-block"
                            >
                              <div className="bg-white border border-[#C5A059]/40 rounded-xl shadow-lg overflow-hidden no-print">
                                <div className="flex items-center justify-between gap-3 px-4 py-3 bg-[#20243B] text-white">
                                  <div className="min-w-0">
                                    <div className="text-[10px] font-extrabold uppercase tracking-widest text-white/80">
                                      Editando{" "}
                                      {isPreambulo
                                        ? "Preâmbulo"
                                        : n
                                          ? `Cláusula ${n}ª`
                                          : "Cláusula"}
                                    </div>
                                    <div className="text-xs font-bold truncate">
                                      {label || block?.title || "Bloco"}
                                    </div>
                                  </div>
                                  <button
                                    onClick={() => {
                                      setEditingBlockId(null);
                                      setActiveBlockData(null);
                                    }}
                                    className="text-white/70 hover:text-white"
                                    type="button"
                                    title="Fechar"
                                  >
                                    <X className="w-4 h-4" />
                                  </button>
                                </div>

                                <div className="p-4">
                                  <div className="space-y-1 mb-3">
                                    <label className={labelBaseStyle}>
                                      Título
                                    </label>
                                    <div className="flex items-center gap-1 border border-gray-200 rounded-t-lg bg-gray-50 px-2 py-1">
                                      <button
                                        type="button"
                                        className="px-2 py-0.5 text-xs font-bold bg-white border border-gray-300 rounded hover:bg-gray-100"
                                        onClick={() => {
                                          const sel = window.getSelection();
                                          if (!sel.rangeCount) return;
                                          const editor =
                                            document.getElementById(
                                              "title-editor",
                                            );
                                          if (
                                            !editor ||
                                            !editor.contains(
                                              sel.getRangeAt(0)
                                                .commonAncestorContainer,
                                            )
                                          )
                                            return;
                                          document.execCommand(
                                            "bold",
                                            false,
                                            null,
                                          );
                                          editor.focus();
                                          setTempTitle(editor.innerHTML);
                                          setIsModified(true);
                                        }}
                                      >
                                        <strong>B</strong>
                                      </button>
                                      <button
                                        type="button"
                                        className="px-2 py-0.5 text-xs italic font-bold bg-white border border-gray-300 rounded hover:bg-gray-100"
                                        onClick={() => {
                                          const sel = window.getSelection();
                                          if (!sel.rangeCount) return;
                                          const editor =
                                            document.getElementById(
                                              "title-editor",
                                            );
                                          if (
                                            !editor ||
                                            !editor.contains(
                                              sel.getRangeAt(0)
                                                .commonAncestorContainer,
                                            )
                                          )
                                            return;
                                          document.execCommand(
                                            "italic",
                                            false,
                                            null,
                                          );
                                          editor.focus();
                                          setTempTitle(editor.innerHTML);
                                          setIsModified(true);
                                        }}
                                      >
                                        <em>I</em>
                                      </button>
                                      <span className="text-[9px] text-gray-400 ml-auto">
                                        Negrito e itálico
                                      </span>
                                    </div>
                                    <div
                                      id="title-editor"
                                      contentEditable
                                      suppressContentEditableWarning
                                      dir="ltr"
                                      style={{
                                        direction: "ltr",
                                        textAlign: "left",
                                        whiteSpace: "pre-wrap",
                                      }}
                                      className="font-bold text-brand-blue text-sm bg-transparent border border-t-0 border-gray-300 w-full focus:outline-none focus:border-highlight-action px-3 py-2 min-h-[32px] rounded-b-lg"
                                      dangerouslySetInnerHTML={{
                                        __html: tempTitle,
                                      }}
                                      onBlur={(e) => {
                                        const html = e.currentTarget.innerHTML;
                                        const val =
                                          html === "<br>" || html.trim() === ""
                                            ? ""
                                            : html;
                                        setTempTitle(val);
                                        setIsModified(true);
                                      }}
                                    />
                                  </div>

                                  <RulesPanel
                                    blockData={activeBlockData || block}
                                    onChange={(next) => {
                                      setActiveBlockData((prev) => ({
                                        ...(prev || block),
                                        ...(next || {}),
                                      }));
                                      setIsModified(true);
                                    }}
                                    taxonomy={TAXONOMY}
                                  />

                                  {hasChildren ? (
                                    <div className="p-4 bg-gray-50 flex flex-col gap-4 rounded-xl border border-gray-200">
                                      <div className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-2 border-b border-gray-200 pb-2 flex justify-between items-center">
                                        <span>
                                          Sub-cláusulas (Itens Dinâmicos)
                                        </span>
                                        <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full text-[9px] font-extrabold shadow-sm">
                                          {(activeBlockData || block)?.children
                                            ?.length || 0}{" "}
                                          itens
                                        </span>
                                      </div>

                                      {(
                                        activeBlockData || block
                                      )?.children?.map((child, cIdx) => (
                                        <div
                                          key={child.id || cIdx}
                                          className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden transition-all hover:border-blue-300 hover:shadow-md"
                                          style={{
                                            marginLeft: `${(Number(child?.level || 0) || 0) * 24}px`,
                                            borderLeft: "none",
                                          }}
                                        >
                                          <div className="bg-gray-100 p-2 flex justify-between items-center border-b border-gray-200">
                                            <span className="text-[10px] font-bold text-gray-600 tracking-wider">
                                              {childNumbers[cIdx]
                                                ? childNumbers[cIdx]
                                                : `ITEM ${cIdx + 1}`}
                                            </span>
                                            <div className="flex items-center gap-1">
                                              <button
                                                onMouseDown={(e) => {
                                                  e.preventDefault();
                                                  e.stopPropagation();
                                                }}
                                                onClick={(e) => {
                                                  e.preventDefault();
                                                  e.stopPropagation();
                                                  addSubClauseAfter(
                                                    child.id,
                                                    cIdx,
                                                  );
                                                }}
                                                className="text-gray-400 hover:text-green-600 hover:bg-green-50 p-1.5 rounded transition-colors"
                                                title="Inserir sub-cláusula abaixo"
                                                type="button"
                                              >
                                                <Plus className="w-3 h-3" />
                                              </button>
                                              <button
                                                onClick={() => {
                                                  setActiveBlockData((prev) => {
                                                    if (
                                                      !prev ||
                                                      !Array.isArray(
                                                        prev.children,
                                                      )
                                                    )
                                                      return prev;
                                                    const newChildren = [
                                                      ...prev.children,
                                                    ];
                                                    newChildren.splice(cIdx, 1);
                                                    return {
                                                      ...prev,
                                                      children: newChildren,
                                                    };
                                                  });
                                                  setIsModified(true);
                                                }}
                                                className="text-red-400 hover:text-red-600 hover:bg-red-50 p-1.5 rounded transition-colors"
                                                title="Remover Item"
                                                type="button"
                                              >
                                                <Trash2 className="w-3 h-3" />
                                              </button>
                                            </div>
                                          </div>

                                          <div className="p-3 flex flex-col gap-3">
                                            <RulesPanel
                                              blockData={child}
                                              onChange={(newChildData) => {
                                                setActiveBlockData((prev) => {
                                                  if (
                                                    !prev ||
                                                    !Array.isArray(
                                                      prev.children,
                                                    )
                                                  )
                                                    return prev;
                                                  const newChildren = [
                                                    ...prev.children,
                                                  ];
                                                  newChildren[cIdx] =
                                                    newChildData;
                                                  return {
                                                    ...prev,
                                                    children: newChildren,
                                                  };
                                                });
                                                setIsModified(true);
                                              }}
                                              taxonomy={TAXONOMY}
                                            />

                                            <div className="flex flex-col gap-2 pb-3 border-b border-gray-100 select-none">
                                              <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:gap-3">
                                                <span className="shrink-0 text-[9px] font-bold text-gray-400 uppercase sm:w-20 sm:text-right sm:pt-1">
                                                  Outorgante:
                                                </span>
                                                <div className="min-w-0 flex-1 flex flex-wrap gap-1.5">
                                                  {[
                                                    [
                                                      "Nome",
                                                      "[[NOME_CLIENTE]]",
                                                    ],
                                                    [
                                                      "CPF/CNPJ",
                                                      "[[CPF_CLIENTE]]",
                                                    ],
                                                    [
                                                      "Qualificação",
                                                      "[[QUALIFICACAO_CONTRATANTE]]",
                                                    ],
                                                    [
                                                      "E-mail",
                                                      "[[EMAIL_CLIENTE]]",
                                                    ],
                                                  ].map(([lbl, val]) => (
                                                    <button
                                                      key={val}
                                                      type="button"
                                                      onClick={() => {
                                                        setActiveBlockData(
                                                          (prev) => {
                                                            if (
                                                              !prev ||
                                                              !Array.isArray(
                                                                prev.children,
                                                              )
                                                            )
                                                              return prev;
                                                            const newChildren =
                                                              [
                                                                ...prev.children,
                                                              ];
                                                            const curr =
                                                              newChildren[
                                                                cIdx
                                                              ] || {};
                                                            newChildren[cIdx] =
                                                              {
                                                                ...curr,
                                                                content:
                                                                  String(
                                                                    curr?.content ||
                                                                      "",
                                                                  ) + val,
                                                              };
                                                            return {
                                                              ...prev,
                                                              children:
                                                                newChildren,
                                                            };
                                                          },
                                                        );
                                                        setIsModified(true);
                                                      }}
                                                      className="text-[9px] font-bold bg-blue-50 text-brand-blue border border-blue-200 px-2 py-0.5 rounded hover:bg-blue-100 transition-colors"
                                                    >
                                                      {lbl}
                                                    </button>
                                                  ))}
                                                </div>
                                              </div>

                                              <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:gap-3">
                                                <span className="shrink-0 text-[9px] font-bold text-gray-400 uppercase sm:w-20 sm:text-right sm:pt-1">
                                                  Procuração:
                                                </span>
                                                <div className="min-w-0 flex-1 flex flex-wrap gap-1.5">
                                                  {[
                                                    [
                                                      "Objeto",
                                                      "[[OBJETO_PRINCIPAL]]",
                                                    ],
                                                    [
                                                      "Ramo",
                                                      "[[RAMO_DIREITO]]",
                                                    ],
                                                    [
                                                      "Posição",
                                                      "[[POSICAO_CLIENTE]]",
                                                    ],
                                                    [
                                                      "Outorgada",
                                                      "[[QUALIFICACAO_CONTRATADO]]",
                                                    ],
                                                    [
                                                      "Validade",
                                                      "[[VALIDADE_MANDATO]]",
                                                    ],
                                                  ].map(([lbl, val]) => (
                                                    <button
                                                      key={val}
                                                      type="button"
                                                      onClick={() => {
                                                        setActiveBlockData(
                                                          (prev) => {
                                                            if (
                                                              !prev ||
                                                              !Array.isArray(
                                                                prev.children,
                                                              )
                                                            )
                                                              return prev;
                                                            const newChildren =
                                                              [
                                                                ...prev.children,
                                                              ];
                                                            const curr =
                                                              newChildren[
                                                                cIdx
                                                              ] || {};
                                                            newChildren[cIdx] =
                                                              {
                                                                ...curr,
                                                                content:
                                                                  String(
                                                                    curr?.content ||
                                                                      "",
                                                                  ) + val,
                                                              };
                                                            return {
                                                              ...prev,
                                                              children:
                                                                newChildren,
                                                            };
                                                          },
                                                        );
                                                        setIsModified(true);
                                                      }}
                                                      className="text-[9px] font-bold bg-orange-50 text-orange-700 border border-orange-200 px-2 py-0.5 rounded hover:bg-orange-100 transition-colors"
                                                    >
                                                      {lbl}
                                                    </button>
                                                  ))}
                                                </div>
                                              </div>
                                            </div>

                                            <ContractEditor
                                              key={child.id || cIdx}
                                              compact
                                              hideActions
                                              initialData={{
                                                ...(child || {}),
                                                type: "text",
                                                content: String(
                                                  child?.content || "",
                                                ),
                                              }}
                                              onSave={() => {}}
                                              onDraftChange={(draft) => {
                                                const nextContent = String(
                                                  draft?.content || "",
                                                );
                                                setActiveBlockData((prev) => {
                                                  if (
                                                    !prev ||
                                                    !Array.isArray(
                                                      prev.children,
                                                    )
                                                  )
                                                    return prev;
                                                  const newChildren = [
                                                    ...prev.children,
                                                  ];
                                                  const curr =
                                                    newChildren[cIdx] || {};
                                                  newChildren[cIdx] = {
                                                    ...curr,
                                                    content: nextContent,
                                                  };
                                                  return {
                                                    ...prev,
                                                    children: newChildren,
                                                  };
                                                });
                                                setIsModified(true);
                                              }}
                                              isSaving={false}
                                              onCancel={null}
                                              structure={
                                                templateData?.structure || []
                                              }
                                            />
                                          </div>
                                        </div>
                                      ))}

                                      <button
                                        onClick={() => {
                                          setActiveBlockData((prev) => {
                                            if (!prev) return prev;
                                            const children = Array.isArray(
                                              prev.children,
                                            )
                                              ? [...prev.children]
                                              : [];
                                            children.push({
                                              id: `sub_${Date.now()}`,
                                              type: "sub_clause",
                                              content: "",
                                              condition: "ALWAYS",
                                              requiredHonorarios: [],
                                            });
                                            return { ...prev, children };
                                          });
                                          setIsModified(true);
                                        }}
                                        className="w-full py-3 border-2 border-dashed border-gray-300 rounded-xl text-gray-500 font-bold text-xs uppercase tracking-wider hover:bg-blue-50 hover:border-brand-blue hover:text-brand-blue transition-all flex items-center justify-center gap-2 mt-2"
                                        type="button"
                                      >
                                        <Plus className="w-4 h-4" /> Adicionar
                                        Novo Item
                                      </button>

                                      <div className="mt-2 pt-4 border-t border-gray-200">
                                        <SaveCancelBar
                                          onCancel={() => {
                                            setEditingBlockId(null);
                                            setActiveBlockData(null);
                                          }}
                                          onSave={() => {
                                            saveEditorContent(activeBlockData);
                                            setActiveBlockData(null);
                                          }}
                                          saving={false}
                                          saveLabel="Salvar Cláusula"
                                          cancelLabel="Cancelar"
                                        />
                                      </div>
                                    </div>
                                  ) : (
                                    <>
                                      <div className="flex justify-end mb-3">
                                        <button
                                          type="button"
                                          onClick={() => {
                                            setActiveBlockData((prev) => {
                                              const base =
                                                prev ||
                                                activeBlockData ||
                                                block;
                                              const firstContent = String(
                                                base?.content || "",
                                              );
                                              const newChild = {
                                                id: `sub_${Date.now()}`,
                                                type: "sub_clause",
                                                content: firstContent,
                                                condition: "ALWAYS",
                                                requiredHonorarios: [],
                                              };
                                              return {
                                                ...(base || {}),
                                                type: "sub_clause_group",
                                                content: "",
                                                items: null,
                                                children: [newChild],
                                              };
                                            });
                                            setIsModified(true);
                                          }}
                                          className="px-3 py-1.5 rounded bg-blue-50 text-brand-blue border border-blue-200 text-[10px] font-extrabold uppercase tracking-wider hover:bg-blue-100"
                                          title="Converte o texto desta cláusula em itens dinâmicos (sub-cláusulas)"
                                        >
                                          Ativar Itens Dinâmicos
                                        </button>
                                      </div>

                                      <div className="flex flex-col gap-2 pb-3 border-b border-gray-100 select-none">
                                        <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:gap-3">
                                          <span className="shrink-0 text-[9px] font-bold text-gray-400 uppercase sm:w-20 sm:text-right sm:pt-1">
                                            Outorgante:
                                          </span>
                                          <div className="min-w-0 flex-1 flex flex-wrap gap-1.5">
                                            {[
                                              ["Nome", "[[NOME_CLIENTE]]"],
                                              ["CPF/CNPJ", "[[CPF_CLIENTE]]"],
                                              ["E-mail", "[[EMAIL_CLIENTE]]"],
                                              [
                                                "Qualificação",
                                                "[[QUALIFICACAO_CONTRATANTE]]",
                                              ],
                                            ].map(([lbl, val]) => (
                                              <button
                                                key={val}
                                                type="button"
                                                onClick={() => {
                                                  setActiveBlockData(
                                                    (prev) => ({
                                                      ...(prev ||
                                                        activeBlockData ||
                                                        block),
                                                      content:
                                                        String(
                                                          (prev?.content ??
                                                            activeBlockData?.content ??
                                                            block?.content) ||
                                                            "",
                                                        ) + val,
                                                    }),
                                                  );
                                                  setIsModified(true);
                                                }}
                                                className="text-[9px] font-bold bg-blue-50 text-brand-blue border border-blue-200 px-2 py-0.5 rounded hover:bg-blue-100 transition-colors"
                                              >
                                                {lbl}
                                              </button>
                                            ))}
                                          </div>
                                        </div>
                                        <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:gap-3">
                                          <span className="shrink-0 text-[9px] font-bold text-gray-400 uppercase sm:w-20 sm:text-right sm:pt-1">
                                            Procuração:
                                          </span>
                                          <div className="min-w-0 flex-1 flex flex-wrap gap-1.5">
                                            {[
                                              [
                                                "Objeto",
                                                "[[OBJETO_PRINCIPAL]]",
                                              ],
                                              ["Ramo", "[[RAMO_DIREITO]]"],
                                              [
                                                "Posição",
                                                "[[POSICAO_CLIENTE]]",
                                              ],
                                              [
                                                "Outorgada",
                                                "[[QUALIFICACAO_CONTRATADO]]",
                                              ],
                                              [
                                                "Validade",
                                                "[[VALIDADE_MANDATO]]",
                                              ],
                                            ].map(([lbl, val]) => (
                                              <button
                                                key={val}
                                                type="button"
                                                onClick={() => {
                                                  setActiveBlockData(
                                                    (prev) => ({
                                                      ...(prev ||
                                                        activeBlockData ||
                                                        block),
                                                      content:
                                                        String(
                                                          (prev?.content ??
                                                            activeBlockData?.content ??
                                                            block?.content) ||
                                                            "",
                                                        ) + val,
                                                    }),
                                                  );
                                                  setIsModified(true);
                                                }}
                                                className="text-[9px] font-bold bg-orange-50 text-orange-700 border border-orange-200 px-2 py-0.5 rounded hover:bg-orange-100 transition-colors"
                                              >
                                                {lbl}
                                              </button>
                                            ))}
                                          </div>
                                        </div>
                                      </div>

                                      <div className="h-[520px]">
                                        <ContractEditor
                                          initialData={activeBlockData || block}
                                          onSave={saveEditorContent}
                                          isSaving={false}
                                          onCancel={() => {
                                            setEditingBlockId(null);
                                            setActiveBlockData(null);
                                          }}
                                          structure={
                                            templateData?.structure || []
                                          }
                                          onDraftChange={(draft) => {
                                            setActiveBlockData((prev) => ({
                                              ...(prev || block),
                                              ...(draft || {}),
                                            }));
                                            setIsModified(true);
                                          }}
                                        />
                                      </div>
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      }

                      if (isPreambulo) {
                        return (
                          <div
                            key={block.id}
                            className="clause-block mb-2"
                            style={{
                              "--indent-level": `${(block.level || 0) * 24}px`,
                              borderLeft: "none",
                              paddingLeft: block.level ? "1rem" : "0.5rem",
                            }}
                          >
                            <div
                              id={`proc-block-${block.id}`}
                              className="clause-block"
                            >
                              <h2 className="clause-heading preamble-heading relative">
                                <span className="clause-heading-row w-full flex items-start flex-wrap gap-1">
                                  <span className="clause-title">
                                    PREÂMBULO
                                  </span>
                                  <span className="ml-auto flex items-center gap-1 relative z-10">
                                    {mode === "auditoria" ? (
                                      <button
                                        onMouseDown={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                        }}
                                        onClick={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          startEditing(block);
                                        }}
                                        className="text-brand-blue hover:bg-blue-50 p-1 rounded no-print"
                                        type="button"
                                        title="Editar"
                                      >
                                        <Edit3 className="w-3 h-3" />
                                      </button>
                                    ) : null}

                                    <a
                                      href="#guia-rapido"
                                      className="text-[9px] font-bold uppercase bg-gray-100 text-gray-500 px-2 py-1 rounded hover:bg-gray-200 transition-colors no-underline relative inline-block overflow-hidden"
                                      title="Voltar ao topo"
                                    >
                                      ↑ TOPO
                                    </a>
                                  </span>
                                </span>
                              </h2>
                              <div
                                className="contract-p clause-body"
                                dangerouslySetInnerHTML={{
                                  __html: sanitizeScreenHtml(
                                    parseText(block.content || ""),
                                  ),
                                }}
                              />
                            </div>
                          </div>
                        );
                      }

                      const visibleInDocument = isBlockVisible(block);

                      if (mode === "auditoria" && !visibleInDocument) {
                        return (
                          <div
                            key={block.id}
                            id={`proc-block-${block.id}`}
                            className="mb-2 no-print"
                          >
                            <div className="flex items-center justify-between p-2 bg-gray-100 border border-gray-300 border-dashed rounded">
                              <div className="flex items-center gap-3">
                                <EyeOff className="w-4 h-4 text-gray-400" />
                                <span className="text-xs font-bold text-gray-500 line-through">
                                  {label}
                                </span>
                              </div>
                              <div className="flex items-center gap-1">
                                <button
                                  onMouseDown={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                  }}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    addClauseAfter(block.id);
                                  }}
                                  className="text-gray-400 hover:text-green-600 hover:bg-green-50 p-1 rounded no-print"
                                  type="button"
                                  title="Inserir cláusula abaixo"
                                >
                                  <Plus className="w-3 h-3" />
                                </button>
                                <button
                                  onMouseDown={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                  }}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    startEditing(block);
                                  }}
                                  className="text-brand-blue hover:bg-blue-50 p-1 rounded no-print"
                                  type="button"
                                  title="Editar"
                                >
                                  <Edit3 className="w-3 h-3" />
                                </button>
                                <button
                                  onMouseDown={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                  }}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    deleteBlock(block.id);
                                  }}
                                  className="text-gray-400 hover:text-red-500 hover:bg-red-50 p-1 rounded no-print"
                                  type="button"
                                  title="Excluir"
                                >
                                  <Trash2 className="w-3 h-3" />
                                </button>
                              </div>
                            </div>
                            <div className="mt-2 p-3 border border-dashed border-gray-300 bg-gray-50 opacity-60 rounded-lg no-print relative">
                              <div className="flex items-center gap-2 mb-1">
                                <EyeOff className="w-3 h-3 text-red-400" />
                                <span className="text-[9px] font-bold text-red-500 uppercase tracking-widest">
                                  Item oculto pela lógica (regra não atendida)
                                </span>
                              </div>
                              {Array.isArray(block?.children) &&
                              block.children.length ? (
                                <div className="clause-body mt-2">
                                  {block.children.map((child, cIdx) => (
                                    <div
                                      key={child?.id || cIdx}
                                      className="mb-2"
                                      style={{
                                        paddingLeft: `${normalizeClauseLevel(child?.level) * 24}px`,
                                      }}
                                    >
                                      <span
                                        className="text-sm"
                                        dangerouslySetInnerHTML={{
                                          __html: sanitizeScreenHtml(
                                            parseText(child?.content || ""),
                                          ),
                                        }}
                                      />
                                    </div>
                                  ))}
                                </div>
                              ) : block.type === "list" &&
                                Array.isArray(block.items) ? (
                                <ol className="first-level-list clause-body">
                                  {block.items.map((item, idx) => (
                                    <li
                                      key={idx}
                                      dangerouslySetInnerHTML={{
                                        __html: sanitizeScreenHtml(
                                          parseText(item),
                                        ),
                                      }}
                                    />
                                  ))}
                                </ol>
                              ) : (
                                <div
                                  className="text-sm"
                                  dangerouslySetInnerHTML={{
                                    __html: sanitizeScreenHtml(
                                      parseText(block.content || ""),
                                    ),
                                  }}
                                />
                              )}
                            </div>
                          </div>
                        );
                      }

                      return (
                        <div
                          key={block.id}
                          id={`proc-block-${block.id}`}
                          className="clause-block group relative transition-all duration-200 hover:bg-blue-50/30 rounded-lg"
                          style={{
                            ...(typeof n === "number"
                              ? { "--macro-num": n }
                              : {}),
                            "--indent-level": `${(block.level || 0) * 24}px`,
                            borderLeft: "none",
                            paddingLeft: block.level ? "1rem" : "0.5rem",
                          }}
                        >
                          <h2
                            id={getAnchorIdForBlock(block) || undefined}
                            className="clause-heading relative"
                          >
                            <span className="clause-heading-row w-full flex items-start flex-wrap gap-1">
                              {n ? (
                                <span className="clause-number">
                                  Cláusula {n}ª –
                                </span>
                              ) : null}
                              <span
                                className="clause-title"
                                dangerouslySetInnerHTML={{ __html: label }}
                              />
                              <span className="ml-auto flex items-center gap-1 relative z-10">
                                {mode === "auditoria" ? (
                                  <>
                                    <button
                                      onMouseDown={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                      }}
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        addClauseAfter(block.id);
                                      }}
                                      className="text-gray-400 hover:text-green-600 hover:bg-green-50 p-1 rounded no-print"
                                      type="button"
                                      title="Inserir cláusula abaixo"
                                    >
                                      <Plus className="w-3 h-3" />
                                    </button>
                                    <button
                                      onMouseDown={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                      }}
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        startEditing(block);
                                      }}
                                      className="text-brand-blue hover:bg-blue-50 p-1 rounded no-print"
                                      type="button"
                                      title="Editar"
                                    >
                                      <Edit3 className="w-3 h-3" />
                                    </button>
                                    <button
                                      onMouseDown={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                      }}
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        deleteBlock(block.id);
                                      }}
                                      className="text-gray-400 hover:text-red-500 hover:bg-red-50 p-1 rounded no-print"
                                      type="button"
                                      title="Excluir"
                                    >
                                      <Trash2 className="w-3 h-3" />
                                    </button>
                                  </>
                                ) : null}

                                <a
                                  href="#guia-rapido"
                                  className="text-[9px] font-bold uppercase bg-gray-100 text-gray-500 px-2 py-1 rounded hover:bg-gray-200 transition-colors no-underline relative inline-block overflow-hidden"
                                  title="Voltar ao topo"
                                >
                                  ↑ TOPO
                                </a>
                              </span>
                            </span>
                          </h2>

                          {Array.isArray(block?.children) &&
                          block.children.length ? (
                            <div className="clause-body mt-2">
                              {(() => {
                                const renderedChildren =
                                  mode === "auditoria"
                                    ? block.children
                                    : block.children.filter((child, index) =>
                                        isChildVisibleInHierarchy(
                                          block.children,
                                          index,
                                        ),
                                      );
                                const childNums = computeHierNumbers(
                                  renderedChildren,
                                  n,
                                );
                                return renderedChildren.map((child, cIdx) => {
                                  const sourceIndex = block.children.findIndex(
                                    (candidate) =>
                                      String(candidate?.id || "") ===
                                      String(child?.id || ""),
                                  );
                                  const childVisibleInDocument =
                                    sourceIndex >= 0
                                      ? isChildVisibleInHierarchy(
                                          block.children,
                                          sourceIndex,
                                        )
                                      : isBlockVisible(child);
                                  if (
                                    mode === "auditoria" &&
                                    !childVisibleInDocument
                                  ) {
                                    return (
                                      <div
                                        key={child?.id || cIdx}
                                        className="mb-2 p-3 border border-dashed border-gray-300 bg-gray-50 opacity-60 rounded-lg no-print relative"
                                        style={{
                                          marginLeft: `${normalizeClauseLevel(child?.level) * 24}px`,
                                        }}
                                      >
                                        <div className="flex items-center gap-2 mb-1">
                                          <EyeOff className="w-3 h-3 text-red-400" />
                                          <span className="text-[9px] font-bold text-red-500 uppercase tracking-widest">
                                            Item oculto pela lógica (regra não
                                            atendida)
                                          </span>
                                        </div>
                                        <span
                                          className="text-sm"
                                          dangerouslySetInnerHTML={{
                                            __html: sanitizeScreenHtml(
                                              parseText(child?.content || ""),
                                            ),
                                          }}
                                        />
                                        <div className="mt-2 flex justify-end">
                                          <button
                                            onMouseDown={(e) => {
                                              e.preventDefault();
                                              e.stopPropagation();
                                            }}
                                            onClick={(e) => {
                                              e.preventDefault();
                                              e.stopPropagation();
                                              addSubClauseAfterInBlock(
                                                block.id,
                                                child?.id,
                                                cIdx,
                                              );
                                            }}
                                            className="text-gray-400 hover:text-green-600 hover:bg-green-50 p-1 rounded no-print ml-2"
                                            type="button"
                                            title="Inserir sub-cláusula abaixo"
                                          >
                                            <Plus className="w-3 h-3" />
                                          </button>
                                        </div>
                                      </div>
                                    );
                                  }

                                  return (
                                    <div
                                      key={child?.id || cIdx}
                                      className="sub-clause-item has-manual-number"
                                      style={{
                                        "--subclause-indent": (() => {
                                          let lvl = Number(child?.level || 0);
                                          if (!Number.isFinite(lvl) || lvl < 0)
                                            lvl = 0;
                                          lvl = Math.floor(lvl);
                                          return lvl > 0
                                            ? `calc(${lvl} * (var(--clause-num-col) + var(--clause-col-gap)))`
                                            : "0";
                                        })(),
                                        marginLeft: 0,
                                      }}
                                    >
                                      <div className="clause-li-row">
                                        <span className="shrink-0 font-bold text-[11pt]">
                                          {childNums[cIdx] || ""}
                                        </span>
                                        <span
                                          className="sub-clause-content"
                                          dangerouslySetInnerHTML={{
                                            __html: sanitizeScreenHtml(
                                              parseText(child?.content || ""),
                                            ),
                                          }}
                                        />
                                        {mode === "auditoria" ? (
                                          <button
                                            onMouseDown={(e) => {
                                              e.preventDefault();
                                              e.stopPropagation();
                                            }}
                                            onClick={(e) => {
                                              e.preventDefault();
                                              e.stopPropagation();
                                              addSubClauseAfterInBlock(
                                                block.id,
                                                child?.id,
                                                cIdx,
                                              );
                                            }}
                                            className="text-gray-400 hover:text-green-600 hover:bg-green-50 p-1 rounded no-print ml-2"
                                            type="button"
                                            title="Inserir sub-cláusula abaixo"
                                          >
                                            <Plus className="w-3 h-3" />
                                          </button>
                                        ) : null}
                                      </div>
                                    </div>
                                  );
                                });
                              })()}
                            </div>
                          ) : block.type === "list" &&
                            Array.isArray(block.items) ? (
                            <ol className="first-level-list clause-body">
                              {block.items.map((item, idx) => (
                                <li
                                  key={idx}
                                  dangerouslySetInnerHTML={{
                                    __html: sanitizeScreenHtml(parseText(item)),
                                  }}
                                />
                              ))}
                            </ol>
                          ) : (
                            <div
                              className="contract-p clause-body"
                              dangerouslySetInnerHTML={{
                                __html: sanitizeScreenHtml(
                                  parseText(block.content || ""),
                                ),
                              }}
                            />
                          )}
                        </div>
                      );
                    });
                })()}

                <div className="avoid-page-break-inside">
                  {renderExecutionClosing()}
                  <div className="mb-8" style={{ marginTop: "1.6cm" }}>
                    {renderOutorganteSignatures()}
                  </div>
                </div>

                {mode === "auditoria" && (
                  <button
                    onClick={addCustomBlock}
                    className="w-full py-4 border-2 border-dashed border-gray-300 rounded-lg text-gray-400 hover:border-brand-blue hover:text-brand-blue hover:bg-blue-50 transition-all flex items-center justify-center gap-2 group no-print mt-8"
                    type="button"
                  >
                    <Plus className="w-4 h-4" />{" "}
                    <span className="text-xs font-bold uppercase">
                      Adicionar Bloco
                    </span>
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="print-only-view">
            <div className="hybrid-visual-header-fixed">
              <DocumentHeader />
            </div>
            <div className="hybrid-visual-footer-fixed">
              <DocumentFooter />
            </div>

            <table className="print-layout-table">
              <thead className="hybrid-table-header-group">
                <tr>
                  <td>
                    <div className="spacer-header-ghost">&nbsp;</div>
                  </td>
                </tr>
              </thead>

              <tbody>
                <tr>
                  <td>
                    <div className="document-content pt-4 pb-4">
                      <div className="text-center mb-8">
                        <h1 className="contract-title">
                          PROCURAÇÃO{" "}
                          <span className="italic">AD JUDICIA ET EXTRA</span>
                        </h1>
                      </div>

                      <div
                        id="guia-rapido-print"
                        className="quadro-resumo-section mb-8"
                      >
                        <h2 className="text-center font-bold text-sm mb-4 uppercase">
                          Guia Rápido do Documento
                        </h2>
                        <div className="quadro-resumo-grid">
                          {getQuickNavBlocks().map((b, idx) => {
                            const meta = getQuickNavMeta(b);
                            const safeId = String(b?.id || "").replace(
                              /[^a-zA-Z0-9_\-]/g,
                              "_",
                            );
                            return (
                              <a
                                key={b.id}
                                href={`#block-${b.id}`}
                                className="quadro-resumo-card group relative"
                              >
                                <span
                                  className="ghost-marker"
                                  style={{ top: 0, left: 0 }}
                                >
                                  {`RDA_TGT_TOP_${safeId}`}
                                </span>
                                <span
                                  className="ghost-marker"
                                  style={{ top: 0, left: 0 }}
                                >
                                  {`RDA_TL_${safeId}`}
                                </span>
                                <span
                                  className="ghost-marker"
                                  style={{ bottom: 0, right: 0 }}
                                >
                                  {`RDA_BR_${safeId}`}
                                </span>
                                <span
                                  className="ghost-marker"
                                  style={{ top: 0, left: 0 }}
                                >
                                  {`RDA_BTN_${safeId}`}
                                </span>
                                <span
                                  className="quadro-resumo-icon"
                                  aria-hidden="true"
                                >
                                  <i className={meta.icon} aria-hidden="true" />
                                </span>
                                <div className="quadro-resumo-content">
                                  <h3 className="quadro-resumo-title-group">
                                    <span className="quadro-resumo-clause-number">
                                      Cláusula {idx + 1}ª
                                    </span>
                                    <span
                                      className="quadro-resumo-label"
                                      title={meta.label.replace(/<[^>]*>/g, "")}
                                    >
                                      <span
                                        dangerouslySetInnerHTML={{
                                          __html: meta?.label || "",
                                        }}
                                      />
                                    </span>
                                  </h3>
                                </div>
                                {meta.badge ? (
                                  <span className="quadro-resumo-badge">
                                    {meta.badge}
                                  </span>
                                ) : null}
                              </a>
                            );
                          })}
                        </div>
                      </div>

                      {(() => {
                        return (templateData?.structure || [])
                          .filter((b) => isBlockVisible(b))
                          .map((block) => {
                            const isPreambulo = isPreambuloBlock(block);
                            const n = getBlockNumberById(block?.id);
                            const label = stripNumericPrefix(
                              block?.title || "",
                            ).toUpperCase();

                            if (isPreambulo) {
                              return (
                                <div
                                  key={block.id}
                                  id="block-PREAMBULO"
                                  className="clause-block mb-2 is-preamble"
                                  style={{
                                    "--indent-level": `${(block.level || 0) * 24}px`,
                                    borderLeft: "none",
                                    paddingLeft: block.level
                                      ? "1rem"
                                      : "0.5rem",
                                  }}
                                >
                                  <h2 className="clause-heading preamble-heading relative">
                                    <span className="clause-heading-row w-full flex items-start flex-wrap gap-1">
                                      <span className="clause-title">
                                        PREÂMBULO
                                      </span>
                                      <span className="ml-auto flex items-center gap-1 relative z-10">
                                        {mode === "auditoria" ? (
                                          <button
                                            onMouseDown={(e) => {
                                              e.preventDefault();
                                              e.stopPropagation();
                                            }}
                                            onClick={(e) => {
                                              e.preventDefault();
                                              e.stopPropagation();
                                              startEditing(block);
                                            }}
                                            className="text-brand-blue hover:bg-blue-50 p-1 rounded no-print"
                                            type="button"
                                            title="Editar"
                                          >
                                            <Edit3 className="w-3 h-3" />
                                          </button>
                                        ) : null}
                                      </span>
                                    </span>
                                  </h2>
                                  <div
                                    className="contract-p clause-body"
                                    dangerouslySetInnerHTML={{
                                      __html: sanitizeScreenHtml(
                                        parseText(block.content || ""),
                                      ),
                                    }}
                                  />
                                </div>
                              );
                            }

                            return (
                              <div
                                key={block.id}
                                id={`block-${block.id}`}
                                className="clause-block mb-2"
                                style={{
                                  ...(typeof n === "number"
                                    ? { "--macro-num": n }
                                    : {}),
                                  "--indent-level": `${(block.level || 0) * 24}px`,
                                  borderLeft: "none",
                                  paddingLeft: block.level ? "1rem" : "0.5rem",
                                }}
                              >
                                <h2
                                  id={getAnchorIdForBlock(block) || undefined}
                                  className="clause-heading relative"
                                >
                                  <span className="clause-heading-row w-full flex items-start flex-wrap gap-1">
                                    {n ? (
                                      <span className="clause-number">
                                        Cláusula {n}ª –
                                      </span>
                                    ) : null}
                                    <span
                                      className="clause-title"
                                      dangerouslySetInnerHTML={{
                                        __html: label,
                                      }}
                                    />
                                    <span className="ml-auto flex items-center gap-1 relative z-10">
                                      <a
                                        href="#guia-rapido-print"
                                        className="text-[9px] font-bold uppercase bg-gray-100 text-gray-500 px-2 py-1 rounded hover:bg-gray-200 transition-colors no-underline relative inline-block overflow-hidden"
                                        title="Voltar ao topo"
                                        onClick={(e) => {
                                          const isPrint = (() => {
                                            try {
                                              return Boolean(
                                                window.matchMedia &&
                                                window.matchMedia("print")
                                                  .matches,
                                              );
                                            } catch {
                                              return false;
                                            }
                                          })();

                                          if (isPrint) return;

                                          e.preventDefault();
                                          const el =
                                            document.getElementById(
                                              "guia-rapido-print",
                                            );
                                          if (el)
                                            el.scrollIntoView({
                                              behavior: "smooth",
                                              block: "start",
                                            });
                                          try {
                                            const nextState =
                                              window.history &&
                                              typeof window.history.state ===
                                                "object" &&
                                              window.history.state
                                                ? window.history.state
                                                : {};
                                            window.history.replaceState(
                                              nextState,
                                              "",
                                              "#guia-rapido-print",
                                            );
                                          } catch {}
                                        }}
                                      >
                                        TOPO
                                        <span aria-hidden="true"> &uarr;</span>
                                      </a>
                                    </span>
                                  </span>
                                </h2>

                                {block.type === "list" &&
                                Array.isArray(block.items) ? (
                                  <ol className="first-level-list clause-body">
                                    {block.items.map((item, idx) => (
                                      <li
                                        key={idx}
                                        dangerouslySetInnerHTML={{
                                          __html: sanitizeScreenHtml(
                                            parseText(item),
                                          ),
                                        }}
                                      />
                                    ))}
                                  </ol>
                                ) : Array.isArray(block?.children) &&
                                  block.children.length ? (
                                  <div className="clause-body mt-2">
                                    {(() => {
                                      const renderedChildren =
                                        block.children.filter((child, index) =>
                                          isChildVisibleInHierarchy(
                                            block.children,
                                            index,
                                          ),
                                        );
                                      const childNums = computeHierNumbers(
                                        renderedChildren,
                                        n,
                                      );
                                      return renderedChildren.map(
                                        (child, cIdx) => (
                                          <div
                                            key={child?.id || cIdx}
                                            className="sub-clause-item has-manual-number"
                                            style={{
                                              "--subclause-indent": (() => {
                                                let lvl = Number(
                                                  child?.level || 0,
                                                );
                                                if (
                                                  !Number.isFinite(lvl) ||
                                                  lvl < 0
                                                )
                                                  lvl = 0;
                                                lvl = Math.floor(lvl);
                                                return lvl > 0
                                                  ? `calc(${lvl} * (var(--clause-num-col) + var(--clause-col-gap)))`
                                                  : "0";
                                              })(),
                                              marginLeft: 0,
                                            }}
                                          >
                                            <div className="clause-li-row">
                                              <span className="shrink-0 font-bold text-[11pt]">
                                                {childNums[cIdx] || ""}
                                              </span>
                                              <span
                                                className="sub-clause-content"
                                                dangerouslySetInnerHTML={{
                                                  __html: sanitizeScreenHtml(
                                                    parseText(
                                                      child?.content || "",
                                                    ),
                                                  ),
                                                }}
                                              />
                                            </div>
                                          </div>
                                        ),
                                      );
                                    })()}
                                  </div>
                                ) : (
                                  <div
                                    className="contract-p clause-body"
                                    dangerouslySetInnerHTML={{
                                      __html: sanitizeScreenHtml(
                                        parseText(block.content || ""),
                                      ),
                                    }}
                                  />
                                )}
                              </div>
                            );
                          });
                      })()}

                      <div className="avoid-page-break-inside">
                        {renderExecutionClosing()}
                        <div className="mb-8" style={{ marginTop: "1.6cm" }}>
                          {renderOutorganteSignatures()}
                        </div>
                      </div>
                      <div
                        id="contrato-legal-pronto"
                        data-status="ready"
                        style={{ display: "none" }}
                      ></div>
                    </div>
                  </td>
                </tr>
              </tbody>

              <tfoot className="hybrid-table-footer-group">
                <tr>
                  <td>
                    <div className="spacer-footer-ghost">&nbsp;</div>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>

      <DocumentMap
        structure={templateData?.structure || []}
        activeId={editingBlockId}
        onScrollTo={scrollToBlock}
        onReorder={handleReorder}
        onDelete={deleteBlock}
      />

      {showImport && (
        <ClientImportModal
          onClose={() => setShowImport(false)}
          onSelect={handleImportClient}
        />
      )}
      {showPartnerImportModal && (
        <PartnerImportModal
          onClose={() => setShowPartnerImportModal(false)}
          onSelect={(picked) =>
            handlePartnerImport(picked, importTargetPartnerIndex)
          }
        />
      )}

      <PreFlightCheckModal
        isOpen={preFlightState.open}
        onClose={closePreFlightForProcuration}
        onConfirm={confirmPreFlightForProcuration}
        documentType="procuration"
        data={formData}
        publishIntent={preFlightState.publishType}
        extraContent={
          preFlightState.format === "pdf" ? (
            <div className="mt-1">
              <p className="text-[10px] font-extrabold text-[#20243B] uppercase tracking-widest mb-1">
                Vincular a Processo/Caderno
              </p>
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg border border-[#C5A059]/60 bg-amber-50/50">
                <svg
                  className="w-3.5 h-3.5 text-[#C5A059] shrink-0"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                  />
                </svg>
                <span className="text-xs font-bold text-[#20243B] truncate">
                  {preFlightState.cadernoTitle ||
                    preFlightState.cadernoId ||
                    "—"}
                </span>
                <span className="ml-auto text-[9px] font-black uppercase tracking-widest text-[#C5A059] shrink-0">
                  Herdado do HTML
                </span>
              </div>
              <p className="text-[9px] text-gray-400 mt-1">
                O caderno só pode ser alterado na edição do HTML.
              </p>
            </div>
          ) : (
            <CadernoSelector
              clientId={String(
                parties?.[0]?.clientId ||
                  parties?.[0]?.qualificationData?.id ||
                  "",
              ).trim()}
              clientName={String(
                parties?.[0]?.name ||
                  parties?.[0]?.qualificationData?.name ||
                  "",
              ).trim()}
              value={preFlightState.cadernoId}
              onChange={(id, title) =>
                setPreFlightState((prev) => ({
                  ...prev,
                  cadernoId: id,
                  cadernoTitle: title,
                }))
              }
            />
          )
        }
      />

      {dialogState.open ? (
        <div className="fixed inset-0 z-[700] flex items-center justify-center p-4 no-print">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => {
              const fn = dialogState.onCancel;
              setDialogState({
                open: false,
                title: "",
                message: "",
                mode: "confirm",
                onConfirm: null,
                onCancel: null,
              });
              if (typeof fn === "function") fn();
            }}
          />
          <div className="relative w-full max-w-md bg-white rounded-xl shadow-2xl border border-gray-200 p-5">
            <div className="text-sm font-bold text-[#20243B]">
              {dialogState.title || "Confirmar"}
            </div>
            <div className="text-xs text-gray-600 mt-2 whitespace-pre-wrap">
              {dialogState.message}
            </div>
            <div className="mt-5">
              {dialogState.mode === "confirm" ? (
                <SaveCancelBar
                  onCancel={() => {
                    const fn = dialogState.onCancel;
                    setDialogState({
                      open: false,
                      title: "",
                      message: "",
                      mode: "confirm",
                      onConfirm: null,
                      onCancel: null,
                    });
                    if (typeof fn === "function") fn();
                  }}
                  onSave={() => {
                    const fn = dialogState.onConfirm;
                    setDialogState({
                      open: false,
                      title: "",
                      message: "",
                      mode: "confirm",
                      onConfirm: null,
                      onCancel: null,
                    });
                    if (typeof fn === "function") fn();
                  }}
                  cancelLabel="Cancelar"
                  saveLabel="Confirmar"
                />
              ) : (
                <SaveCancelBar
                  onCancel={() =>
                    setDialogState({
                      open: false,
                      title: "",
                      message: "",
                      mode: "confirm",
                      onConfirm: null,
                      onCancel: null,
                    })
                  }
                  onSave={() =>
                    setDialogState({
                      open: false,
                      title: "",
                      message: "",
                      mode: "confirm",
                      onConfirm: null,
                      onCancel: null,
                    })
                  }
                  cancelLabel=""
                  saveLabel="OK"
                  disabledCancel={true}
                />
              )}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};
